import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import BearLogo from "./Logo";

// ─── BUILD STAMP ──────────────────────────────────────────────────────────────
// Update this on EVERY delivery, date + time + change. It renders in the app
// footer, so the live version is verifiable at a glance — if the stamp doesn't
// match the latest delivery, the deployed file is stale: re-download before
// deploying. (Time included so multiple same-day builds stay distinguishable.)
// (Stale-file check on Mac: grep "BUILD_STAMP" src/App.js)
const BUILD_STAMP = "2026-11-03 15:47 MST · recurring link matches amount";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const DEFAULT_CATEGORIES = [
  { id:"mortgage",label:"Mortgage/Rent",icon:"🏠",color:"#E07B54" },
  { id:"lease",label:"Lease/Auto",icon:"🚗",color:"#5B8DB8" },
  { id:"groceries",label:"Groceries",icon:"🛒",color:"#3BAA6B" },
  { id:"utilities",label:"Utilities",icon:"⚡",color:"#D4A017" },
  { id:"insurance",label:"Insurance",icon:"🛡️",color:"#9B6BB5" },
  { id:"gas",label:"Gas",icon:"⛽",color:"#E05C5C" },
  { id:"hobbies",label:"Hobbies",icon:"🎯",color:"#E88A3C" },
  { id:"dining",label:"Dining Out",icon:"🍽️",color:"#C9637A" },
  { id:"healthcare",label:"Healthcare",icon:"💊",color:"#0AABB8" },
  { id:"subscriptions",label:"Subscriptions",icon:"📱",color:"#5B6CE8" },
  { id:"clothing",label:"Clothing",icon:"👕",color:"#A07840" },
  { id:"other",label:"Other",icon:"📦",color:"#888888" },
];
const DEFAULT_KEYWORDS = {
  mortgage:["mortgage","rent","housing","home loan"],
  lease:["lease","car payment","auto","vehicle"],
  groceries:["grocery","groceries","food","supermarket","walmart","costco","trader joe","whole foods","kroger","safeway","aldi","rcss","superstore"],
  utilities:["utility","utilities","electric","water","internet","gas bill","power","sewage","shaw"],
  insurance:["insurance","premium","policy"],
  gas:["gas","fuel","shell","chevron","exxon","bp","speedway","petro-canada"],
  hobbies:["hobby","gym","sport","game","fitness","recreation"],
  dining:["restaurant","dining","lunch","dinner","breakfast","coffee","cafe","doordash","ubereats","grubhub","mcdonald","burger","pizza","sushi","taco","diner","bistro","grill"],
  healthcare:["doctor","dentist","pharmacy","prescription","medical","health","hospital","cvs","walgreens","shoppers","london drugs"],
  subscriptions:["netflix","spotify","hulu","amazon prime","apple","subscription","streaming","disney","youtube premium"],
  clothing:["clothes","clothing","shoes","fashion","target","h&m","zara","gap","old navy","nike","adidas"],
};
// Category icon renderer: supports emoji strings AND image icons (data URI / URL).
// Plain emoji renders exactly as before (returned unwrapped); images size to the
// surrounding font size via em units.
function CatIcon({icon,size}){
  if(typeof icon==="string"&&(icon.startsWith("data:image")||icon.startsWith("http"))){
    const s=size||"1.4em";
    return <img src={icon} alt="" style={{width:s,height:s,objectFit:"contain",flexShrink:0}}/>;
  }
  return icon;
}
// Text-safe icon for <option> elements (which cannot render images).
function iconGlyph(icon){
  return (typeof icon==="string"&&(icon.startsWith("data:image")||icon.startsWith("http")))?"\u26a1":icon;
}
// Point every utility category at the original \u26a1 emoji (display layer).
// An expense's saved catObj may be an object (in-memory after an edit) or a
// JSON string (loaded from Supabase). Returns null when unusable.
function parseCatObj(v){
  if(!v)return null;
  if(typeof v==="object")return v;
  try{const p=JSON.parse(v);return p&&typeof p==="object"?p:null;}catch{return null;}
}
// Category to display for an expense: the live category first; if the id was
// deleted, the expense's saved catObj when it names a live category; else
// the 📦 "not found" fallback (never a guessed category).
function catForExpense(e,cats){
  const live=(cats||[]).find(x=>x.id===e.category);
  if(live)return live;
  const co=parseCatObj(e.catObj);
  if(co&&co.id){const lc=(cats||[]).find(x=>x.id===co.id);if(lc)return lc;}
  return {icon:"📦",color:"#888",label:"Other"};
}
function withLightning(cats){
  return (cats||[]).map(c=>{
    const isUtil=c.id==="utilities"||/utilit/i.test(c.label||"");
    return (isUtil&&c.icon!=="\u26a1")?{...c,icon:"\u26a1"}:c;
  });
}
const EMOJI_OPTIONS = ["🏠","🚗","🛒","⚡","🛡️","⛽","🎯","🍽️","💊","📱","👕","📦","🎮","✈️","🐾","👶","📚","🎵","💄","🏋️","🌿","💡","🍷","🎁","🏖️","💰","🔧","🎨","🐶","🐱","🧴","🎓","🏥","🌎","🎸"];
const COLOR_OPTIONS = ["#E07B54","#5B8DB8","#3BAA6B","#D4A017","#9B6BB5","#E05C5C","#E88A3C","#C9637A","#0AABB8","#5B6CE8","#A07840","#888888","#2ECC71","#E74C3C","#3498DB","#F39C12","#1ABC9C","#8E44AD","#16A085","#D35400"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// Receipt scanner endpoint. Local `netlify dev` serves the Netlify function at
// /.netlify/functions/gemini. For a Cloudflare Pages deployment, build with
// REACT_APP_SCAN_URL=/api/scan (e.g. via .env.production) and deploy
// functions/api/scan.js alongside the build.
const SCAN_URL=(typeof process!=="undefined"&&process.env&&process.env.REACT_APP_SCAN_URL)||"/api/scan";

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function guessCategory(desc, kws, learned) {
  const lower = desc.toLowerCase().trim();
  // Priority pass: explicit user corrections (learned) always win, regardless of
  // whether the same word also happens to sit in a different category's default
  // keyword list. Without this, a manual re-categorization could silently get
  // overridden by an earlier-ordered default keyword next time — which is exactly
  // why auto-categorize used to work "sometimes but not always".
  if (learned) {
    for (const [word,catId] of Object.entries(learned)) {
      const wl = word.toLowerCase();
      if (lower===wl || lower.startsWith(wl+" ") || lower.endsWith(" "+wl) || lower.includes(" "+wl+" ")) return catId;
    }
  }
  // First pass: exact word match (higher confidence)
  for (const [cat,words] of Object.entries(kws)) {
    if(words.some(w=>{const wl=w.toLowerCase();return lower===wl||lower.startsWith(wl+" ")||lower.endsWith(" "+wl)||lower.includes(" "+wl+" ");})) return cat;
  }
  // Second pass: substring match
  for (const [cat,words] of Object.entries(kws)) {
    if (words.some(w => lower.includes(w.toLowerCase()))) return cat;
  }
  return null;
}
function monthLabel(key) { const [y,m]=key.split("-"); return `${MONTHS[parseInt(m)-1]} ${y}`; }

function monthShort(key) { const [,m]=key.split("-"); return MONTHS_SHORT[parseInt(m)-1]; }
let _currency="CAD";
let _privacy=false; // when true, fmt() shows decoy amounts (privacy mode)
let _fxRate=1; // display-currency conversion; amounts are always STORED in CAD
function buzz(p){try{if(typeof navigator!=="undefined"&&navigator.vibrate)navigator.vibrate(p);}catch{}}
const CURRS=[
  {code:"CAD",flag:"🍁",label:"Canadian Dollar"},
  {code:"USD",flag:"🇺🇸",label:"US Dollar"},
  {code:"JPY",flag:"🇯🇵",label:"Japanese Yen"},
  {code:"CNY",flag:"🇨🇳",label:"Chinese Yuan"},
  {code:"TWD",flag:"🇹🇼",label:"Taiwan Dollar"},
];
const FX_FALLBACK={USD:0.73,JPY:109.8,CNY:5.28,TWD:23.4}; // per 1 CAD; replaced by live rates when fetchable
function fmt(n,cur) {
  const c=cur||_currency;
  let v=Number(n)||0;
  if(c!=="CAD")v=v*(_fxRate||1);
  const dec=c==="JPY"?0:2;
  if(_privacy) return fmtDecoy(v,c,dec);
  return new Intl.NumberFormat(c==="CAD"?"en-CA":"en-US",{style:"currency",currency:c,minimumFractionDigits:dec,maximumFractionDigits:dec}).format(v);
}
// Deterministic decoy amounts for privacy mode: the same real amount always
// maps to the same fake amount, so the screen looks stable instead of
// flickering. Not reversible — for casual shoulder-surfing only. Totals won't
// match the sum of line items, but a glance won't check that.
function fmtDecoy(n,cur,dec){
  const c=cur||_currency;
  const d=dec==null?2:dec;
  let h=(Math.floor((n||0)*100))>>>0; // cents as the seed
  h=Math.imul(h^0x9E3779B9,0x85EBCA6B)>>>0;
  h^=h>>>13;h=Math.imul(h,0xC2B2AE35)>>>0;h=(h^(h>>>16))>>>0; // keep unsigned!
  const fake=((h%19999)/100)+1; // $1.00 – $200.98
  return new Intl.NumberFormat(c==="CAD"?"en-CA":"en-US",{style:"currency",currency:c,minimumFractionDigits:d,maximumFractionDigits:d}).format(fake);
}
function hashPin(pin) { let h=0; for(let i=0;i<pin.length;i++){h=((h<<5)-h)+pin.charCodeAt(i);h|=0;} return h.toString(); }
function expMonthKey(e) {
  // Try created_at first
  if(e.created_at){
    const d=new Date(e.created_at);
    if(!isNaN(d.getTime())&&d.getFullYear()>2000){
      return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
    }
  }
  // Fallback (only used if created_at is missing/invalid — normally never happens
  // since every add/edit path sets it): parse date string e.g. "Jan 1", "Dec 15".
  // Guess the year using a "most recent past occurrence" heuristic — e.g. if it's
  // February and the entry says "Dec 1", that's almost certainly last December,
  // not a future one, since expenses are never dated ahead of time.
  if(e.date){
    const months={Jan:1,Feb:2,Mar:3,Apr:4,May:5,Jun:6,Jul:7,Aug:8,Sep:9,Oct:10,Nov:11,Dec:12};
    const parts=e.date.split(" ");
    const mo=months[parts[0]];
    if(mo){
      const now=new Date();
      let yr=now.getFullYear();
      if(mo>now.getMonth()+1)yr-=1;
      return `${yr}-${String(mo).padStart(2,"0")}`;
    }
  }
  // Last resort: current month
  const d=new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
}
function localISO(d=new Date()){return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;}
function nowMonthKey() { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`; }
// Month key from an ISO timestamp in LOCAL time (not UTC). created_at.slice(0,7)
// is UTC and disagrees with expMonthKey for entries made in the evening
// (6pm–midnight local = next day UTC), which let duplicates slip past the
// backfill guards. Always use this instead of slice(0,7).
function isoMonthKey(iso){const d=new Date(iso);return isNaN(d.getTime())?"":`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;}
function getMonthIncome(income,mk) {
  if(!income)return 0;
  if(income.monthly&&income.monthly[mk]!=null)return parseFloat(income.monthly[mk])||0;
  return parseFloat(income.default)||parseFloat(income.total)||0;
}

// ─── THEME ────────────────────────────────────────────────────────────────────
const FONT="'DM Serif Display', serif";
const MONO="'JetBrains Mono', monospace";
const SANS="'DM Sans', sans-serif";
const T={
  bg:"#F6F4F0", bgCard:"#FFFFFF", bgCardAlt:"#F9F7F4",
  border:"#E5E0D8", borderStrong:"#CEC8BE",
  text:"#1C1915", textSub:"#6B6560", textMuted:"#A09A93",
  accent:"#D96F3E", accentLight:"#FEF0E7", accentBorder:"#F3C9A8",
  navBg:"#EDE9E3",
  shadow:"0 1px 3px rgba(0,0,0,0.07), 0 4px 16px rgba(0,0,0,0.05)",
  shadowMd:"0 8px 32px rgba(0,0,0,0.12)",
  green:"#1E9A57", red:"#D43A3A",
  greenBg:"#EAF7F1", redBg:"#FDEAEA",
  greenBorder:"#A8DFC2", redBorder:"#F5BBBB",
  blue:"#3B82F6", blueBg:"#EFF6FF",
  lock:"#6B5EA8", lockBg:"#F0EEF8", lockBorder:"#C8C0E8",
};

const GLOBAL_CSS = `
  *{box-sizing:border-box;-webkit-tap-highlight-color:transparent;}
  html,body{margin:0;padding:0;overflow-x:hidden;width:100%;max-width:100vw;}
  body{background:${T.bg};font-family:${SANS};}
  input,select,textarea{font-size:16px!important;}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.3}}
  @keyframes bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
  @keyframes fadein{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
  .tab-content{animation:fadein 0.2s ease;}
  .g2{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:16px;}
  .g3{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:16px;}
  .g4{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:16px;}
  @media(max-width:680px){
    .g4{grid-template-columns:1fr 1fr!important;}
    .g3{grid-template-columns:1fr 1fr!important;}
  }
  @media(max-width:480px){
    .g2{grid-template-columns:1fr!important;}
  }
  .app-nav{display:flex;gap:6px;background:${T.navBg};border-radius:16px;padding:5px;overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none;flex-shrink:0;justify-content:center;}
  .app-nav::-webkit-scrollbar{display:none;}
  .cat-pills{display:flex;gap:5px;overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none;width:100%;padding-bottom:4px;}
  .cat-pills::-webkit-scrollbar{display:none;}
`;

const S = {
  app:{ minHeight:"100vh", background:T.bg, color:T.text, fontFamily:SANS, overflowX:"hidden", width:"100%" },
  inner:{ maxWidth:1100, margin:"0 auto", padding:"0 14px 120px", width:"100%" },
  header:{ padding:"14px 0 12px", borderBottom:`1px solid ${T.border}`, marginBottom:20, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 },
  card:{ background:T.bgCard, border:`1px solid ${T.border}`, borderRadius:18, padding:20, boxShadow:T.shadow, width:"100%" },
  sectionTitle:{ fontFamily:FONT, fontSize:20, marginBottom:14, color:T.text },
  label:{ fontSize:11, textTransform:"uppercase", letterSpacing:1.5, color:T.textMuted, marginBottom:6, display:"block", fontWeight:600 },
  input:{ width:"100%", padding:"14px", background:T.bgCardAlt, border:`1.5px solid ${T.border}`, borderRadius:12, color:T.text, fontFamily:SANS, fontSize:16, outline:"none", boxSizing:"border-box" },
  select:{ width:"100%", padding:"14px", background:T.bgCardAlt, border:`1.5px solid ${T.border}`, borderRadius:12, color:T.text, fontFamily:SANS, fontSize:16, outline:"none", boxSizing:"border-box" },
  btn:{ padding:"14px 22px", background:T.accent, border:"none", borderRadius:12, color:"#fff", fontFamily:SANS, fontSize:15, fontWeight:700, cursor:"pointer", letterSpacing:0.2, width:"100%", display:"block" },
  btnSm:{ padding:"10px 16px", background:T.accent, border:"none", borderRadius:10, color:"#fff", fontFamily:SANS, fontSize:13, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 },
  btnOutline:{ padding:"12px 16px", background:"transparent", border:`1.5px solid ${T.borderStrong}`, borderRadius:12, color:T.textSub, fontFamily:SANS, fontSize:14, cursor:"pointer", width:"100%" },
  tag:(color)=>({ display:"inline-flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:20, background:`${color}18`, color, fontSize:12, fontWeight:700, border:`1px solid ${color}28` }),
  pill:(active,color)=>({ padding:"9px 15px", borderRadius:20, border:`1.5px solid ${active?color:T.border}`, background:active?`${color}18`:"transparent", color:active?color:T.textSub, fontSize:13, fontWeight:600, cursor:"pointer", whiteSpace:"nowrap" }),
  navBtn:(a,count=4)=>{
    // Scale down padding/font as more tabs get added so everything fits without
    // needing to guess there's more off-screen. 4 tabs = spacious, 6+ = compact.
    const big=count<=4, med=count===5, small=count>=6;
    return{
      padding:big?"18px 8px":med?"13px 6px":"10px 4px",
      borderRadius:big?14:12,
      border:"none", cursor:"pointer", fontFamily:SANS,
      fontSize:big?14:med?12:11, fontWeight:700,
      flex:"1 1 0", minWidth:big?90:small?58:70,
      transition:"all 0.18s",
      background:a?T.accent:"transparent", color:a?"#fff":T.textSub,
      boxShadow:a?"0 2px 12px rgba(217,111,62,0.3)":"none",
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
      gap:big?5:3,
    };
  },
  modal:{ position:"fixed", inset:0, background:"rgba(28,25,21,0.5)", backdropFilter:"blur(8px)", display:"flex", alignItems:"flex-end", justifyContent:"center", zIndex:1000 },
  modalBox:{ background:T.bgCard, borderRadius:"20px 20px 0 0", padding:24, width:"100%", maxWidth:560, maxHeight:"92vh", overflowY:"auto", boxShadow:T.shadowMd },
  chatBubble:(ai)=>({ maxWidth:"84%", padding:"13px 16px", borderRadius:ai?"4px 16px 16px 16px":"16px 4px 16px 16px", background:ai?T.accentLight:T.bgCardAlt, border:`1px solid ${ai?T.accentBorder:T.border}`, fontSize:14, lineHeight:1.7, color:T.text }),
};

// ─── SMALL UI COMPONENTS ──────────────────────────────────────────────────────
function SyncBadge({status}) {
  const c={synced:T.green,syncing:"#4A90D9",error:T.red};
  const l={synced:"Synced",syncing:"Syncing…",error:"Error"};
  return <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:c[status],whiteSpace:"nowrap"}}>
    <span style={{width:7,height:7,borderRadius:"50%",background:c[status],display:"inline-block",animation:status==="syncing"?"pulse 1s infinite":"none"}}/>
    {l[status]}
  </div>;
}

function StatCard({label,value,sub,accent,bg,locked,onClick}) {
  return <div onClick={onClick} style={{...S.card,background:bg||T.bgCard,borderColor:accent?`${accent}28`:T.border,position:"relative",overflow:"hidden",cursor:onClick?"pointer":"default",minWidth:0}}>
    {locked&&<div style={{position:"absolute",inset:0,background:T.lockBg,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:18,zIndex:2}}><span style={{fontSize:22}}>🔒</span></div>}
    <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:2,color:T.textMuted,marginBottom:6,fontWeight:600}}>{label}</div>
    <div style={{fontFamily:MONO,fontSize:19,fontWeight:700,color:accent||T.text,lineHeight:1.1}}>{value}</div>
    {sub&&<div style={{fontSize:11,color:T.textMuted,marginTop:5}}>{sub}</div>}
  </div>;
}

function BarChart({data,color=T.accent}) {
  if(!data||!data.length) return <div style={{textAlign:"center",color:T.textMuted,padding:"28px 0",fontSize:13}}>No data yet</div>;
  const max=Math.max(...data.map(d=>d.value),1);
  return <div style={{display:"flex",alignItems:"flex-end",gap:5,height:110,padding:"0 2px"}}>
    {data.map((d,i)=><div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:3,height:"100%",justifyContent:"flex-end",minWidth:0}}>
      <div style={{fontSize:9,color:T.textMuted,fontFamily:MONO,whiteSpace:"nowrap",width:"100%",textAlign:"center",overflow:"hidden",textOverflow:"ellipsis"}}>{fmt(d.value)}</div>
      <div style={{width:"100%",background:`linear-gradient(180deg,${color}EE,${color}77)`,borderRadius:"5px 5px 0 0",height:`${Math.max((d.value/max)*78,3)}px`,transition:"height 0.4s ease"}}/>
      <div style={{fontSize:9,color:T.textMuted,whiteSpace:"nowrap"}}>{d.label}</div>
    </div>)}
  </div>;
}


// ─── LOCK SYSTEM ──────────────────────────────────────────────────────────────
function LockedSection({label,onUnlock}) {
  return <div style={{background:T.lockBg,border:`1.5px solid ${T.lockBorder}`,borderRadius:18,padding:28,display:"flex",flexDirection:"column",alignItems:"center",gap:12}}>
    <div style={{fontSize:30}}>🔒</div>
    <div style={{fontFamily:FONT,fontSize:16,color:T.lock}}>{label}</div>
    <button onClick={onUnlock} style={{padding:"10px 22px",background:T.lock,border:"none",borderRadius:10,color:"#fff",fontFamily:SANS,fontSize:14,fontWeight:700,cursor:"pointer"}}>Unlock 🔓</button>
  </div>;
}
function LockableCard({sectionKey,label,locks,privacyMode,onRequestUnlock,onToggleLock,children,style}) {
  const isLocked=locks[sectionKey];
  return <div style={style}>
    {isLocked?<LockedSection label={label} onUnlock={()=>onRequestUnlock(sectionKey)}/>:(
      <div style={{...S.card,position:"relative"}}>
        <button onClick={()=>onToggleLock(sectionKey)} title="Lock" style={{position:"absolute",top:14,right:14,background:"none",border:"none",cursor:"pointer",fontSize:15,opacity:0.3,padding:0}}>🔒</button>
        {children}
      </div>
    )}
  </div>;
}
function PinModal({mode,storedHash,onSuccess,onClose,onSetPin}) {
  const [pin,setPin]=useState(""); const [conf,setConf]=useState(""); const [err,setErr]=useState("");
  const [fails,setFails]=useState(0); const [lockUntil,setLockUntil]=useState(0);
  const [rmode,setRmode]=useState(mode); // unlock screen can switch to setup via "Forgot PIN?"
  const ref=useRef(null); useEffect(()=>{setTimeout(()=>ref.current?.focus(),200);},[]);
  const go=()=>{
    const now=Date.now();
    if(now<lockUntil){setErr(`Too many tries — wait ${Math.ceil((lockUntil-now)/1000)}s`);return;}
    if(rmode==="setup"){if(pin.length<4){setErr("Min 4 digits");return;}if(pin!==conf){setErr("PINs don't match");return;}onSetPin(hashPin(pin));onSuccess();}
    else{
      if(hashPin(pin)===storedHash){onSuccess();}
      else{
        const f=fails+1;setPin("");
        if(f>=5){setLockUntil(now+30000);setFails(0);setErr("Too many tries — locked for 30 seconds");}
        else{setFails(f);setErr(`Wrong PIN (${5-f} ${5-f===1?"try":"tries"} left)`);}
      }
    }
  };
  return <div style={S.modal} onClick={e=>e.target===e.currentTarget&&onClose()}>
    <div style={{...S.modalBox,borderRadius:20,maxWidth:340,margin:"auto",textAlign:"center"}}>
      <div style={{fontSize:36,marginBottom:10}}>🔐</div>
      <div style={{fontFamily:FONT,fontSize:22,marginBottom:4}}>{rmode==="setup"?(mode==="setup"?"Set PIN":"Reset PIN"):"Enter PIN"}</div>
      <div style={{fontSize:13,color:T.textMuted,marginBottom:20}}>{rmode==="setup"?(mode==="setup"?"Protect sensitive sections":"Choose a new PIN"):"Enter your PIN to unlock"}</div>
      {rmode==="setup"?<>
        <input ref={ref} type="password" inputMode="numeric" maxLength={8} placeholder="New PIN" style={{...S.input,textAlign:"center",letterSpacing:6,marginBottom:10}} value={pin} onChange={e=>setPin(e.target.value.replace(/\D/g,""))}/>
        <input type="password" inputMode="numeric" maxLength={8} placeholder="Confirm PIN" style={{...S.input,textAlign:"center",letterSpacing:6,marginBottom:16}} value={conf} onChange={e=>setConf(e.target.value.replace(/\D/g,""))} onKeyDown={e=>e.key==="Enter"&&go()}/>
      </>:
        <input ref={ref} type="password" inputMode="numeric" maxLength={8} placeholder="• • • •" style={{...S.input,textAlign:"center",letterSpacing:10,fontSize:22,marginBottom:16}} value={pin} onChange={e=>setPin(e.target.value.replace(/\D/g,""))} onKeyDown={e=>e.key==="Enter"&&go()}/>
      }
      {err&&<div style={{color:T.red,fontSize:13,marginBottom:12}}>{err}</div>}
      <div style={{display:"flex",gap:10}}>
        <button style={{...S.btn,flex:1,background:T.lock,width:"auto"}} onClick={go}>{rmode==="setup"?"Set PIN":"Unlock"}</button>
        <button style={{...S.btnOutline,flex:1,width:"auto"}} onClick={onClose}>Cancel</button>
      </div>
      {rmode!=="setup"&&<button onClick={()=>{setRmode("setup");setPin("");setConf("");setErr("");setFails(0);}} style={{background:"none",border:"none",color:T.accent,fontSize:13,marginTop:14,cursor:"pointer",fontFamily:SANS}}>Forgot PIN? Set a new one</button>}
    </div>
  </div>;
}
function ConfirmDialog({title,message,confirmLabel,danger,onConfirm,onClose}) {
  return <div style={S.modal} onClick={e=>e.target===e.currentTarget&&onClose()}>
    <div style={{...S.modalBox,borderRadius:20,maxWidth:340,margin:"auto",textAlign:"center"}}>
      <div style={{fontSize:36,marginBottom:10}}>{danger?"⚠️":"❓"}</div>
      <div style={{fontFamily:FONT,fontSize:20,marginBottom:6}}>{title}</div>
      {message&&<div style={{fontSize:13,color:T.textMuted,marginBottom:20,lineHeight:1.6}}>{message}</div>}
      <div style={{display:"flex",gap:10}}>
        <button style={{...S.btn,flex:1,width:"auto",background:danger?T.red:T.accent}} onClick={()=>{onConfirm();onClose();}}>{confirmLabel||"Confirm"}</button>
        <button style={{...S.btnOutline,flex:1,width:"auto"}} onClick={onClose}>Cancel</button>
      </div>
    </div>
  </div>;
}
function HouseholdLogo({logo,size=46}) {
  if(logo&&logo.startsWith("data:image"))
    return <img src={logo} alt="household logo" style={{width:size,height:size,borderRadius:Math.round(size*0.28),objectFit:"cover",flexShrink:0}}/>;
  if(logo)return <span style={{fontSize:Math.round(size*0.92),lineHeight:1}}>{logo}</span>;
  return <BearLogo size={size}/>;
}
function HouseholdProfileModal({name,logo,onSave,onClose}) {
  const [n,setN]=useState(name);
  const [l,setL]=useState(logo);
  const [uploading,setUploading]=useState(false);
  const fileRef=useRef(null);
  const onPickFile=async(file)=>{
    if(!file)return;
    setUploading(true);
    try{setL(await toLogoDataUrl(file));}catch{}
    setUploading(false);
  };
  return <div style={S.modal} onClick={e=>e.target===e.currentTarget&&onClose()}>
    <div style={S.modalBox}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
        <div style={{fontFamily:FONT,fontSize:20}}>Household Profile</div>
        <button onClick={onClose} style={{background:"none",border:"none",fontSize:26,cursor:"pointer",color:T.textMuted,lineHeight:1}}>×</button>
      </div>
      <div style={{fontSize:13,color:T.textMuted,marginBottom:18}}>Shows in the header for everyone in your household.</div>
      <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:18,padding:16,background:T.bgCardAlt,borderRadius:14,border:`1.5px solid ${T.border}`}}>
        <HouseholdLogo logo={l} size={48}/>
        <div style={{lineHeight:1.3}}>
          <div style={{fontFamily:FONT,fontSize:17,color:T.accent}}>{n.trim()||"Your Household"}</div>
          <div style={{fontFamily:FONT,fontSize:12,color:T.textSub}}>Expenses</div>
        </div>
      </div>
      <div><label style={S.label}>Household name</label>
      <input style={{...S.input,marginBottom:16}} value={n} onChange={e=>setN(e.target.value)} placeholder="e.g. The Lees" maxLength={30}/></div>
      <div style={S.label}>Logo</div>
      <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:10}}>
        <button onClick={()=>setL("")} title="Default bear logo" style={{width:48,height:48,borderRadius:12,border:`2px solid ${l===""?T.accent:T.border}`,background:l===""?T.accentLight:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}><BearLogo size={30}/></button>
        {EMOJI_OPTIONS.map(e=><button key={e} onClick={()=>setL(e)} style={{width:48,height:48,borderRadius:12,border:`2px solid ${l===e?T.accent:T.border}`,background:l===e?T.accentLight:"transparent",fontSize:24,cursor:"pointer"}}>{e}</button>)}
      </div>
      <button onClick={()=>fileRef.current.click()} style={{...S.btnOutline,width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginBottom:6}}>
        {l&&l.startsWith("data:image")?<img src={l} alt="custom logo" style={{width:28,height:28,borderRadius:8,objectFit:"cover"}}/>:"📤"}
        {uploading?"Uploading…":(l&&l.startsWith("data:image")?"Change photo":"Upload your own photo")}
      </button>
      <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>{if(e.target.files?.[0])onPickFile(e.target.files[0]);e.target.value="";}}/>
      <div style={{fontSize:11,color:T.textMuted,marginBottom:16}}>First tile is the default bear logo — or pick an emoji above.</div>
      <div style={{display:"flex",gap:10}}>
        <button style={{...S.btn,flex:1,width:"auto"}} onClick={()=>{onSave(n.trim()||"My Household",l);onClose();}}>Save</button>
        <button style={{...S.btnOutline,flex:1,width:"auto"}} onClick={onClose}>Cancel</button>
      </div>
    </div>
  </div>;
}
function PrivacyButton({privacyMode,pinHash,onToggle,onSetupPin}) {
  return <button onClick={privacyMode?onToggle:(pinHash?onToggle:onSetupPin)} style={{display:"flex",alignItems:"center",gap:6,padding:"10px 13px",borderRadius:10,border:"none",cursor:"pointer",fontFamily:SANS,fontSize:13,fontWeight:700,background:privacyMode?T.lock:T.lockBg,color:privacyMode?"#fff":T.lock,whiteSpace:"nowrap",flexShrink:0}}>
    {privacyMode?"🙈 Hidden":"👁 Privacy"}
  </button>;
}

// ─── EDIT EXPENSE MODAL ───────────────────────────────────────────────────────
function EditExpenseModal({expense,categories,onSave,onClose}) {
  // Parse existing date into ISO format for the date input
  const parseExpDate=(e)=>{
    if(e.created_at){const d=new Date(e.created_at);if(!isNaN(d.getTime())&&d.getFullYear()>2000)return localISO(d);}
    const months={Jan:"01",Feb:"02",Mar:"03",Apr:"04",May:"05",Jun:"06",Jul:"07",Aug:"08",Sep:"09",Oct:"10",Nov:"11",Dec:"12"};
    if(e.date){const parts=e.date.split(" ");const mo=months[parts[0]];const dy=String(parts[1]||1).padStart(2,"0");if(mo)return `${new Date().getFullYear()}-${mo}-${dy}`;}
    return localISO();
  };
  const [form,setForm]=useState({
    description:expense.description,
    amount:expense.amount,
    category:expense.category,
    recurring:expense.recurring||false,
    dateISO:parseExpDate(expense),
  });
  const [applyFuture,setApplyFuture]=useState(true);
  const [editErr,setEditErr]=useState("");
  return <div style={S.modal} onClick={e=>e.target===e.currentTarget&&onClose()}>
    <div style={S.modalBox}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div style={{fontFamily:FONT,fontSize:20}}>Edit Expense</div>
        <button onClick={onClose} style={{background:"none",border:"none",fontSize:26,cursor:"pointer",color:T.textMuted,lineHeight:1}}>×</button>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div><label style={S.label}>Description</label><input style={S.input} value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}/></div>
        <div><label style={S.label}>Amount ($)</label><input style={S.input} type="number" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))}/></div>
        <div><label style={S.label}>Date</label><input style={S.input} type="date" value={form.dateISO} onChange={e=>setForm(f=>({...f,dateISO:e.target.value}))}/></div>
        <div><label style={S.label}>Category</label><select style={S.select} value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))}>{categories.map(c=><option key={c.id} value={c.id}>{iconGlyph(c.icon)} {c.label}</option>)}</select></div>
        <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:T.bgCardAlt,borderRadius:12,border:`1.5px solid ${T.border}`,cursor:"pointer"}} onClick={()=>setForm(f=>({...f,recurring:!f.recurring}))}>
          <input type="checkbox" checked={form.recurring} onChange={e=>setForm(f=>({...f,recurring:e.target.checked}))} style={{accentColor:T.accent,width:20,height:20,flexShrink:0}}/>
          <div><div style={{fontSize:14,fontWeight:600}}>Recurring monthly</div><div style={{fontSize:12,color:T.textMuted}}>Bills that repeat every month</div></div>
        </div>
        {expense.recurring&&form.recurring&&<div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:applyFuture?`${T.accent}10`:T.bgCardAlt,borderRadius:12,border:`1.5px solid ${applyFuture?T.accent:T.border}`,cursor:"pointer"}} onClick={()=>setApplyFuture(v=>!v)}>
          <input type="checkbox" checked={applyFuture} onChange={e=>setApplyFuture(e.target.checked)} style={{accentColor:T.accent,width:20,height:20,flexShrink:0}}/>
          <div><div style={{fontSize:14,fontWeight:600}}>Apply to future months too</div><div style={{fontSize:12,color:T.textMuted}}>Updates this and all upcoming copies of this bill</div></div>
        </div>}
        {editErr&&<div style={{fontSize:13,color:T.red,textAlign:"center",marginBottom:10}}>{editErr}</div>}
        <div style={{display:"flex",gap:10,marginTop:4}}>
          <button style={{...S.btn,flex:1,width:"auto"}} onClick={()=>{
            const amt=parseFloat(form.amount);
            if(!form.description.trim()){setEditErr("Enter a description");return;}
            if(isNaN(amt)){setEditErr("Enter an amount");return;}
            if(amt<=0){setEditErr("Amount must be more than $0");return;}
            setEditErr("");
            const catObj=categories.find(c=>c.id===form.category)||parseCatObj(expense.catObj)||null;
            const d=new Date(form.dateISO+"T12:00:00");
            const MONTHS_SHORT=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
            const dateStr=`${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}`;
            onSave({...form,amount:amt,catObj,date:dateStr,created_at:d.toISOString(),applyFuture});
          }}>Save Changes</button>
          <button style={{...S.btnOutline,flex:1,width:"auto"}} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  </div>;
}

// ─── MANAGE CATEGORIES MODAL ──────────────────────────────────────────────────
function ManageCategoriesModal({categories,onAdd,onEdit,onRemove,onClose}) {
  const [view,setView]=useState("list");
  const [editing,setEditing]=useState(null);
  const [label,setLabel]=useState("");
  const [icon,setIcon]=useState("📦");
  const [color,setColor]=useState("#E07B54");
  const [kw,setKw]=useState("");
  const [showEmoji,setShowEmoji]=useState(false);
  const [catErr,setCatErr]=useState("");

  const openAdd=()=>{setLabel("");setIcon("📦");setColor("#E07B54");setKw("");setShowEmoji(false);setCatErr("");setView("add");};
  const openEdit=(c)=>{setEditing(c);setLabel(c.label);setIcon(c.icon);setColor(c.color);setKw((c.keywords||[]).join(", "));setShowEmoji(false);setCatErr("");setView("edit");};
  const add=()=>{
    if(!label.trim()){setCatErr("Enter a category name");return;}
    setCatErr("");
    onAdd({id:label.toLowerCase().replace(/\s+/g,"_")+"_"+Date.now(),label:label.trim(),icon,color,custom:true,keywords:kw?kw.split(",").map(k=>k.trim().toLowerCase()).filter(Boolean):[]});
    setView("list");
  };
  const save=()=>{
    if(!label.trim()||!editing){setCatErr("Enter a category name");return;}
    setCatErr("");
    onEdit(editing.id,{...editing,label:label.trim(),icon,color,keywords:kw?kw.split(",").map(k=>k.trim().toLowerCase()).filter(Boolean):[]});
    setView("list");
  };

  // Shared form fields — inlined as JSX, NOT as a sub-component, to prevent focus loss
  const formJSX = <>
    <div style={{display:"grid",gridTemplateColumns:"56px 1fr",gap:10,marginBottom:14}}>
      <div style={{position:"relative"}}>
        <button onClick={()=>setShowEmoji(!showEmoji)} style={{width:56,height:50,borderRadius:12,border:`1.5px solid ${T.border}`,background:T.bgCard,fontSize:24,cursor:"pointer"}}><CatIcon icon={icon}/></button>
        {showEmoji&&<div style={{position:"absolute",top:56,left:0,zIndex:20,background:T.bgCard,border:`1px solid ${T.border}`,borderRadius:14,padding:10,display:"flex",flexWrap:"wrap",gap:3,width:260,boxShadow:T.shadowMd,maxHeight:200,overflowY:"auto"}}>
          {EMOJI_OPTIONS.map(e=><button key={e} onClick={()=>{setIcon(e);setShowEmoji(false);}} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",padding:5,borderRadius:8}}><CatIcon icon={e}/></button>)}
        </div>}
      </div>
      <div>
        <label style={S.label}>Category Name</label>
        <input
          style={S.input}
          placeholder="e.g. Pet Care"
          value={label}
          onChange={e=>setLabel(e.target.value)}
          autoComplete="off"
        />
      </div>
    </div>
    <div style={{marginBottom:14}}>
      <label style={S.label}>Color</label>
      <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
        {COLOR_OPTIONS.map(c=><button key={c} onClick={()=>setColor(c)} style={{width:28,height:28,borderRadius:"50%",background:c,border:color===c?"3px solid #1C1915":"2px solid transparent",cursor:"pointer"}}/>)}
      </div>
    </div>
    <div style={{marginBottom:14}}>
      <label style={S.label}>Auto-detect Keywords (comma separated)</label>
      <input
        style={S.input}
        placeholder="e.g. petco, vet, pet food"
        value={kw}
        onChange={e=>setKw(e.target.value)}
        autoComplete="off"
      />
    </div>
    {label&&<div style={{marginBottom:14,display:"flex",alignItems:"center",gap:8}}>
      <span style={{fontSize:12,color:T.textMuted}}>Preview:</span>
      <span style={{...S.tag(color),fontSize:13,padding:"5px 13px"}}><CatIcon icon={icon}/> {label}</span>
    </div>}
  </>;

  return <div style={S.modal} onClick={e=>e.target===e.currentTarget&&onClose()}>
    <div style={S.modalBox}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          {view!=="list"&&<button onClick={()=>setView("list")} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:T.textMuted,padding:0,lineHeight:1}}>←</button>}
          <div style={{fontFamily:FONT,fontSize:20}}>
            {view==="list"?"Manage Categories":view==="add"?"Add Category":"Edit Category"}
          </div>
        </div>
        <button onClick={onClose} style={{background:"none",border:"none",fontSize:26,cursor:"pointer",color:T.textMuted,lineHeight:1}}>×</button>
      </div>

      {view==="list"&&<>
        <button onClick={openAdd} style={{...S.btn,marginBottom:18}}>+ Add New Category</button>
        <div style={{fontFamily:FONT,fontSize:17,marginBottom:10}}>All Categories</div>
        {categories.map(c=><div key={c.id} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 0",borderBottom:`1px solid ${T.border}`}}>
          <div style={{width:40,height:40,borderRadius:11,background:`${c.color}18`,border:`1.5px solid ${c.color}30`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0}}><CatIcon icon={c.icon}/></div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:14,fontWeight:600}}>{c.label}</div>
            {c.keywords?.length>0&&<div style={{fontSize:11,color:T.textMuted,marginTop:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.keywords.slice(0,5).join(", ")}</div>}
          </div>
          <button onClick={()=>openEdit(c)} style={{background:"none",border:"none",color:T.blue,cursor:"pointer",fontSize:15,padding:"6px 8px"}}>✏️</button>
          {c.custom
            ?<button onClick={()=>onRemove(c.id)} style={{background:"none",border:"none",color:T.red,cursor:"pointer",fontSize:20,opacity:0.7,padding:"4px 8px"}}>×</button>
            :<span style={{fontSize:10,color:T.textMuted,background:T.bgCardAlt,border:`1px solid ${T.border}`,borderRadius:6,padding:"3px 8px",flexShrink:0}}>built-in</span>
          }
        </div>)}
      </>}

      {view==="add"&&<>
        {formJSX}
        {catErr&&<div style={{fontSize:13,color:T.red,marginBottom:10}}>{catErr}</div>}
        <div style={{display:"flex",gap:10}}>
          <button style={S.btn} onClick={add}>+ Add Category</button>
          <button style={{...S.btnOutline,width:"auto",padding:"14px 18px"}} onClick={()=>setView("list")}>Cancel</button>
        </div>
      </>}

      {view==="edit"&&editing&&<>
        {!editing.custom&&<div style={{background:T.blueBg,border:`1px solid ${T.blue}22`,borderRadius:12,padding:"11px 14px",marginBottom:14,fontSize:13,color:T.blue}}>
          💡 Built-in categories can have their icon and color changed. Name and keywords are editable too.
        </div>}
        {formJSX}
        {catErr&&<div style={{fontSize:13,color:T.red,marginBottom:10}}>{catErr}</div>}
        <div style={{display:"flex",gap:10}}>
          <button style={S.btn} onClick={save}>✓ Save Changes</button>
          <button style={{...S.btnOutline,width:"auto",padding:"14px 18px"}} onClick={()=>setView("list")}>Cancel</button>
        </div>
      </>}
    </div>
  </div>;
}

// ─── MANAGE RECURRING BILLS ─────────────────────────────────────────────
function ManageRecurringModal({templates,categories,onSaveTpl,onDeleteTpl,onClose}) {
  const [editing,setEditing]=useState(null); // template id, or "new"
  const [desc,setDesc]=useState("");
  const [amt,setAmt]=useState("");
  const [cat,setCat]=useState("");
  const [day,setDay]=useState("1");
  const [err,setErr]=useState("");
  const openEdit=(t)=>{setEditing(t.id);setDesc(t.description);setAmt(String(t.amount));setCat(t.category);setDay(String(t.day_of_month||1));setErr("");};
  const openNew=()=>{setEditing("new");setDesc("");setAmt("");setCat(categories[0]?.id||"");setDay("1");setErr("");};
  const save=()=>{
    const a=parseFloat(amt),d=parseInt(day)||1;
    if(!desc.trim()){setErr("Enter a description");return;}
    if(isNaN(a)||a<=0){setErr("Enter an amount over $0");return;}
    const catObj=categories.find(c=>c.id===cat)||categories[0];
    onSaveTpl(editing,{description:desc.trim(),amount:a,category:cat,catObj,day_of_month:Math.min(Math.max(d,1),28)});
    setEditing(null);
  };
  return <div style={S.modal} onClick={e=>e.target===e.currentTarget&&onClose()}>
    <div style={{...S.modalBox,maxHeight:"88dvh",overflowY:"auto"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
        <div style={{fontFamily:FONT,fontSize:20}}>🔄 Recurring bills</div>
        <button onClick={onClose} style={{background:"none",border:"none",fontSize:26,cursor:"pointer",color:T.textMuted,lineHeight:1}}>×</button>
      </div>
      <div style={{fontSize:12,color:T.textMuted,marginBottom:16,lineHeight:1.5}}>Each bill auto-creates every month. Edit one and future months follow.</div>
      {(templates||[]).map(t=>{
        const c=catForExpense({category:t.category,catObj:t.cat_obj},categories);
        return <div key={t.id} style={{display:"flex",alignItems:"center",gap:10,padding:"11px 0",borderBottom:`1px solid ${T.border}`}}>
          <div style={{width:36,height:36,borderRadius:10,background:`${c.color}14`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,flexShrink:0}}><CatIcon icon={c.icon}/></div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:14,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.description}</div>
            <div style={{fontSize:11,color:T.textMuted}}>Monthly · day {t.day_of_month}</div>
          </div>
          <div style={{fontFamily:MONO,fontSize:14,fontWeight:700,color:T.accent}}>{fmt(t.amount)}</div>
          <button onClick={()=>openEdit(t)} style={{background:"none",border:"none",fontSize:15,padding:"8px",cursor:"pointer"}}>✏️</button>
          <button onClick={()=>onDeleteTpl(t)} style={{background:"none",border:"none",fontSize:18,padding:"8px",cursor:"pointer",color:T.red}}>×</button>
        </div>;
      })}
      {(templates||[]).length===0&&<div style={{textAlign:"center",color:T.textMuted,padding:"24px 0",fontSize:14}}>No recurring bills yet.<br/>Add one — Netflix, rent, phone…</div>}
      <button onClick={openNew} style={{...S.btnOutline,width:"100%",marginTop:14}}>+ New recurring bill</button>
      {editing&&<div style={{marginTop:16,padding:16,background:T.bgCardAlt,borderRadius:14,border:`1.5px solid ${T.accent}`}}>
        <div style={{fontSize:14,fontWeight:700,marginBottom:12}}>{editing==="new"?"New bill":"Edit bill"}</div>
        <label style={S.label}>Description</label>
        <input style={{...S.input,marginBottom:10}} value={desc} onChange={e=>setDesc(e.target.value)} placeholder="e.g. Netflix" autoComplete="off"/>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10}}>
          <div><label style={S.label}>Amount</label><input style={S.input} value={amt} onChange={e=>setAmt(e.target.value.replace(/[^0-9.]/g,""))} inputMode="decimal" placeholder="0.00"/></div>
          <div><label style={S.label}>Day of month</label><input style={S.input} value={day} onChange={e=>setDay(e.target.value.replace(/\D/g,"").slice(0,2))} inputMode="numeric" placeholder="1"/></div>
          <div><label style={S.label}>Category</label><select style={S.select} value={cat} onChange={e=>setCat(e.target.value)}>{categories.map(c=><option key={c.id} value={c.id}>{iconGlyph(c.icon)} {c.label}</option>)}</select></div>
        </div>
        {err&&<div style={{fontSize:13,color:T.red,marginBottom:10}}>{err}</div>}
        <div style={{display:"flex",gap:10}}>
          <button style={{...S.btn,flex:1,width:"auto"}} onClick={save}>✓ Save</button>
          <button style={{...S.btnOutline,width:"auto"}} onClick={()=>setEditing(null)}>Cancel</button>
        </div>
      </div>}
    </div>
  </div>;
}

// ─── INCOME & BUDGETS ───────────────────────────────────────────────────
function IncomeBudgetsModal({income,budgets,categories,onSave,onClose}) {
  const [inc,setInc]=useState(income?.default!=null&&income.default!==""?String(income.default):(income?.total?String(income.total):""));
  const [bmap,setBmap]=useState(()=>{const m={};(categories||[]).forEach(c=>{if(budgets&&budgets[c.id])m[c.id]=String(budgets[c.id]);});return m;});
  const save=()=>{
    const b={};
    Object.entries(bmap).forEach(([k,v])=>{const n=parseFloat(v);if(!isNaN(n)&&n>0)b[k]=n;});
    onSave(parseFloat(inc)||0,b);onClose();
  };
  return <div style={S.modal} onClick={e=>e.target===e.currentTarget&&onClose()}>
    <div style={{...S.modalBox,maxHeight:"88dvh",overflowY:"auto"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
        <div style={{fontFamily:FONT,fontSize:20}}>💰 Income & Budgets</div>
        <button onClick={onClose} style={{background:"none",border:"none",fontSize:26,cursor:"pointer",color:T.textMuted,lineHeight:1}}>×</button>
      </div>
      <div style={{fontSize:12,color:T.textMuted,marginBottom:16}}>Powers the dashboard's "remaining" and budget alerts.</div>
      <label style={S.label}>Monthly income (after tax)</label>
      <input style={{...S.input,marginBottom:16}} value={inc} onChange={e=>setInc(e.target.value.replace(/[^0-9.]/g,""))} inputMode="decimal" placeholder="e.g. 5200"/>
      <div style={{fontFamily:FONT,fontSize:16,marginBottom:10}}>Monthly budgets</div>
      {(categories||[]).map(c=><div key={c.id} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
        <div style={{width:32,height:32,borderRadius:9,background:`${c.color}14`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}><CatIcon icon={c.icon}/></div>
        <div style={{flex:1,fontSize:14}}>{c.label}</div>
        <input style={{...S.input,width:110,marginBottom:0,textAlign:"right"}} value={bmap[c.id]||""} onChange={e=>setBmap(m=>({...m,[c.id]:e.target.value.replace(/[^0-9.]/g,"")}))} inputMode="decimal" placeholder="No limit"/>
      </div>)}
      <button style={{...S.btn,marginTop:14}} onClick={save}>✓ Save</button>
    </div>
  </div>;
}

// ─── CURRENCY PICKER ────────────────────────────────────────────────────
function CurrencyPickerModal({current,rates,onPick,onClose}) {
  return <div style={S.modal} onClick={e=>e.target===e.currentTarget&&onClose()}>
    <div style={{...S.modalBox,borderRadius:20,maxWidth:340,margin:"auto"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
        <div style={{fontFamily:FONT,fontSize:20}}>Display currency</div>
        <button onClick={onClose} style={{background:"none",border:"none",fontSize:26,cursor:"pointer",color:T.textMuted,lineHeight:1}}>×</button>
      </div>
      <div style={{fontSize:12,color:T.textMuted,marginBottom:14,lineHeight:1.5}}>Amounts are stored in CAD — this only changes how they're shown{rates?"":" (live rates unavailable, using estimates)"}.</div>
      {CURRS.map(c=><button key={c.code} onClick={()=>{onPick(c.code);onClose();}} style={{display:"flex",alignItems:"center",gap:12,width:"100%",padding:"13px 14px",borderRadius:12,border:`1.5px solid ${current===c.code?T.accent:T.border}`,background:current===c.code?`${T.accent}10`:"transparent",cursor:"pointer",marginBottom:8}}>
        <span style={{fontSize:22}}>{c.flag}</span>
        <span style={{flex:1,textAlign:"left"}}><span style={{fontSize:14,fontWeight:700}}>{c.code}</span><span style={{fontSize:12,color:T.textMuted}}> · {c.label}</span></span>
        {current===c.code&&<span style={{color:T.accent,fontSize:18}}>✓</span>}
      </button>)}
    </div>
  </div>;
}

// ─── CATEGORY DELETE WITH REASSIGN ──────────────────────────────────────
function CategoryReassignModal({catName,expenses,categories,onMove,onClose}) {
  const [targets,setTargets]=useState({}); // expenseId -> target categoryId
  const [sel,setSel]=useState({}); // expenseId -> true (checkbox selection)
  const list=[...(expenses||[])].sort((a,b)=>new Date(b.created_at||0)-new Date(a.created_at||0));
  const allSet=list.length>0&&list.every(e=>targets[e.id]);
  const selCount=list.filter(e=>sel[e.id]).length;
  const allSel=list.length>0&&selCount===list.length;
  const toggleAll=()=>{if(allSel)setSel({});else{const s={};list.forEach(e=>{s[e.id]=true;});setSel(s);}};
  // Apply one category to just the checked rows, then clear the selection.
  const applyToSelected=(toId)=>{if(!toId||!selCount)return;setTargets(t=>{const n={...t};list.forEach(e=>{if(sel[e.id])n[e.id]=toId;});return n;});setSel({});};
  const targetOptions=<>{categories.map(c=><option key={c.id} value={c.id}>{iconGlyph(c.icon)} {c.label}</option>)}</>;
  return <div style={S.modal} onClick={e=>e.target===e.currentTarget&&onClose()}>
    <div style={{...S.modalBox,borderRadius:20,maxWidth:430,margin:"auto",textAlign:"center",maxHeight:"88dvh",display:"flex",flexDirection:"column"}}>
      <div style={{fontSize:36,marginBottom:10}}>🗂️</div>
      <div style={{fontFamily:FONT,fontSize:20,marginBottom:6}}>Delete "{catName}"?</div>
      <div style={{fontSize:13,color:T.textMuted,marginBottom:12,lineHeight:1.6}}>{list.length} expense{list.length===1?"":"s"} use{list.length===1?"s":""} this category. Tick some and apply a category to them, or set each row individually:</div>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
        <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:T.textMuted,flexShrink:0,cursor:"pointer"}}>
          <input type="checkbox" checked={allSel} onChange={toggleAll} style={{width:18,height:18,accentColor:T.accent}}/> Select all
        </label>
        <select style={{...S.select,marginBottom:0,opacity:selCount?1:0.5}} value="" disabled={!selCount} onChange={e=>applyToSelected(e.target.value)}>
          <option value="">{selCount?`Apply to ${selCount} selected…`:"Tick expenses first…"}</option>
          {targetOptions}
        </select>
      </div>
      <div style={{overflowY:"auto",marginBottom:14,border:`1px solid ${T.border}`,borderRadius:12,maxHeight:"42dvh",textAlign:"left"}}>
        {list.map((e,i)=><div key={e.id} style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",borderBottom:i<list.length-1?`1px solid ${T.border}`:"none"}}>
          <input type="checkbox" checked={!!sel[e.id]} onChange={()=>setSel(s=>({...s,[e.id]:!s[e.id]}))} style={{flexShrink:0,width:18,height:18,accentColor:T.accent}}/>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:13,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.description}</div>
            <div style={{fontSize:11,color:T.textMuted,marginTop:1}}>{e.date} · {fmt(e.amount)}{e.recurring?" · 🔄":""}</div>
          </div>
          <select style={{...S.select,marginBottom:0,maxWidth:150,fontSize:12,padding:"9px 10px",flexShrink:0}} value={targets[e.id]||""} onChange={ev=>setTargets(t=>({...t,[e.id]:ev.target.value}))}>
            <option value="">Choose…</option>
            {targetOptions}
          </select>
        </div>)}
      </div>
      <div style={{display:"flex",gap:10}}>
        <button style={{...S.btn,flex:1,width:"auto",background:T.red,opacity:allSet?1:0.4}} disabled={!allSet} onClick={()=>{onMove(list.map(e=>({id:e.id,toId:targets[e.id]})));onClose();}}>Move & delete</button>
        <button style={{...S.btnOutline,flex:1,width:"auto"}} onClick={onClose}>Cancel</button>
      </div>
    </div>
  </div>;
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function DashboardTab({expenses,income,budgets,categories,setTab,onEditIncome}) {
  const mk=nowMonthKey();
  const prevMk=(()=>{const d=new Date();d.setMonth(d.getMonth()-1);return`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;})();
  const mInc=getMonthIncome(income,mk);
  const thisM=expenses.filter(e=>expMonthKey(e)===mk);
  const prevM=expenses.filter(e=>expMonthKey(e)===prevMk);
  const tExp=thisM.reduce((s,e)=>s+e.amount,0);
  const prevExp=prevM.reduce((s,e)=>s+e.amount,0);
  const net=mInc-tExp,sr=mInc?((net/mInc)*100).toFixed(1):0;
  const recurringTotal=thisM.filter(e=>e.recurring).reduce((s,e)=>s+e.amount,0);
  const recurringCount=thisM.filter(e=>e.recurring).length;
  const momChange=prevExp>0?((tExp-prevExp)/prevExp*100):null;
  const months=[...new Set(expenses.map(e=>expMonthKey(e)))].sort().reverse().slice(0,6).reverse();
  const chartData=months.map(k=>({label:monthShort(k),value:expenses.filter(e=>expMonthKey(e)===k).reduce((s,e)=>s+e.amount,0)}));
  const catT={};thisM.forEach(e=>{catT[e.category]=(catT[e.category]||0)+e.amount;});
  const topCats=Object.entries(catT).sort((a,b)=>b[1]-a[1]).slice(0,5);
  const maxCat=topCats[0]?.[1]||1;
  const alerts=Object.entries(budgets).filter(([id,bud])=>bud&&catT[id]>0).map(([id,bud])=>{
    const c=categories.find(x=>x.id===id);const spent=catT[id]||0;
    return{id,label:c?.label||id,icon:c?.icon||"📦",color:c?.color||"#888",spent,bud,pct:spent/bud};
  }).filter(a=>a.pct>=0.75).sort((a,b)=>b.pct-a.pct);

  return <div className="tab-content">
    <div style={{...S.card,position:"relative",background:"linear-gradient(135deg,#1C1915 0%,#2E2318 100%)",border:"none",marginBottom:12,padding:"22px 20px 18px"}}>
      <button onClick={onEditIncome} title="Income & budgets" style={{position:"absolute",top:12,right:12,background:"rgba(255,255,255,0.10)",border:"none",borderRadius:9,color:"#fff",fontSize:15,padding:"7px 10px",cursor:"pointer"}}>⚙️</button>
      <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:2,color:"rgba(255,255,255,0.4)",marginBottom:6,fontWeight:600}}>
        Total Spent — {new Date().toLocaleDateString("en-US",{month:"long",year:"numeric"})}
      </div>
      <div style={{fontFamily:MONO,fontSize:32,fontWeight:700,color:"#fff",lineHeight:1,marginBottom:4}}>{fmt(tExp)}</div>
      <div style={{fontSize:12,color:net>=0&&mInc>0?"#4ade80":"rgba(255,255,255,0.4)"}}>
        {mInc>0?`${fmt(Math.max(0,net))} remaining · ${sr}% saved`:`${thisM.length} transactions this month`}
      </div>
      {mInc<=0&&<button onClick={onEditIncome} style={{marginTop:12,background:"rgba(255,255,255,0.14)",border:"none",borderRadius:9,color:"#fff",fontSize:12,fontWeight:700,padding:"9px 16px",cursor:"pointer"}}>＋ Set monthly income & budgets</button>}
      {thisM.length===0&&mInc>0&&<button onClick={()=>setTab("expenses")} style={{marginTop:12,background:"rgba(255,255,255,0.14)",border:"none",borderRadius:9,color:"#fff",fontSize:12,fontWeight:700,padding:"9px 16px",cursor:"pointer"}}>＋ Add your first expense</button>}
      {momChange!==null&&<div style={{fontSize:11,marginTop:6,color:momChange>0?"#f87171":"#4ade80",fontWeight:600}}>
        {momChange>0?"▲":"▼"} {Math.abs(momChange).toFixed(1)}% vs last month
      </div>}
    </div>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
      <StatCard label="Recurring" value={fmt(recurringTotal)} sub={`${recurringCount} bills`} accent={T.blue} bg={T.blueBg} onClick={()=>setTab("expenses")}/>
      <StatCard label="One-time" value={fmt(tExp-recurringTotal)} sub={`${thisM.length-recurringCount} transactions`} accent={T.accent} bg={T.accentLight} onClick={()=>setTab("spending")}/>
    </div>
    {alerts.length>0&&<div style={{...S.card,marginBottom:12,borderColor:alerts.some(a=>a.pct>=1)?T.red:T.accent,borderWidth:1.5}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <div style={{fontSize:13,fontWeight:700,color:T.text}}>⚠️ Budget Alerts</div>
        <button onClick={()=>setTab("spending")} style={{fontSize:11,color:T.accent,background:"none",border:"none",cursor:"pointer",fontWeight:700}}>View →</button>
      </div>
      {alerts.slice(0,3).map(a=>{const over=a.pct>=1;return<div key={a.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:`1px solid ${T.border}`}}>
        <span style={{fontSize:18,flexShrink:0}}><CatIcon icon={a.icon}/></span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
            <span style={{fontSize:13,fontWeight:600}}>{a.label}</span>
            <span style={{fontFamily:MONO,fontSize:12,fontWeight:700,color:over?T.red:T.accent}}>{fmt(a.spent)}<span style={{color:T.textMuted,fontWeight:400}}>/{fmt(a.bud)}</span></span>
          </div>
          <div style={{height:5,background:T.bgCardAlt,borderRadius:3,overflow:"hidden"}}>
            <div style={{height:"100%",width:`${Math.min(a.pct*100,100)}%`,background:over?T.red:T.accent,borderRadius:3}}/>
          </div>
        </div>
        <span style={{fontSize:11,fontWeight:700,color:over?T.red:T.accent,flexShrink:0}}>{over?"OVER":`${(a.pct*100).toFixed(0)}%`}</span>
      </div>;})}
    </div>}
    <div className="g2">
      <div style={S.card}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div style={S.sectionTitle}>Monthly Spending</div>
          <button onClick={()=>setTab("spending")} style={{fontSize:12,color:T.accent,background:"none",border:"none",cursor:"pointer",fontFamily:SANS,fontWeight:700}}>View all →</button>
        </div>
        <BarChart data={chartData} color={T.accent}/>
      </div>
      <div style={S.card}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div style={S.sectionTitle}>Top This Month</div>
          <div style={{fontSize:12,color:T.textMuted}}>{monthLabel(mk)}</div>
        </div>
        {topCats.length===0?<div style={{color:T.textMuted,fontSize:13,textAlign:"center",paddingTop:28}}>No expenses this month</div>:topCats.map(([catId,amt])=>{const c=categories.find(x=>x.id===catId)||{icon:"📦",label:catId,color:"#888"};const pct=tExp?((amt/tExp)*100).toFixed(0):0;return<div key={catId} style={{marginBottom:12}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
            <span style={{fontSize:13,fontWeight:500}}><CatIcon icon={c.icon}/> {c.label}</span>
            <div style={{display:"flex",gap:6,alignItems:"center"}}><span style={{fontSize:10,color:T.textMuted}}>{pct}%</span><span style={{fontFamily:MONO,fontSize:13,color:c.color,fontWeight:700}}>{fmt(amt)}</span></div>
          </div>
          <div style={{height:7,borderRadius:4,background:T.bgCardAlt,overflow:"hidden"}}><div style={{height:"100%",borderRadius:4,background:c.color,width:`${(amt/maxCat)*100}%`,transition:"width 0.5s"}}/></div>
        </div>;})}
      </div>
    </div>
    <div style={S.card}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div style={S.sectionTitle}>Recent Expenses</div>
        <button onClick={()=>setTab("expenses")} style={{fontSize:12,color:T.accent,background:"none",border:"none",cursor:"pointer",fontFamily:SANS,fontWeight:700}}>See all →</button>
      </div>
      {expenses.slice(0,6).length===0?<div style={{color:T.textMuted,fontSize:13,textAlign:"center",paddingTop:24}}>No transactions yet</div>:expenses.slice(0,6).map(e=>{const c=catForExpense(e,categories);return<div key={e.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 0",borderBottom:`1px solid ${T.border}`}}>
        <div style={{width:36,height:36,borderRadius:10,background:`${c.color}15`,border:`1px solid ${c.color}22`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,flexShrink:0}}><CatIcon icon={c.icon}/></div>
        <div style={{flex:1,minWidth:0}}><div style={{fontSize:14,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{e.description}</div><div style={{fontSize:11,color:T.textMuted,marginTop:1}}>{e.date}{e.recurring&&<span style={{marginLeft:6,color:T.blue}}>🔄</span>}</div></div>
        <div style={{fontFamily:MONO,fontSize:13,fontWeight:700,color:T.accent,flexShrink:0}}>{fmt(e.amount)}</div>
      </div>;})}
    </div>
  </div>;
}

// ─── EXPENSES TAB ─────────────────────────────────────────────────────────────
function ExpensesTab({expenses,categories,fxRates,displayCurrency,onAddExpense,onAddExpenses,onEditExpense,onDeleteExpense,onDeleteRecurringSeries,onEditRecurringSeries,onUpdateTemplate,onDeleteInstanceOnly,onDeleteTemplateSeries,findTemplateForInstance,onHealTemplateLink,onToast,onAskConfirm,templates,onManageRecurring,onAddCategory,onEditCategory,onDeleteCategory,onDeleteCategoryAndMove,locks,privacyMode,onRequestUnlock,onToggleLock,householdId,learnedKW,onLearnKW}) {
  const [form,setForm]=useState({description:"",amount:"",category:"",recurring:false,autoRecur:false,dateISO:localISO()});
  const [formErr,setFormErr]=useState("");
  // Entry currency: what the typed amount is denominated in. Converted to CAD
  // (the storage currency) at the live rate on save. Defaults to display currency.
  // Persists across reloads so a trip week stays in the foreign currency.
  const [entryCur,setEntryCurRaw]=useState(()=>{try{return localStorage.getItem("vestEntryCur")||displayCurrency||"CAD";}catch(e){return displayCurrency||"CAD";}});
  const setEntryCur=(c)=>{setEntryCurRaw(c);try{localStorage.setItem("vestEntryCur",c);}catch(e){}};
  const rateFor=(code)=>code==="CAD"?1:((fxRates&&fxRates[code])||FX_FALLBACK[code]||1);
  const [autocat,setAutocat]=useState(null);
  const [filter,setFilter]=useState("all");
  const [search,setSearch]=useState("");
  const [monthFilter,setMonthFilter]=useState("all");
  const searchBoxRef=useRef(null); // scroll target when an expense name is clicked
  const [showCatModal,setShowCatModal]=useState(false);
  const [reassignCat,setReassignCat]=useState(null); // {id,label,items} for delete-with-move
  const [editingExpense,setEditingExpense]=useState(null);
  const [deleteTarget,setDeleteTarget]=useState(null);
  const [showScanner,setShowScanner]=useState(false);
  const [suggHide,setSuggHide]=useState(false); // dismiss description autocomplete
  const buildKW=()=>{
    const kw={...DEFAULT_KEYWORDS};
    categories.filter(c=>c.custom&&c.keywords?.length).forEach(c=>{kw[c.id]=c.keywords;});
    return kw;
  };
  const handleDesc=(v)=>{
    // learnedKW is passed separately (not merged into kw) so explicit corrections
    // always take priority over default/custom keyword lists — see guessCategory.
    const cat=v.length>2?guessCategory(v,buildKW(),learnedKW):null;
    setAutocat(cat);
    setSuggHide(false); // typing re-opens autocomplete
    setForm(f=>({...f,description:v}));
  };
  // ── Description autocomplete from history ──────────────────────────────
  // Every past expense, most recent first — no deduping by description, so
  // all matching records (e.g. every Jolliebee visit, any month) show up.
  // Tapping one restores its description + amount + category.
  const pastDescs=[...expenses]
    .filter(e=>(e.description||"").trim())
    .sort((a,b)=>new Date(b.created_at||0)-new Date(a.created_at||0));
  const typedDesc=form.description.trim().toLowerCase();
  // Word-start matching (same rule as the app's search): the typed text must
  // start the description or start any word inside it. So "lo" matches
  // "Lotto" but not "Uniqlo", and "parking" matches "Edmonton Parking" while
  // "king" does not.
  const descMatches=typedDesc.length>=2&&!suggHide
    ?pastDescs.filter(e=>{const d=e.description.toLowerCase();return d!==typedDesc&&d.split(/\s+/).some(w=>w.startsWith(typedDesc));})
    :[];
  const pickSuggestion=(e)=>{
    // Suggested amounts are stored CAD — show them in the entry currency.
    const conv=e.amount!=null?e.amount*rateFor(entryCur):null;
    setForm(f=>({...f,description:e.description,amount:conv!=null?String(Math.round(conv*100)/100):f.amount,category:e.category||""}));
    setAutocat(null);
    setSuggHide(true);
  };
  const addExpense=()=>{
    const amt=parseFloat(form.amount);
    if(!form.description.trim()){setFormErr("Enter a description");return;}
    if(!form.amount||isNaN(amt)){setFormErr("Enter an amount");return;}
    if(amt<=0){setFormErr("Amount must be more than $0");return;}
    setFormErr("");
    const catId=form.category||autocat||"other";
    const catObj=categories.find(c=>c.id===catId)||categories[categories.length-1];
    // Convert the typed amount to CAD (storage currency) at the current rate.
    // The stored CAD value is fixed — later rate moves don't rewrite history.
    const cadAmt=Math.round((amt/rateFor(entryCur))*100)/100;
    // Learn: if the user manually chose a category, remember this description
    // (full phrase + each meaningful word) in one batched call so the
    // correction reliably sticks next time.
    if(form.category&&form.category!==autocat&&onLearnKW){
      onLearnKW(form.description,form.category);
    }
      onAddExpense({description:form.description,amount:cadAmt,category:catId,catObj:{...catObj,autoRecur:!!form.autoRecur},recurring:form.recurring,autoRecur:!!form.autoRecur,dateISO:form.dateISO});
    setForm({description:"",amount:"",category:"",recurring:false,dateISO:localISO()});setAutocat(null);
  };
  // Learn from corrections made via Edit (not just the add form): if the final
  // category isn't what auto-detect would guess for the final description,
  // remember the user's choice so it sticks next time. For recurring bills with
  // "apply to future months" checked, the TEMPLATE is updated — future months
  // follow the new values until changed again.
  const saveEditExpense=async(orig,data)=>{
    const{applyToFuture,...clean}=data;
    if(clean.category&&onLearnKW){
      const auto=guessCategory(clean.description||"",buildKW(),learnedKW);
      if(clean.category!==auto)onLearnKW(clean.description,clean.category);
    }
    const changed=clean.description!==orig.description||Number(clean.amount)!==Number(orig.amount)||clean.category!==orig.category;
    let tpl=orig.recurring&&findTemplateForInstance?findTemplateForInstance(orig):null;
    // The link can break (e.g. description drifted after a rename) — repair
    // it before giving up, so "apply to future" is never silently dropped.
    if(!tpl&&orig.recurring&&clean.recurring&&applyToFuture&&changed&&onHealTemplateLink){
      try{tpl=await onHealTemplateLink(orig);}catch(e){tpl=null;}
    }
    if(tpl&&clean.recurring&&applyToFuture&&changed&&onUpdateTemplate){
      // Guard: editing an earlier month would silently overwrite later months
      // that currently have DIFFERENT values. List them and ask first.
      const origMk=expMonthKey(orig);
      const differing=expenses.filter(e=>e.template_id===tpl.id&&e.id!==orig.id&&expMonthKey(e)>=origMk
        &&(e.description!==clean.description||Number(e.amount)!==Number(clean.amount)||e.category!==clean.category));
      if(differing.length&&onAskConfirm){
        const mLabel=mk=>`${monthShort(mk)} ${mk.slice(0,4)}`;
        onAskConfirm({
          title:"Months differ — update them all?",
          message:<span>These months currently say something different and would be overwritten:<br/>{differing.map(e=><span key={e.id} style={{display:"block",marginTop:6}}><b>{mLabel(expMonthKey(e))}:</b> {e.description} · {fmt(e.amount)}</span>)}<br/>They would all become <b>{clean.description} · {fmt(clean.amount)}</b>.</span>,
          confirmLabel:`Update ${differing.length+1} months`,
          onConfirm:()=>onUpdateTemplate(tpl,orig,clean)
        });
        setEditingExpense(null);
        return;
      }
      onUpdateTemplate(tpl,orig,clean);
    }else if(!tpl&&orig.recurring&&clean.recurring&&applyToFuture&&changed&&onEditRecurringSeries){
      onEditRecurringSeries(orig,clean); // legacy fallback (templates table not migrated yet)
    }else{
      // "Apply to future" was asked for but no series could be found — say so
      // instead of silently editing one month.
      if(!tpl&&orig.recurring&&clean.recurring&&applyToFuture&&changed&&onToast){
        onToast("Couldn't find this bill's recurring series — changed this month only. Update the series in 🔄 Recurring bills.","err");
      }
      onEditExpense(orig.id,clean);
    }
    setEditingExpense(null);
  };
  const allMonthKeys=[...new Set(expenses.map(e=>expMonthKey(e)))].sort().reverse();
  let filtered=expenses;
  // While searching, the month/category filters are ignored — search covers
  // ALL records so every match across the full history shows up.
  const searching=search.trim().length>0;
  if(filter!=="all"&&!searching)filtered=filtered.filter(e=>e.category===filter);
  // Normalize the query into words exactly like description words are normalized
  // below, so "T&T" / "t&t" becomes "tt" and matches "T&T" in descriptions.
  // (Previously the query kept its "&" while words had it stripped, so "T&T"
  // could never match anything.)
  const qWords=search.toLowerCase().trim().split(/\s+/).map(w=>w.replace(/[^a-z0-9]/g,"")).filter(Boolean);
  // Numeric query words (e.g. "58.62", "$58.62") match amounts by prefix, so
  // typing a cost finds every transaction at that cost across all history.
  // (Kept separate from qWords because that strips "." — "58.62" would
  // otherwise become "5862".)
  const qAmounts=search.trim().split(/\s+/).map(w=>w.replace(/[$,\s]/g,"")).filter(w=>/^\d+(\.\d*)?$/.test(w));
  if(qWords.length||qAmounts.length){
    filtered=filtered.filter(e=>{
      const desc=(e.description||"").toLowerCase();
      // Strip punctuation so "Felix's" becomes "felixs" and "T&T" becomes "tt"
      const words=desc.split(/\s+/).map(w=>w.replace(/[^a-z0-9]/g,""));
      // Match if ANY query word is a prefix of ANY description word.
      // Single-word searches behave exactly as before: "pho" matches "Pho" and
      // "Photos" (both genuinely start with "pho"), but not "Sephora" since
      // "pho" only appears mid-word there.
      // Multi-word queries (e.g. clicking an expense name to find its siblings)
      // match same/similar names via any shared word.
      // Amounts match on their 2-decimal form: "58.62" finds $58.62 exactly,
      // "58.6" narrows to $58.6x, "58" to anything starting with 58.
      const amtStr=(parseFloat(e.amount)||0).toFixed(2);
      return qWords.some(q=>words.some(w=>w.startsWith(q)))||qAmounts.some(a=>amtStr.startsWith(a));
    });
  }
  if(monthFilter!=="all"&&!searching)filtered=filtered.filter(e=>expMonthKey(e)===monthFilter);
  const grouped={};filtered.forEach(e=>{const k=expMonthKey(e);if(!grouped[k])grouped[k]=[];grouped[k].push(e);});
  const sortedMonths=Object.keys(grouped).sort().reverse();
  // Scope stat cards to current month only
  const curMk=nowMonthKey();
  const curMonthExp=expenses.filter(e=>expMonthKey(e)===curMk);
  const total=curMonthExp.reduce((s,e)=>s+e.amount,0);
  const monthly=curMonthExp.filter(e=>e.recurring).reduce((s,e)=>s+e.amount,0);
  const catTotals={};curMonthExp.forEach(e=>{catTotals[e.category]=(catTotals[e.category]||0)+e.amount;});
  const topCats=Object.entries(catTotals).sort((a,b)=>b[1]-a[1]);
  const maxCat=topCats[0]?.[1]||1;
  const effectiveCat=form.category||autocat;
  const displayCat=effectiveCat?categories.find(c=>c.id===effectiveCat):null;

  return <div className="tab-content">
    {showCatModal&&<ManageCategoriesModal categories={categories} onAdd={onAddCategory} onEdit={onEditCategory} onRemove={(id)=>{
      const c=categories.find(x=>x.id===id);
      const n=expenses.filter(e=>e.category===id).length;
      if(n>0&&onDeleteCategoryAndMove){setReassignCat({id,label:c?.label||id,items:expenses.filter(e=>e.category===id)});}
      else if(onAskConfirm){onAskConfirm({title:"Delete category?",message:`"${c?.label||id}" — this can't be undone.`,confirmLabel:"Delete",danger:true,onConfirm:()=>onDeleteCategory(id)});}
      else{onDeleteCategory(id);}
    }} onClose={()=>setShowCatModal(false)}/>}
    {reassignCat&&<CategoryReassignModal catName={reassignCat.label} expenses={reassignCat.items||[]} categories={categories.filter(c=>c.id!==reassignCat.id)} onMove={(moves)=>{onDeleteCategoryAndMove(reassignCat.id,moves);setReassignCat(null);}} onClose={()=>setReassignCat(null)}/>}
    {showScanner&&<ReceiptScannerModal categories={categories} fxRates={fxRates} displayCurrency={displayCurrency} onAddExpenses={onAddExpenses} onClose={()=>setShowScanner(false)}/>}
    {editingExpense&&<EditExpenseModal expense={editingExpense} categories={categories} onSave={data=>saveEditExpense(editingExpense,data)} onClose={()=>setEditingExpense(null)}/>}
    {deleteTarget&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={e=>e.target===e.currentTarget&&setDeleteTarget(null)}>
      <div style={{background:"#fff",borderRadius:20,padding:28,maxWidth:360,width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
        <div style={{fontSize:28,marginBottom:12,textAlign:"center"}}>🔄</div>
        <div style={{fontFamily:"'DM Serif Display',serif",fontSize:18,marginBottom:6,textAlign:"center"}}>Recurring Expense</div>
        <div style={{fontSize:13,color:"#6B6560",marginBottom:20,textAlign:"center"}}>{deleteTarget.description} · {deleteTarget.date}</div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <button style={{padding:"13px 16px",borderRadius:12,border:"1.5px solid #E5E0D8",background:"#F9F7F4",cursor:"pointer",fontSize:14,fontWeight:600,textAlign:"left"}} onClick={()=>{onDeleteInstanceOnly(deleteTarget);setDeleteTarget(null);}}>
            🗑 Delete this month only
            <div style={{fontSize:11,color:"#A09A93",fontWeight:400,marginTop:3}}>Removes only this entry. Future months still auto-create from the recurring bill.</div>
          </button>
          <button style={{padding:"13px 16px",borderRadius:12,border:"1.5px solid #E5E0D8",background:"#F9F7F4",cursor:"pointer",fontSize:14,fontWeight:600,textAlign:"left"}} onClick={async()=>{await onDeleteTemplateSeries(deleteTarget);setDeleteTarget(null);}}>
            🚫 Delete the whole series
            <div style={{fontSize:11,color:"#A09A93",fontWeight:400,marginTop:3}}>Stops the recurring bill entirely — this and future months are removed. Past months stay as history.</div>
          </button>
          <button style={{padding:"12px 16px",borderRadius:12,border:"1.5px solid #E5E0D8",background:"transparent",cursor:"pointer",fontSize:14,color:"#6B6560"}} onClick={()=>setDeleteTarget(null)}>Cancel</button>
        </div>
      </div>
    </div>}

    <div className="g3">
      <StatCard label="Total Logged" value={fmt(total)} sub={`${curMonthExp.length} transactions this month`}/>
      <StatCard label="Monthly Fixed" value={fmt(monthly)} sub="Recurring" accent={T.accent} bg={T.accentLight}/>
      <StatCard label="Variable" value={fmt(total-monthly)} sub="One-time"/>
    </div>

    {/* Add Expense Card — redesigned */}
    <div style={{...S.card,marginBottom:16}}>
      {/* Quick action buttons */}
      <div style={{display:"flex",gap:12,marginBottom:22}}>
        <button onClick={()=>setShowScanner(true)} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:7,padding:"20px 12px",background:"linear-gradient(135deg,#1C1915,#2E2318)",border:"none",borderRadius:16,cursor:"pointer",color:"#fff"}}>
          <span style={{fontSize:28}}>📷</span>
          <span style={{fontFamily:SANS,fontSize:14,fontWeight:700}}>Scan Receipt</span>
          <span style={{fontSize:11,opacity:0.45}}>AI auto-categorizes items</span>
        </button>
        <button onClick={()=>setShowCatModal(true)} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:7,padding:"20px 12px",background:T.bgCardAlt,border:`1.5px solid ${T.border}`,borderRadius:16,cursor:"pointer"}}>
          <span style={{fontSize:28}}>🏷️</span>
          <span style={{fontFamily:SANS,fontSize:14,fontWeight:700,color:T.text}}>Categories</span>
          <span style={{fontSize:11,color:T.textMuted}}>Add & manage</span>
        </button>
      </div>

      {/* Divider */}
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
        <div style={{flex:1,height:1,background:T.border}}/>
        <span style={{fontSize:11,color:T.textMuted,fontWeight:700,textTransform:"uppercase",letterSpacing:1.5}}>Add manually</span>
        <div style={{flex:1,height:1,background:T.border}}/>
      </div>

      {/* Form */}
      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        <div>
          <label style={S.label}>Description</label>
          <input style={S.input} placeholder="e.g. Mortgage, Costco, Netflix…" value={form.description} onChange={e=>handleDesc(e.target.value)}/>
          {descMatches.length>0&&<div style={{marginTop:6,background:T.bgCard,border:`1.5px solid ${T.border}`,borderRadius:12,overflowY:"auto",maxHeight:300}}>
            {descMatches.map((m,mi)=>{
              const mc=categories.find(c=>c.id===m.category);
              return <button key={m.id} onClick={()=>pickSuggestion(m)} style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"10px 12px",background:"transparent",border:"none",borderBottom:mi<descMatches.length-1?`1px solid ${T.border}`:"none",cursor:"pointer",textAlign:"left"}}>
                <span style={{fontSize:16,flexShrink:0}}><CatIcon icon={mc?.icon||"🧾"}/></span>
                <span style={{flex:1,minWidth:0,fontSize:13,fontWeight:600,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.description}</span>
                <span style={{fontSize:12,color:T.textMuted,flexShrink:0}}>{m.date||""}</span>
                <span style={{fontSize:12,color:T.textMuted,flexShrink:0}}>{mc?.label||""}</span>
                <span style={{fontFamily:MONO,fontSize:13,fontWeight:700,color:T.text,flexShrink:0}}>{fmt(m.amount)}</span>
              </button>;
            })}
          </div>}
          {displayCat&&<div style={{marginTop:10,display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:12,color:T.textMuted}}>{form.category?"Category:":"Auto-detected:"}</span>
            <span style={{...S.tag(displayCat.color),fontSize:13,padding:"5px 12px"}}><CatIcon icon={displayCat.icon}/> {displayCat.label}</span>
          </div>}
        </div>
        <div className="g2">
          <div><label style={S.label}>Amount</label><input id="amt-inp" style={S.input} type="number" inputMode="decimal" placeholder="0.00" value={form.amount} onChange={e=>setForm(f=>({...f,amount:e.target.value}))}/>
            <div style={{display:"flex",gap:6,marginTop:8,flexWrap:"wrap"}}>
              {CURRS.map(c=><button key={c.code} type="button" onClick={()=>setEntryCur(c.code)} style={{padding:"6px 10px",borderRadius:8,border:`1.5px solid ${entryCur===c.code?T.accent:T.border}`,background:entryCur===c.code?`${T.accent}14`:"transparent",fontSize:12,fontWeight:700,cursor:"pointer",color:T.text}}>{c.flag} {c.code}</button>)}
            </div>
            {entryCur!=="CAD"&&form.amount&&!isNaN(parseFloat(form.amount))&&<div style={{fontSize:12,color:T.textMuted,marginTop:6}}>≈ {fmt(parseFloat(form.amount)/rateFor(entryCur),"CAD")} stored · live rate</div>}
          </div>
          <div><label style={S.label}>Category</label><select style={S.select} value={form.category} onChange={e=>{setForm(f=>({...f,category:e.target.value}));setAutocat(null);}}><option value="">Auto-detect</option>{categories.map(c=><option key={c.id} value={c.id}>{iconGlyph(c.icon)} {c.label}</option>)}</select></div>
        </div>
        <div><label style={S.label}>Date</label><input style={S.input} type="date" value={form.dateISO} max={localISO()} onChange={e=>setForm(f=>({...f,dateISO:e.target.value}))}/><div style={{fontSize:11,color:T.textMuted,marginTop:4}}>Defaults to today — pick a past date for a forgotten expense and it files into that month.</div></div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <div style={{display:"flex",alignItems:"center",gap:12,padding:"13px 15px",background:form.recurring?`${T.accent}10`:T.bgCardAlt,borderRadius:12,border:`1.5px solid ${form.recurring?T.accent:T.border}`,cursor:"pointer"}} onClick={()=>setForm(f=>({...f,recurring:!f.recurring}))}>
            <input type="checkbox" checked={form.recurring} onChange={e=>setForm(f=>({...f,recurring:e.target.checked}))} style={{accentColor:T.accent,width:20,height:20,flexShrink:0}}/>
            <div><div style={{fontSize:14,fontWeight:600,color:T.text}}>🔄 Recurring expense</div><div style={{fontSize:12,color:T.textMuted}}>Repeats every month until you stop it</div></div>
          </div>
        </div>
        <button style={S.btn} onClick={addExpense}>+ Add Expense</button>
        {formErr&&<div style={{fontSize:13,color:T.red,textAlign:"center",marginTop:8}}>{formErr}</div>}
      </div>
    </div>

    {/* Breakdown */}
    <div style={{...S.card,marginBottom:16}}>
      <div style={S.sectionTitle}>Spending Breakdown</div>
      {topCats.length===0?<div style={{color:T.textMuted,fontSize:13,textAlign:"center",padding:"30px 0"}}>Add expenses to see breakdown</div>:topCats.map(([catId,amt])=>{const c=categories.find(x=>x.id===catId)||{icon:"📦",label:catId,color:"#888"};return<div key={catId} style={{marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}><span style={{fontSize:14,fontWeight:500}}><CatIcon icon={c.icon}/> {c.label}</span><span style={{fontFamily:MONO,fontSize:13,color:c.color,fontWeight:700}}>{fmt(amt)}</span></div>
        <div style={{height:8,borderRadius:4,background:T.bgCardAlt,overflow:"hidden"}}><div style={{height:"100%",borderRadius:4,background:`linear-gradient(90deg,${c.color},${c.color}AA)`,width:`${(amt/maxCat)*100}%`,transition:"width 0.5s"}}/></div>
      </div>;})}
    </div>

    {/* Product Tracker */}

    {/* History */}
    <button onClick={onManageRecurring} style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%",padding:"13px 16px",borderRadius:12,border:`1.5px solid ${T.border}`,background:T.bgCardAlt,cursor:"pointer",marginBottom:14}}>
      <span style={{fontSize:14,fontWeight:600}}>🔄 Recurring bills</span>
      <span style={{fontSize:12,color:T.textMuted}}>{(templates||[]).length} active · manage →</span>
    </button>
    <LockableCard sectionKey="expenses" label="💳 Expense History" locks={locks} privacyMode={privacyMode} onRequestUnlock={onRequestUnlock} onToggleLock={onToggleLock} style={{marginBottom:20}}>
      <div style={{marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,gap:8}}>
          <div style={S.sectionTitle}>Transaction History</div>
          <select style={{...S.select,width:"auto",padding:"9px 12px",fontSize:13}} value={monthFilter} onChange={e=>setMonthFilter(e.target.value)}>
            <option value="all">All Months</option>{allMonthKeys.map(k=><option key={k} value={k}>{monthLabel(k)}</option>)}
          </select>
        </div>
        <div ref={searchBoxRef} style={{position:"relative",marginBottom:10}}>
          <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",fontSize:14,color:T.textMuted,pointerEvents:"none"}}>🔍</span>
          <input style={{...S.input,paddingLeft:36,paddingTop:"10px",paddingBottom:"10px"}} placeholder="Search transactions…" value={search} onChange={e=>setSearch(e.target.value)}/>
          {search&&<button onClick={()=>setSearch("")} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",fontSize:18,cursor:"pointer",color:T.textMuted,lineHeight:1}}>×</button>}
        </div>
        <div className="cat-pills">
          <button style={{...S.pill(filter==="all",T.accent),flexShrink:0}} onClick={()=>setFilter("all")}>All</button>
          {categories.map(c=><button key={c.id} style={{...S.pill(filter===c.id,c.color),flexShrink:0}} onClick={()=>setFilter(c.id)}><CatIcon icon={c.icon}/> {c.label}</button>)}
        </div>
      </div>
      {qWords.length>0&&filtered.length>0&&<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:T.accentLight,border:`1.5px solid ${T.accent}`,borderRadius:12,padding:"10px 14px",marginBottom:14}}>
        <span style={{fontSize:13,fontWeight:600,color:T.text}}>🔍 {filtered.length} result{filtered.length===1?"":"s"} for &ldquo;{search.trim()}&rdquo; · all months</span>
        <span style={{fontFamily:MONO,fontSize:15,fontWeight:700,color:T.accent}}>{fmt(filtered.reduce((s,e)=>s+(parseFloat(e.amount)||0),0))} total</span>
      </div>}
      {sortedMonths.length===0
        ?<div style={{textAlign:"center",color:T.textMuted,padding:"40px 0",fontSize:14}}>{searching?`No matches for "${search.trim()}" in any month.`:"No expenses yet. Add one above or scan a receipt!"}</div>
        :sortedMonths.map(mk=>{
          const mes=grouped[mk],mt=mes.reduce((s,e)=>s+e.amount,0);
          return <div key={mk} style={{marginBottom:22}}>
            <div style={{padding:"10px 0 8px",borderBottom:`2px solid ${T.border}`,marginBottom:2}}>
              <div style={{fontFamily:FONT,fontSize:16}}>{monthLabel(mk)}</div>
            </div>
            {mes.map(e=>{
              const c=catForExpense(e,categories);
              return <div key={e.id} style={{display:"flex",alignItems:"center",padding:"12px 0",borderBottom:`1px solid ${T.border}`,gap:10}}>
                <div style={{width:38,height:38,borderRadius:10,background:`${c.color}14`,border:`1px solid ${c.color}22`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}><CatIcon icon={c.icon}/></div>
                <div style={{flex:1,minWidth:0}}><div onClick={()=>{setSearch(e.description);setMonthFilter("all");searchBoxRef.current?.scrollIntoView({behavior:"smooth",block:"center"});}} title="Show expenses with a similar name" style={{fontSize:14,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",cursor:"pointer"}}>{e.description}</div><div style={{fontSize:11,color:T.textMuted,marginTop:2}}>{e.date}{e.recurring?" · 🔄":""}{e.pendingSync?" · ⏳ syncing":""}</div></div>
                <span style={{...S.tag(c.color),flexShrink:0}}><CatIcon icon={c.icon}/></span>
                <div style={{fontFamily:MONO,fontSize:14,fontWeight:700,color:T.accent,flexShrink:0,minWidth:68,textAlign:"right"}}>{fmt(e.amount)}</div>
                <button onClick={()=>setEditingExpense(e)} style={{background:"none",border:"none",color:T.blue,cursor:"pointer",fontSize:16,padding:"10px 8px",flexShrink:0}}>✏️</button>
                <button onClick={()=>{
                  if(e.recurring){
                    setDeleteTarget(e);
                  } else if(onAskConfirm){
                    onAskConfirm({title:"Delete expense?",message:`${e.description} · ${fmt(e.amount)} — this can't be undone.`,confirmLabel:"Delete",danger:true,onConfirm:()=>onDeleteExpense(e.id)});
                  } else {
                    onDeleteExpense(e.id);
                  }
                }} style={{background:"none",border:"none",color:T.textMuted,cursor:"pointer",fontSize:22,lineHeight:1,padding:"10px 12px",flexShrink:0}}>×</button>
              </div>;
            })}
            <div style={{display:"flex",justifyContent:"flex-end",alignItems:"baseline",gap:8,padding:"10px 0 2px"}}>
              <span style={{fontSize:12,color:T.textMuted}}>Month total</span>
              <span style={{fontFamily:MONO,fontSize:13,fontWeight:700,color:T.accent}}>{fmt(mt)}</span>
            </div>
          </div>;
        })
      }
      {qWords.length>0&&filtered.length>0&&<div style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:T.accentLight,border:`1.5px solid ${T.accent}`,borderRadius:12,padding:"12px 14px",marginTop:6}}>
        <span style={{fontSize:13,fontWeight:600,color:T.text}}>Total · all results</span>
        <span style={{fontFamily:MONO,fontSize:16,fontWeight:700,color:T.accent}}>{fmt(filtered.reduce((s,e)=>s+(parseFloat(e.amount)||0),0))}</span>
      </div>}
    </LockableCard>
  </div>;
}

// ─── SHARED LIST TAB (Grocery / To-Do with photos) ────────────────────────────
// ─── RECEIPT SCANNER ──────────────────────────────────────────────────────────
// High-res JPEG conversion for receipt OCR — needs to stay readable, so much less
// aggressive compression than the small list/note photo thumbnails elsewhere.
function toLogoDataUrl(file, size=256, quality=0.82){
  // Square center-crop + shrink a photo into a small JPEG data URL for the
  // household logo. Kept tiny (~20-40KB) so it fits in the settings row and
  // syncs to the other phone over the existing realtime channel.
  return new Promise((resolve,reject)=>{
    const reader=new FileReader();
    reader.onload=(e)=>{
      const img=new Image();
      img.onload=()=>{
        const s=Math.min(img.width,img.height);
        const sx=(img.width-s)/2, sy=(img.height-s)/2;
        const canvas=document.createElement("canvas");
        canvas.width=size;canvas.height=size;
        canvas.getContext("2d").drawImage(img,sx,sy,s,s,0,0,size,size);
        resolve(canvas.toDataURL("image/jpeg",quality));
      };
      img.onerror=reject;
      img.src=e.target.result;
    };
    reader.onerror=reject;
    reader.readAsDataURL(file);
  });
}

function toReceiptJpeg(file, maxDim=1200, quality=0.80){
  return new Promise((resolve,reject)=>{
    const reader=new FileReader();
    reader.onload=(e)=>{
      const img=new Image();
      img.onload=()=>{
        let w=img.width,h=img.height;
        if(w>h&&w>maxDim){h=Math.round(h*(maxDim/w));w=maxDim;}
        else if(h>maxDim){w=Math.round(w*(maxDim/h));h=maxDim;}
        const canvas=document.createElement("canvas");
        canvas.width=w;canvas.height=h;
        canvas.getContext("2d").drawImage(img,0,0,w,h);
        // Strip the data URL prefix — the API wants raw base64
        resolve(canvas.toDataURL("image/jpeg",quality).split(",")[1]);
      };
      img.onerror=reject;
      img.src=e.target.result;
    };
    reader.onerror=reject;
    reader.readAsDataURL(file);
  });
}

function ReceiptScannerModal({categories,fxRates,displayCurrency,onAddExpenses,onClose}) {
  const [stage,setStage]=useState("capture"); // capture | scanning | review
  const [previewUrl,setPreviewUrl]=useState(null);
  const [error,setError]=useState("");
  const [debugInfo,setDebugInfo]=useState("");
  const [storeName,setStoreName]=useState("");
  const [retryable,setRetryable]=useState(false); // timeout/network/overload fail: same photo can be retried
  const lastFileRef=useRef(null);
  const [scanMode,setScanModeRaw]=useState(()=>{try{return localStorage.getItem("vestScanMode")||"simple";}catch(e){return"simple";}});
  const setScanMode=(m)=>{setScanModeRaw(m);try{localStorage.setItem("vestScanMode",m);}catch(e){}};
  const [simpleCat,setSimpleCat]=useState("");
  const [simpleAmt,setSimpleAmt]=useState("");
  const [receiptTotal,setReceiptTotal]=useState(null);
  const [items,setItems]=useState([]);
  const [tipPct,setTipPct]=useState(0); // % tip for this scan (reset per scan)
  const [scanDateISO,setScanDateISO]=useState(()=>localISO()); // filing date for scanned expenses (default today)
  // Receipt currency: scanned amounts are denominated in this, converted to
  // CAD (storage currency) at the live rate when added. Shared persisted
  // preference with the manual add form.
  const [entryCur,setEntryCurRaw]=useState(()=>{try{return localStorage.getItem("vestEntryCur")||displayCurrency||"CAD";}catch(e){return displayCurrency||"CAD";}});
  const setEntryCur=(c)=>{setEntryCurRaw(c);try{localStorage.setItem("vestEntryCur",c);}catch(e){}};
  const rateFor=(code)=>code==="CAD"?1:((fxRates&&fxRates[code])||FX_FALLBACK[code]||1);
  const fmtEntry=(v)=>new Intl.NumberFormat("en-US",{style:"currency",currency:entryCur,minimumFractionDigits:entryCur==="JPY"?0:2,maximumFractionDigits:entryCur==="JPY"?0:2}).format(v||0);
  const fileRef=useRef(null);
  const abortRef=useRef(null);
  const cancelRef=useRef(false);
  const previewRef=useRef(null);
  const setPreview=(url)=>{if(previewRef.current)URL.revokeObjectURL(previewRef.current);previewRef.current=url;setPreviewUrl(url);};
  useEffect(()=>()=>{if(previewRef.current)URL.revokeObjectURL(previewRef.current);if(abortRef.current)abortRef.current.abort();},[]);

  const scan=async(file)=>{
    if(!file)return;
    lastFileRef.current=file;
    setRetryable(false);
    setPreview(URL.createObjectURL(file));setStage("scanning");setError("");setDebugInfo("");
    abortRef.current=new AbortController();
    cancelRef.current=false;
    const timer=setTimeout(()=>abortRef.current.abort(),120000);
    try{
      let base64;
      try{base64=await toReceiptJpeg(file);}
      catch(imgErr){throw new Error(`Image could not be read — try selecting it from Camera Roll instead. (${imgErr.message})`);}

      const catList=categories.map(c=>`${c.id}: ${c.label}`).join(", ");
      let res;
      try{
        res=await fetch(SCAN_URL,{
          method:"POST",
          headers:{"Content-Type":"application/json"},
          body:JSON.stringify({image:base64,mediaType:"image/jpeg",categories:catList}),
          signal:abortRef.current.signal
        });
      }catch(fetchErr){
        if(cancelRef.current)throw new Error("__CANCELLED__");
        if(fetchErr.name==="AbortError")throw new Error("Scan timed out after 2 minutes — the scanner service is slow right now. Tap Retry to try the same photo again.");
        throw new Error("Could not connect to the scanner. Make sure the scanner function is deployed (Netlify: netlify/functions/gemini.js, Cloudflare Pages: functions/api/scan.js).");
      }finally{clearTimeout(timer);}

      const rawText=await res.text();
      let data;
      try{data=JSON.parse(rawText);}
      catch{
        if(res.status===429)throw new Error("Daily scan limit reached — free scanning resets tomorrow. To scan more, contact your admin about upgrading the plan.");
        if(res.status===404)throw new Error("Scanner function not found on server (404). Deploy the function for your host — netlify/functions/gemini.js on Netlify, functions/api/scan.js on Cloudflare Pages.");
        if(res.status===500)throw new Error("Server error — check that GEMINI_SCAN_KEY is set on your host (local .env, Netlify env vars, or a Cloudflare Pages secret).");
        // Local dev servers (npm start) don't serve server functions — the request falls
        // through to the app's own index.html. Detect that and explain the actual fix.
        const ctype=res.headers.get("content-type")||"";
        if(ctype.includes("text/html"))throw new Error("The scanner's server function isn't running here. Locally, run the app with 'netlify dev' (port 8888), not 'npm start'.");
        throw new Error(`Server returned unexpected response (status ${res.status}). Check the scanner function deployment.`);
      }
      if(data.error){
        if(data.error.code==="QUOTA_EXCEEDED"||res.status===429){
          setDebugInfo("Scan quota: Gemini daily limit reached");
          throw new Error("Daily scan limit reached — free scanning resets tomorrow. To scan more, contact your admin about upgrading the plan.");
        }
        // Model-overload (503) is temporary and retryable even on old
        // function deploys, which pass Google's raw "high demand" message
        // through with no code — match the message too.
        if(data.error.code==="MODEL_OVERLOADED"||/high demand|overloaded|currently experiencing/i.test(data.error.message||"")){
          setDebugInfo("Scanner: Gemini model overloaded (503) — temporary");
          throw new Error("Google's scanner is busy right now — tap Retry to try the same photo again.");
        }
        setDebugInfo(`API error: ${data.error.message||JSON.stringify(data.error)}`);
        throw new Error(data.error.message||"API error — check your Gemini API key.");
      }

      // Gemini sometimes emits raw line breaks inside string values (invalid
      // JSON) and omits commas between } { — repair both before parsing.
      const text=repairScanJson((data.text||"").replace(/[\x00-\x1F]+/g," "));
      let parsed=null;
      const attempts=[
        ()=>{const s=text.replace(/```json\s*/gi,"").replace(/```\s*/g,"").trim();const i=s.indexOf("{"),j=s.lastIndexOf("}");if(i===-1||j===-1)throw new Error("x");return JSON.parse(s.slice(i,j+1));},
        ()=>{const i=text.indexOf("{"),j=text.lastIndexOf("}");if(i===-1||j===-1)throw new Error("x");return JSON.parse(text.slice(i,j+1));},
        ()=>JSON.parse(text.trim()),
      ];
      for(const attempt of attempts){try{parsed=attempt();break;}catch{}}
      if(!parsed){setDebugInfo(`AI response (${text.length} chars): ${text.slice(0,300)}`);throw new Error("The scan came back garbled — please try scanning again.");}
      if(!parsed.items?.length){setDebugInfo("AI found no items in response");throw new Error("No items found — make sure the receipt is fully visible and well-lit.");}

      setStoreName(parsed.store||"");
      setReceiptTotal(parsed.paid_total||null);
      setItems(parsed.items.map((it,i)=>({...it,amount:parseFloat(it.amount)||0,id:i,enabled:true})));
      // Simple-mode defaults: most common item category + the receipt total.
      const catCounts={};(parsed.items||[]).forEach(it=>{if(it.category)catCounts[it.category]=(catCounts[it.category]||0)+1;});
      const topCat=Object.entries(catCounts).sort((a,b)=>b[1]-a[1])[0];
      setSimpleCat(topCat?topCat[0]:((categories&&categories[0]&&categories[0].id)||""));
      setSimpleAmt(parsed.paid_total!=null?String(parsed.paid_total):"");
      setTipPct(0);
      setScanDateISO(localISO());
      setStage("review");
    }catch(e){
      if(e.message==="__CANCELLED__"){setStage("capture");setError("");}
      else{
        // Timeouts and connection failures are worth one-tap retrying with the
        // same photo — no retaking. Parse/quota errors are not retried.
        setRetryable(e.message.includes("timed out")||e.message.includes("Could not connect")||e.message.includes("scanner is busy"));
        setError(e.message);setStage("capture");
      }
    }
  };

  const updateItem=(id,field,val)=>setItems(p=>p.map(it=>it.id===id?{...it,[field]:val}:it));
  const removeItem=(id)=>setItems(p=>p.filter(it=>it.id!==id));
  const enabledItems=items.filter(it=>it.enabled);
  const enabledTotal=enabledItems.reduce((s,it)=>s+(parseFloat(it.amount)||0),0);
  // Tip: % applied to the amount being added. Detailed mode spreads it across
  // enabled items (last item absorbs rounding so the saved sum is exact);
  // simple mode applies it to the single total. Reset on every new scan.
  const tipRate=tipPct>0?tipPct/100:0;
  const detailedTip=Math.round(enabledTotal*tipRate*100)/100;
  const detailedTotal=Math.round((enabledTotal+detailedTip)*100)/100;
  const simpleBase=parseFloat(simpleAmt)||0;
  const simpleTip=Math.round(simpleBase*tipRate*100)/100;
  const simpleTotal=Math.round((simpleBase+simpleTip)*100)/100;

  const tipPicker=<div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap",alignItems:"center"}}>
    <span style={{fontSize:11,color:T.textMuted}}>💰 Tip:</span>
    {[0,15,18,20,25].map(p=><button key={p} type="button" onClick={()=>setTipPct(p)} style={{padding:"5px 10px",borderRadius:8,border:`1.5px solid ${tipPct===p?T.accent:T.border}`,background:tipPct===p?`${T.accent}14`:"transparent",fontSize:11,fontWeight:700,cursor:"pointer",color:T.text}}>{p===0?"None":p+"%"}</button>)}
    <input type="number" min="0" step="1" value={[0,15,18,20,25].includes(tipPct)?"":tipPct} onChange={e=>setTipPct(Math.max(0,parseFloat(e.target.value)||0))} placeholder="Custom" style={{...S.input,width:76,padding:"5px 8px",fontSize:11,marginBottom:0}}/>
    <span style={{fontSize:11,color:T.textMuted}}>%</span>
  </div>;

  const confirmAdd=()=>{
    // Filing date for scanned expenses (default today; backdate an old receipt
    // the same way as a manual add — created_at drives the month grouping).
    const sISO=(scanDateISO && !isNaN(new Date(scanDateISO+"T12:00:00").getTime())) ? scanDateISO : localISO();
    const sD=new Date(sISO+"T12:00:00");
    const sDateStr=sD.toLocaleDateString("en-US",{month:"short",day:"numeric"});
    const sCreated=sD.toISOString();
    // Simple mode: one expense — store name + receipt total.
    if(scanMode==="simple"){
      const catObj=categories.find(c=>c.id===simpleCat)||categories[categories.length-1];
      onAddExpenses([{
        description:storeName||"Receipt",
        amount:Math.round(((parseFloat(simpleAmt)||0)*(1+tipRate)/rateFor(entryCur))*100)/100,
        category:simpleCat||catObj.id,
        catObj,
        recurring:false,
        date:sDateStr,
        created_at:sCreated,
      }]);
      onClose();
      return;
    }
    // Spread the tip across enabled items; the last item absorbs rounding so
    // the saved sum exactly equals the displayed total with tip.
    let tippedAmts=null;
    if(tipRate>0){
      let acc=0;
      tippedAmts=enabledItems.map(it=>{const v=Math.round((parseFloat(it.amount)||0)*(1+tipRate)*100)/100;acc=Math.round((acc+v)*100)/100;return v;});
      const residual=Math.round((detailedTotal-acc)*100)/100;
      if(residual!==0&&tippedAmts.length)tippedAmts[tippedAmts.length-1]=Math.round((tippedAmts[tippedAmts.length-1]+residual)*100)/100;
    }
    const toAdd=enabledItems.map((it,idx)=>{
      const catObj=categories.find(c=>c.id===it.category)||categories[categories.length-1];
      return{
        description:storeName?`${it.description} (${storeName})`:it.description,
        amount:Math.round(((tippedAmts?tippedAmts[idx]:(parseFloat(it.amount)||0))/rateFor(entryCur))*100)/100,
        category:it.category,
        catObj,
        recurring:false,
        date:sDateStr,
        created_at:sCreated,
      };
    });
    onAddExpenses(toAdd);
    onClose();
  };

  return <div style={S.modal} onClick={e=>e.target===e.currentTarget&&stage!=="scanning"&&onClose()}>
    <div style={{...S.modalBox,maxHeight:"90dvh",overflowY:stage==="review"?"auto":"visible"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
        <div style={{fontFamily:FONT,fontSize:20}}>📸 Scan Receipt</div>
        {stage!=="scanning"&&<button onClick={onClose} style={{background:"none",border:"none",fontSize:26,cursor:"pointer",color:T.textMuted,lineHeight:1,padding:"0 4px"}}>×</button>}
      </div>
      {stage!=="scanning"&&<div style={{display:"flex",background:T.bgCardAlt,borderRadius:12,padding:4,marginBottom:16}}>
        {[["simple","🧾 Total only"],["detailed","📋 Line items"]].map(([v,label])=>(
          <button key={v} type="button" onClick={()=>setScanMode(v)} style={{flex:1,padding:"9px 0",borderRadius:9,border:"none",fontSize:13,fontWeight:700,cursor:"pointer",background:scanMode===v?T.accent:"transparent",color:scanMode===v?"#fff":T.textMuted}}>{label}</button>
        ))}
      </div>}

      {stage==="capture"&&<>
        <div style={{fontSize:13,color:T.textSub,marginBottom:16,lineHeight:1.6}}>Take a photo or choose from camera roll. Works with all iPhone formats including HEIC.</div>
        {error&&<div style={{background:T.redBg,border:`1px solid ${T.redBorder}`,borderRadius:12,padding:"11px 14px",marginBottom:16,fontSize:13,color:T.red,lineHeight:1.5}}>Could not read receipt: {error}{debugInfo&&<div style={{marginTop:6,fontSize:11,opacity:0.75,fontFamily:MONO}}>{debugInfo}</div>}{retryable&&lastFileRef.current&&<button onClick={()=>scan(lastFileRef.current)} style={{...S.btnOutline,marginTop:10,padding:"10px 16px",width:"auto",fontSize:13}}>↻ Retry same photo</button>}</div>}
        <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}} onChange={e=>{if(e.target.files?.[0])scan(e.target.files[0]);e.target.value="";}}/>
        <button onClick={()=>{fileRef.current.setAttribute("capture","environment");fileRef.current.click();}} style={{...S.btn,display:"flex",alignItems:"center",justifyContent:"center",gap:10,padding:"18px 24px",fontSize:16,marginBottom:12}}>📷 Take Photo</button>
        <button onClick={()=>{fileRef.current.removeAttribute("capture");fileRef.current.click();}} style={{...S.btnOutline,display:"flex",alignItems:"center",justifyContent:"center",gap:10,padding:"14px 24px",fontSize:14}}>🖼️ Choose from Camera Roll</button>
        <div style={{background:T.bgCardAlt,borderRadius:12,padding:"11px 14px",marginTop:16,fontSize:12,color:T.textMuted,lineHeight:1.6}}>💡 Works with grocery, restaurant & pharmacy receipts. Loyalty credits handled automatically.</div>
      </>}

      {stage==="scanning"&&<div style={{textAlign:"center",padding:"30px 0"}}>
        {previewUrl&&<img src={previewUrl} alt="receipt" style={{maxWidth:"100%",maxHeight:220,borderRadius:14,marginBottom:20,boxShadow:T.shadowMd}}/>}
        <div style={{fontSize:32,marginBottom:12}}>⏳</div>
        <div style={{fontFamily:FONT,fontSize:17,marginBottom:4}}>Reading your receipt…</div>
        <div style={{fontSize:12,color:T.textMuted}}>This usually takes 5–20 seconds — longer when the scanner is busy</div>
        <button onClick={()=>{cancelRef.current=true;abortRef.current?.abort();}} style={{...S.btnOutline,marginTop:18,padding:"10px 28px",width:"auto"}}>Cancel</button>
      </div>}

      {stage==="review"&&scanMode==="simple"&&<>
        <div style={{textAlign:"center",marginBottom:14}}>
          <div style={{fontSize:15,fontWeight:700}}>{storeName||"Receipt"}</div>
          <div style={{fontSize:12,color:T.textMuted,marginTop:4}}>One expense will be added</div>
        </div>
        <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap",alignItems:"center"}}>
          <span style={{fontSize:11,color:T.textMuted}}>Receipt in:</span>
          {CURRS.map(c=><button key={c.code} type="button" onClick={()=>setEntryCur(c.code)} style={{padding:"5px 9px",borderRadius:8,border:`1.5px solid ${entryCur===c.code?T.accent:T.border}`,background:entryCur===c.code?`${T.accent}14`:"transparent",fontSize:11,fontWeight:700,cursor:"pointer",color:T.text}}>{c.flag} {c.code}</button>)}
        </div>
        {tipPicker}
        <div style={{marginBottom:10}}><label style={S.label}>Date</label><input style={{...S.input,marginBottom:0}} type="date" value={scanDateISO} max={localISO()} onChange={e=>setScanDateISO(e.target.value)}/></div>
        <div style={{display:"flex",gap:8,marginBottom:16}}>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:T.textMuted,marginBottom:4}}>Amount ({entryCur})</div>
            <input type="number" step="0.01" value={simpleAmt} onChange={e=>setSimpleAmt(e.target.value)} style={{...S.input,marginBottom:0,fontFamily:MONO}}/>
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:T.textMuted,marginBottom:4}}>Category</div>
            <select value={simpleCat} onChange={e=>setSimpleCat(e.target.value)} style={{...S.select,marginBottom:0}}>
              {categories.map(c=><option key={c.id} value={c.id}>{iconGlyph(c.icon)} {c.label}</option>)}
            </select>
          </div>
        </div>
        {tipPct>0&&<div style={{fontSize:12,color:T.textMuted,marginBottom:12,textAlign:"center"}}>{fmtEntry(simpleBase)} + {tipPct}% tip {fmtEntry(simpleTip)} = <b style={{color:T.text}}>{fmtEntry(simpleTotal)}</b></div>}
        <div style={{display:"flex",gap:10}}>
          <button onClick={confirmAdd} style={{...S.btn}}>＋ Add Expense</button>
          <button onClick={()=>setStage("capture")} style={{...S.btnOutline,width:"auto",padding:"14px 18px"}}>Rescan</button>
        </div>
      </>}

      {stage==="review"&&scanMode==="detailed"&&<>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div>
            <div style={{fontSize:15,fontWeight:700,color:T.text}}>{storeName||"Receipt"}</div>
            {receiptTotal&&<div style={{fontSize:12,color:T.textMuted}}>Receipt total: {fmtEntry(receiptTotal)}</div>}
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:9,color:T.textMuted,textTransform:"uppercase",letterSpacing:1}}>Adding</div>
            <div style={{fontFamily:MONO,fontSize:18,fontWeight:700,color:T.accent}}>{fmtEntry(detailedTotal)}</div>
          </div>
        </div>
        {receiptTotal&&enabledItems.length>0&&Math.abs(enabledTotal-receiptTotal)>0.005&&
          <div style={{fontSize:11,color:"#B7791F",marginBottom:10,lineHeight:1.5}}>
            ⚠️ Items add up to {fmtEntry(Math.abs(receiptTotal-enabledTotal))} {enabledTotal<receiptTotal?"less":"more"} than the receipt total{enabledTotal<receiptTotal?" — an item may be missing from the list above":""}.
          </div>}
        {tipPicker}
        {tipPct>0&&<div style={{fontSize:11,color:T.textMuted,marginBottom:10}}>Items {fmtEntry(enabledTotal)} + {tipPct}% tip {fmtEntry(detailedTip)} = <b style={{color:T.text}}>{fmtEntry(detailedTotal)}</b></div>}
        <div style={{marginBottom:10}}><label style={S.label}>Date</label><input style={{...S.input,marginBottom:0}} type="date" value={scanDateISO} max={localISO()} onChange={e=>setScanDateISO(e.target.value)}/></div>
        <div style={{display:"flex",gap:6,marginBottom:10,flexWrap:"wrap",alignItems:"center"}}>
          <span style={{fontSize:11,color:T.textMuted}}>Receipt in:</span>
          {CURRS.map(c=><button key={c.code} type="button" onClick={()=>setEntryCur(c.code)} style={{padding:"5px 9px",borderRadius:8,border:`1.5px solid ${entryCur===c.code?T.accent:T.border}`,background:entryCur===c.code?`${T.accent}14`:"transparent",fontSize:11,fontWeight:700,cursor:"pointer",color:T.text}}>{c.flag} {c.code}</button>)}
        </div>
        <div style={{fontSize:11,color:T.textMuted,marginBottom:12}}>Uncheck items you don't want, edit descriptions/amounts/categories, or remove entirely.</div>
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
          {items.map(it=><div key={it.id} style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",background:it.enabled?T.bgCardAlt:"transparent",borderRadius:12,border:`1.5px solid ${it.enabled?T.border:T.border}`,opacity:it.enabled?1:0.5}}>
            <input type="checkbox" checked={it.enabled} onChange={e=>updateItem(it.id,"enabled",e.target.checked)} style={{accentColor:T.accent,width:18,height:18,flexShrink:0}}/>
            <div style={{flex:1,minWidth:0}}>
              <input value={it.description} onChange={e=>updateItem(it.id,"description",e.target.value)} style={{...S.input,padding:"6px 9px",fontSize:13,marginBottom:5}}/>
              <select value={it.category} onChange={e=>updateItem(it.id,"category",e.target.value)} style={{...S.select,padding:"5px 9px",fontSize:12}}>
                {categories.map(c=><option key={c.id} value={c.id}>{iconGlyph(c.icon)} {c.label}</option>)}
              </select>
            </div>
            <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0}}>
              <div style={{display:"flex",alignItems:"center",gap:2}}>
                <span style={{fontSize:12,color:T.textMuted}}>{entryCur==="CAD"||entryCur==="USD"?"$":entryCur+" "}</span>
                <input type="number" step="0.01" value={it.amount} onChange={e=>updateItem(it.id,"amount",e.target.value)} style={{...S.input,width:64,padding:"6px 6px",fontSize:13,textAlign:"right",fontFamily:MONO}}/>
              </div>
              <button onClick={()=>removeItem(it.id)} style={{background:"none",border:"none",fontSize:16,color:T.textMuted,cursor:"pointer",padding:0}}>×</button>
            </div>
          </div>)}
        </div>
        <div style={{display:"flex",gap:10}}>
          <button onClick={confirmAdd} disabled={enabledItems.length===0} style={{...S.btn,opacity:enabledItems.length===0?0.5:1}}>＋ Add {enabledItems.length} Expense{enabledItems.length!==1?"s":""}</button>
          <button onClick={()=>setStage("capture")} style={{...S.btnOutline,width:"auto",padding:"14px 18px"}}>Rescan</button>
        </div>
      </>}
    </div>
  </div>;
}


// Tolerant repair for Gemini's occasionally sloppy JSON: inserts a missing
// comma between } and { (e.g. `...9912"} {"description"...`). String-aware,
// so braces inside quoted values are never touched.
function repairScanJson(s){
  let out="",inStr=false,esc=false;
  for(let i=0;i<s.length;i++){
    const ch=s[i];
    if(inStr){
      out+=ch;
      if(esc)esc=false;
      else if(ch==="\\")esc=true;
      else if(ch==='"')inStr=false;
      continue;
    }
    if(ch==='"'){inStr=true;out+=ch;continue;}
    out+=ch;
    if(ch==="}"){
      let j=i+1;
      while(j<s.length&&s[j]===" ")j++;
      if(s[j]==="{")out+=",";
    }
  }
  return out;
}

function compressImage(file, maxDim=560, quality=0.6){
  return new Promise((resolve,reject)=>{
    const reader=new FileReader();
    reader.onload=(e)=>{
      const img=new Image();
      img.onload=()=>{
        let w=img.width,h=img.height;
        if(w>h&&w>maxDim){h=Math.round(h*(maxDim/w));w=maxDim;}
        else if(h>maxDim){w=Math.round(w*(maxDim/h));h=maxDim;}
        const canvas=document.createElement("canvas");
        canvas.width=w;canvas.height=h;
        canvas.getContext("2d").drawImage(img,0,0,w,h);
        resolve(canvas.toDataURL("image/jpeg",quality));
      };
      img.onerror=reject;
      img.src=e.target.result;
    };
    reader.onerror=reject;
    reader.readAsDataURL(file);
  });
}

function PhotoViewerModal({photo,label,onClose}){
  return <div style={{...S.modal,padding:16}} onClick={onClose}>
    <div style={{maxWidth:"100%",maxHeight:"90dvh",display:"flex",flexDirection:"column",alignItems:"center",gap:12}} onClick={e=>e.stopPropagation()}>
      <img src={photo} alt={label||"item"} style={{maxWidth:"100%",maxHeight:"75dvh",borderRadius:16,boxShadow:"0 12px 40px rgba(0,0,0,0.4)"}}/>
      {label&&<div style={{color:"#fff",fontSize:16,fontWeight:600,fontFamily:SANS,textAlign:"center"}}>{label}</div>}
      <button onClick={onClose} style={{background:"rgba(255,255,255,0.15)",border:"none",color:"#fff",borderRadius:12,padding:"10px 24px",cursor:"pointer",fontSize:14,fontWeight:600}}>Close</button>
    </div>
  </div>;
}

function ListTab({items,onSave,meta:metaRaw,onSaveMeta,onSaveAll,userEmail}){
  const [showAddForm,setShowAddForm]=useState(false);
  const [pendingGroupId,setPendingGroupId]=useState(null);
  const [taskType,setTaskType]=useState("");
  const [customType,setCustomType]=useState("");
  const [showCustomInput,setShowCustomInput]=useState(false);
  const [text,setText]=useState("");
  const [uploading,setUploading]=useState(false);
  const [viewPhoto,setViewPhoto]=useState(null);
  const [filterType,setFilterType]=useState("all");
  const [collapsedGroups,setCollapsedGroups]=useState({});
  const [editingId,setEditingId]=useState(null);
  const [editText,setEditText]=useState("");
  const [editNotes,setEditNotes]=useState("");
  const [quickAddGroup,setQuickAddGroup]=useState(null);
  const [quickAddText,setQuickAddText]=useState("");
  const [draggedId,setDraggedId]=useState(null);
  const [editingMeta,setEditingMeta]=useState(null);
  const [metaCost,setMetaCost]=useState("");
  const [metaNotes,setMetaNotes]=useState("");
  const [metaReceiptPreview,setMetaReceiptPreview]=useState(null);
  const [metaUploading,setMetaUploading]=useState(false);
  const receiptFileRef=useRef(null);
  const fileRef=useRef(null);

  const TASK_TYPES=["Grocery Shopping","Errands","Delivery","Returns","Appointments"];
  const TYPE_ICONS={"Grocery Shopping":"🛒",Errands:"🏃",Delivery:"📦",Returns:"↩️",Appointments:"📅",Other:"📌"};
  const iconFor=(t)=>TYPE_ICONS[t]||"🏷️";
  const activeType=taskType||"";

  // Open the "New Task" form — generate a fresh groupId immediately so this
  // becomes its own separate list, even if the same task TYPE gets picked again.
  const openAddForm=()=>{
    setPendingGroupId(Date.now().toString(36)+Math.random().toString(36).slice(2,6));
    setShowAddForm(true);
  };
  const closeAddForm=()=>{setShowAddForm(false);setTaskType("");setText("");setPendingGroupId(null);};

  const nextOrder=(groupId)=>{
    const inGroup=items.filter(i=>i.groupId===groupId);
    if(!inGroup.length)return 0;
    return Math.min(...inGroup.map(i=>i.order??0))-1;
  };

  const newItemObj=(itemText,type,groupId,photo)=>({
    id:Date.now().toString(36)+Math.random().toString(36).slice(2,6),
    text:itemText,type,groupId,photo:photo||null,done:false,
    order:nextOrder(groupId),
    addedBy:(userEmail||"").split("@")[0]||"you",
    createdAt:new Date().toISOString(),
  });

  const pickPhotos=async(e)=>{
    const files=Array.from(e.target.files||[]);
    if(!files.length||!activeType||!pendingGroupId)return;
    setUploading(true);
    try{
      const newItems=[];
      let base=nextOrder(pendingGroupId);
      for(const file of files){
        const compressed=await compressImage(file);
        newItems.push({...newItemObj(text.trim()||"Tap to name item",activeType,pendingGroupId,compressed),order:base--});
      }
      onSave([...newItems,...items]);
      closeAddForm();
    }catch{}
    setUploading(false);
    e.target.value="";
  };

  const addTextItem=()=>{
    if(!text.trim()||!activeType||!pendingGroupId)return;
    onSave([newItemObj(text.trim(),activeType,pendingGroupId,null),...items]);
    closeAddForm();
  };

  const confirmCustomType=()=>{
    if(customType.trim()){setTaskType(customType.trim());setShowCustomInput(false);setCustomType("");}
  };

  const toggleDone=(id)=>onSave(items.map(i=>i.id===id?{...i,done:!i.done,doneAt:!i.done?new Date().toISOString():null}:i));
  const removeItem=(id)=>onSave(items.filter(i=>i.id!==id));
  const startEdit=(item)=>{setEditingId(item.id);setEditText(item.text);setEditNotes(item.notes||"");};
  const saveEdit=()=>{
    if(editText.trim())onSave(items.map(i=>i.id===editingId?{...i,text:editText.trim(),notes:editNotes.trim()||null}:i));
    setEditingId(null);setEditText("");setEditNotes("");
  };
  const cancelEdit=()=>{setEditingId(null);setEditText("");setEditNotes("");};

  // ── Reordering — now scoped per groupId (per separate list), not per type ──
  const reorderWithinGroup=(groupId,orderedIds)=>{
    const updated=items.map(i=>{
      const idx=orderedIds.indexOf(i.id);
      return idx===-1?i:{...i,order:idx};
    });
    onSave(updated);
  };
  const moveItem=(groupId,id,direction)=>{
    const pendingIds=items.filter(i=>i.groupId===groupId&&!i.done).sort((a,b)=>(a.order??0)-(b.order??0)).map(i=>i.id);
    const idx=pendingIds.indexOf(id);
    const swapIdx=idx+direction;
    if(swapIdx<0||swapIdx>=pendingIds.length)return;
    [pendingIds[idx],pendingIds[swapIdx]]=[pendingIds[swapIdx],pendingIds[idx]];
    reorderWithinGroup(groupId,pendingIds);
  };
  const onDragStart=(id)=>setDraggedId(id);
  const onDragOverItem=(e)=>e.preventDefault();
  const onDropItem=(groupId,targetId)=>{
    if(!draggedId||draggedId===targetId){setDraggedId(null);return;}
    const pendingIds=items.filter(i=>i.groupId===groupId&&!i.done).sort((a,b)=>(a.order??0)-(b.order??0)).map(i=>i.id);
    const fromIdx=pendingIds.indexOf(draggedId);
    const toIdx=pendingIds.indexOf(targetId);
    if(fromIdx===-1||toIdx===-1){setDraggedId(null);return;}
    pendingIds.splice(fromIdx,1);
    pendingIds.splice(toIdx,0,draggedId);
    reorderWithinGroup(groupId,pendingIds);
    setDraggedId(null);
  };

  const quickAdd=(groupId,type,textVal,photo)=>{
    onSave([newItemObj(textVal,type,groupId,photo||null),...items]);
  };

  // Close a task group — marks every item in it as done in one tap.
  const closeTask=(groupId)=>{
    const now=new Date().toISOString();
    onSave(items.map(i=>i.groupId===groupId&&!i.done?{...i,done:true,doneAt:now}:i));
  };
  const reopenTask=(groupId)=>{
    onSave(items.map(i=>i.groupId===groupId&&i.done?{...i,done:false,doneAt:null}:i));
  };

  // Rename only affects items in THIS specific group — separate trips of the
  // same type stay independent, unlike the old type-based rename.
  const [renamingGroup,setRenamingGroup]=useState(null);
  const [renameValue,setRenameValue]=useState("");
  const startRenameGroup=(groupId,currentLabel)=>{setRenamingGroup(groupId);setRenameValue(currentLabel);};
  const saveRenameGroup=(groupId)=>{
    if(!renameValue.trim()){setRenamingGroup(null);return;}
    onSave(items.map(i=>i.groupId===groupId?{...i,type:renameValue.trim()}:i));
    setRenamingGroup(null);setRenameValue("");
  };
  const cancelRenameGroup=()=>{setRenamingGroup(null);setRenameValue("");};

  // ── Notes, receipt & cost per task group (keyed by groupId now) ─────────────
  const startMetaEdit=(groupId)=>{
    setEditingMeta(groupId);
    setMetaCost(meta[groupId]?.cost!=null?String(meta[groupId].cost):"");
    setMetaReceiptPreview(meta[groupId]?.receiptPhoto||null);
    setMetaNotes(meta[groupId]?.notes||"");
  };
  const pickReceipt=async(e)=>{
    const file=e.target.files?.[0];
    if(!file)return;
    setMetaUploading(true);
    try{const compressed=await compressImage(file,900,0.6);setMetaReceiptPreview(compressed);}catch{}
    setMetaUploading(false);
    e.target.value="";
  };
  const saveMeta=()=>{
    const newMeta={...meta,[editingMeta]:{cost:metaCost?parseFloat(metaCost):null,receiptPhoto:metaReceiptPreview||null,notes:metaNotes.trim()||null}};
    onSaveMeta(newMeta);
    setEditingMeta(null);setMetaCost("");setMetaReceiptPreview(null);setMetaNotes("");
  };
  const clearMeta=(groupId)=>{
    const newMeta={...meta};delete newMeta[groupId];
    onSaveMeta(newMeta);
    if(editingMeta===groupId){setEditingMeta(null);setMetaCost("");setMetaReceiptPreview(null);setMetaNotes("");}
  };
  const cancelMetaEdit=()=>{setEditingMeta(null);setMetaCost("");setMetaReceiptPreview(null);setMetaNotes("");};

  const allTypes=[...new Set(items.map(i=>i.type).filter(Boolean))];
  // Filter by type (browsing "all Grocery Shopping trips" still works), but
  // group/display by groupId — so multiple trips of the same type stay as
  // separate cards instead of merging into one.
  const filteredItems=filterType==="all"?items:items.filter(i=>i.type===filterType);
  const grouped={};
  filteredItems.forEach(i=>{
    const gid=i.groupId||`legacy-${i.type||"other"}`; // fallback for any pre-existing items without a groupId
    if(!grouped[gid])grouped[gid]={type:i.type||"Other",itemsArr:[]};
    grouped[gid].itemsArr.push(i);
  });
  Object.values(grouped).forEach(g=>{
    g.itemsArr.sort((a,b)=>{
      if(a.done!==b.done)return a.done?1:-1;
      return (a.order??0)-(b.order??0);
    });
  });
  // Old lists (created before separate-list support) have no groupId, so their saved
  // notes/receipts live under the type name. Alias them so nothing looks lost.
  const meta={...metaRaw};
  Object.keys(grouped).forEach(gid=>{if(gid.startsWith("legacy-")&&!meta[gid]&&metaRaw[grouped[gid].type])meta[gid]=metaRaw[grouped[gid].type];});
  // Newest list on top, based on when the list was first created (not when items were last added)
  const groupCreated=(gid)=>Math.min(...grouped[gid].itemsArr.map(i=>new Date(i.createdAt).getTime()||0));
  const groupOrder=Object.keys(grouped).sort((a,b)=>groupCreated(b)-groupCreated(a));

  // Collapsible lists: each list renders as a one-line summary (chevron + name
  // + date + open count); tap the header to expand/collapse. Collapsed default.
  const isCollapsed=(gid)=>collapsedGroups[gid]!==false;
  const toggleCollapse=(gid)=>setCollapsedGroups(c=>({...c,[gid]:c[gid]===false}));
  const groupDateLabel=(gid)=>{
    const ts=groupCreated(gid);
    if(!ts||!isFinite(ts))return"";
    const d=new Date(ts),now=new Date();
    return d.toLocaleDateString("en-US",d.getFullYear()===now.getFullYear()?{month:"short",day:"numeric"}:{month:"short",day:"numeric",year:"numeric"});
  };

  // Delete a whole list: removes its items AND its saved notes/receipt in one save
  // (single combined save so the two writes can't overwrite each other).
  const deleteGroup=(gid)=>{
    if(!window.confirm("Delete this whole list and everything in it?"))return;
    const gidOf=(i)=>i.groupId||`legacy-${i.type||"other"}`;
    const nm={...metaRaw};
    delete nm[gid];
    if(gid.startsWith("legacy-"))delete nm[grouped[gid]?.type];
    onSaveAll(items.filter(i=>gidOf(i)!==gid),nm);
  };

  const totalPending=items.filter(i=>!i.done).length;
  const totalDone=items.filter(i=>i.done).length;

  return <div className="tab-content">
    {viewPhoto&&<PhotoViewerModal photo={viewPhoto.photo} label={viewPhoto.text} onClose={()=>setViewPhoto(null)}/>}

    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
      <div style={{fontFamily:FONT,fontSize:22}}>📝 Tasks</div>
      <div style={{fontSize:12,color:T.textMuted,fontWeight:600}}>{totalPending} open · {totalDone} done</div>
    </div>

    {!showAddForm&&<button onClick={openAddForm} style={{...S.btn,marginBottom:16,display:"flex",alignItems:"center",justifyContent:"center",gap:8,fontSize:15}}>＋ New Task</button>}

    {showAddForm&&<div style={{...S.card,marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{fontSize:14,fontWeight:700,color:T.text}}>New Task</div>
        <button onClick={closeAddForm} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:T.textMuted,lineHeight:1}}>×</button>
      </div>

      <div style={{marginBottom:12}}>
        <div style={{fontSize:11,color:T.textMuted,fontWeight:600,marginBottom:6,textTransform:"uppercase",letterSpacing:1}}>What kind of task?</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
          {TASK_TYPES.map(t=><button key={t} onClick={()=>{setTaskType(t);setShowCustomInput(false);}} style={{padding:"7px 13px",borderRadius:16,border:`1.5px solid ${activeType===t?T.accent:T.border}`,background:activeType===t?T.accentLight:"transparent",cursor:"pointer",fontSize:12,fontWeight:activeType===t?700:500,color:activeType===t?T.accent:T.text,display:"flex",alignItems:"center",gap:5}}>
            <span>{iconFor(t)}</span>{t}
          </button>)}
          {allTypes.filter(t=>!TASK_TYPES.includes(t)).map(t=><button key={t} onClick={()=>{setTaskType(t);setShowCustomInput(false);}} style={{padding:"7px 13px",borderRadius:16,border:`1.5px solid ${activeType===t?T.accent:T.border}`,background:activeType===t?T.accentLight:"transparent",cursor:"pointer",fontSize:12,fontWeight:activeType===t?700:500,color:activeType===t?T.accent:T.text}}>{iconFor(t)} {t}</button>)}
          <button onClick={()=>setShowCustomInput(v=>!v)} style={{padding:"7px 13px",borderRadius:16,border:`1.5px dashed ${T.border}`,background:"transparent",cursor:"pointer",fontSize:12,color:T.textMuted}}>＋ Other</button>
        </div>
        {showCustomInput&&<div style={{display:"flex",gap:6,marginTop:8}}>
          <input style={{...S.input,flex:1,padding:"8px 12px",fontSize:13}} placeholder="Task type name…" value={customType} onChange={e=>setCustomType(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")confirmCustomType();}}/>
          <button onClick={confirmCustomType} style={{padding:"8px 14px",borderRadius:10,border:"none",background:T.accent,color:"#fff",cursor:"pointer",fontSize:13,fontWeight:700}}>Use</button>
        </div>}
      </div>

      {activeType&&<>
        <div style={{marginBottom:10}}>
          <input
            style={S.input}
            placeholder="What do you need to do?"
            value={text}
            onChange={e=>setText(e.target.value)}
            onKeyDown={e=>{if(e.key==="Enter")addTextItem();}}
          />
        </div>
        <div style={{display:"flex",gap:8}}>
          <input ref={fileRef} type="file" accept="image/*" multiple style={{display:"none"}} onChange={pickPhotos}/>
          <button onClick={()=>fileRef.current?.click()} disabled={uploading} style={{flex:1,borderRadius:12,border:`1.5px solid ${T.border}`,background:T.bgCardAlt,cursor:"pointer",fontSize:14,fontWeight:600,color:T.text,padding:"11px",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
            {uploading?"⏳ Adding…":"📷 Add Photo(s)"}
          </button>
          <button onClick={addTextItem} style={{flex:1,borderRadius:12,border:"none",background:T.accent,color:"#fff",cursor:"pointer",fontSize:14,fontWeight:700,padding:"11px"}}>＋ Add</button>
        </div>
      </>}
    </div>}

    {allTypes.length>1&&<div className="cat-pills" style={{marginBottom:16}}>
      <button onClick={()=>setFilterType("all")} style={{...S.pill(filterType==="all",T.accent),flexShrink:0}}>All</button>
      {allTypes.map(t=><button key={t} onClick={()=>setFilterType(t)} style={{...S.pill(filterType===t,T.accent),flexShrink:0}}>{iconFor(t)} {t}</button>)}
    </div>}

    {items.length===0&&!showAddForm&&<div style={{...S.card,textAlign:"center",padding:"40px 20px",color:T.textMuted}}>
      <div style={{fontSize:40,marginBottom:10}}>📝</div>
      <div style={{fontSize:14,fontWeight:600,marginBottom:4}}>No tasks yet</div>
      <div style={{fontSize:12}}>Tap "＋ New Task" above to get started</div>
    </div>}

    {groupOrder.map(gid=>{
      const g=grouped[gid];
      const t=g.type;
      const groupItems=g.itemsArr;
      const withPhotos=groupItems.filter(i=>i.photo);
      const pending=groupItems.filter(i=>!i.done).length;
      const allClosed=groupItems.length>0&&pending===0;
      const collapsed=isCollapsed(gid);
      const dateLabel=groupDateLabel(gid);
      return <div key={gid} style={{...S.card,marginBottom:12,padding:collapsed?"10px 14px":"16px",border:allClosed?`1.5px solid ${T.green}55`:undefined}}>
        <div onClick={()=>toggleCollapse(gid)} style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:collapsed?0:12,gap:8,cursor:"pointer"}}>
          <div style={{flex:1,minWidth:0}}>
            {renamingGroup===gid
              ?<div style={{display:"flex",gap:6}} onClick={(e)=>e.stopPropagation()}>
                  <input autoFocus style={{...S.input,flex:1,padding:"7px 9px",fontSize:13}} value={renameValue} onChange={e=>setRenameValue(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")saveRenameGroup(gid);if(e.key==="Escape")cancelRenameGroup();}}/>
                  <button onClick={()=>saveRenameGroup(gid)} style={{padding:"7px 12px",borderRadius:8,border:"none",background:T.accent,color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700}}>Save</button>
                  <button onClick={cancelRenameGroup} style={{padding:"7px 10px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",cursor:"pointer",fontSize:12,color:T.textMuted}}>Cancel</button>
                </div>
              :<div style={{display:"flex",alignItems:"center",gap:8,minWidth:0}}>
                  <span style={{fontSize:11,flexShrink:0,color:T.textMuted,transform:collapsed?"none":"rotate(90deg)",transition:"transform 0.15s"}}>▶</span>
                  <span style={{fontSize:18,flexShrink:0}}>{iconFor(t)}</span>
                  <span style={{fontFamily:FONT,fontSize:17,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t}</span>
                  {dateLabel&&<span style={{fontSize:11,color:T.textMuted,flexShrink:0,whiteSpace:"nowrap"}}>{dateLabel}</span>}
                  <button onClick={(e)=>{e.stopPropagation();startRenameGroup(gid,t);}} style={{background:"none",border:"none",fontSize:12,cursor:"pointer",color:T.textMuted,flexShrink:0,padding:"2px"}}>✏️</button>
                  {allClosed&&<span style={{fontSize:11,fontWeight:700,color:T.green,background:T.greenBg,borderRadius:10,padding:"3px 9px",flexShrink:0}}>✓ Closed</span>}
                </div>}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
            <button onClick={(e)=>{e.stopPropagation();deleteGroup(gid);}} title="Delete list" style={{background:"none",border:"none",fontSize:15,cursor:"pointer",color:T.textMuted,padding:"2px 4px"}}>🗑️</button>
            {pending>0&&<span style={{fontSize:11,fontWeight:700,color:T.accent,background:T.accentLight,borderRadius:10,padding:"3px 9px"}}>{pending} open</span>}
            {pending>0
              ?<button onClick={(e)=>{e.stopPropagation();closeTask(gid);}} style={{fontSize:11,fontWeight:700,color:"#fff",background:T.green,border:"none",borderRadius:10,padding:"5px 10px",cursor:"pointer"}}>✓ Close</button>
              :groupItems.length>0&&<button onClick={(e)=>{e.stopPropagation();reopenTask(gid);}} style={{fontSize:11,fontWeight:600,color:T.textMuted,background:"transparent",border:`1px solid ${T.border}`,borderRadius:10,padding:"5px 10px",cursor:"pointer"}}>↺ Reopen</button>}
          </div>
        </div>

        {!collapsed&&<>
        {withPhotos.length>0&&<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:10,marginBottom:16}}>
          {withPhotos.map(item=>
            <div key={item.id} onClick={()=>setViewPhoto(item)} style={{position:"relative",aspectRatio:"1",borderRadius:14,overflow:"hidden",cursor:"pointer",border:`1.5px solid ${T.border}`}}>
              <img src={item.photo} alt={item.text} style={{width:"100%",height:"100%",objectFit:"cover",opacity:item.done?0.45:1}}/>
              <div style={{position:"absolute",bottom:0,left:0,right:0,background:"linear-gradient(0deg,rgba(0,0,0,0.75),rgba(0,0,0,0))",padding:"18px 10px 8px"}}>
                <div style={{fontSize:13,fontWeight:700,color:"#fff",textDecoration:item.done?"line-through":"none",lineHeight:1.25,overflow:"hidden",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>{item.text}</div>
              </div>
              {item.done&&<div style={{position:"absolute",top:8,right:8,width:26,height:26,borderRadius:"50%",background:T.green,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,color:"#fff",boxShadow:"0 2px 6px rgba(0,0,0,0.3)"}}>✓</div>}
            </div>
          )}
        </div>}

        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {groupItems.map((item,idx)=>{
            if(editingId===item.id){
              return <div key={item.id} style={{padding:"10px",background:T.bgCardAlt,borderRadius:10,border:`1.5px solid ${T.accent}`}}>
                <input autoFocus style={{...S.input,padding:"8px 10px",fontSize:13,marginBottom:8}} value={editText} onChange={e=>setEditText(e.target.value)} placeholder="Task name"/>
                <textarea style={{...S.input,padding:"8px 10px",fontSize:13,marginBottom:8,minHeight:60,resize:"vertical",fontFamily:SANS}} value={editNotes} onChange={e=>setEditNotes(e.target.value)} placeholder="Notes (optional) — size, brand, instructions…"/>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={saveEdit} style={{flex:1,padding:"8px",borderRadius:8,border:"none",background:T.accent,color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700}}>Save</button>
                  <button onClick={cancelEdit} style={{flex:1,padding:"8px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",cursor:"pointer",fontSize:12,color:T.textMuted}}>Cancel</button>
                </div>
              </div>;
            }
            const firstPending=groupItems.findIndex(i=>!i.done)===idx;
            const lastPending=(()=>{const p=groupItems.filter(i=>!i.done);return p.length&&p[p.length-1].id===item.id;})();
            return <div
              key={item.id}
              draggable={!item.done}
              onDragStart={()=>onDragStart(item.id)}
              onDragOver={onDragOverItem}
              onDrop={()=>onDropItem(gid,item.id)}
              style={{display:"flex",alignItems:"center",gap:8,padding:"9px 10px",background:item.done?T.bgCardAlt:draggedId===item.id?T.accentLight:"transparent",borderRadius:10,opacity:draggedId===item.id?0.5:1,cursor:item.done?"default":"grab"}}
            >
              {!item.done&&<span style={{fontSize:14,color:T.textMuted,flexShrink:0,cursor:"grab"}}>⠿</span>}
              <button onClick={()=>toggleDone(item.id)} style={{width:24,height:24,borderRadius:7,border:item.done?"none":`2px solid ${T.border}`,background:item.done?T.green:"transparent",color:"#fff",cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13}}>{item.done?"✓":""}</button>
              {item.photo&&<img src={item.photo} alt="" onClick={()=>setViewPhoto(item)} style={{width:48,height:48,borderRadius:10,objectFit:"cover",flexShrink:0,cursor:"pointer",opacity:item.done?0.5:1,border:`1px solid ${T.border}`}}/>}
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:14,fontWeight:500,color:item.done?T.textMuted:T.text,textDecoration:item.done?"line-through":"none",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.text}</div>
                {item.notes&&<div style={{fontSize:11,color:T.textMuted,marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>📝 {item.notes}</div>}
              </div>
              {!item.done&&<div style={{display:"flex",flexDirection:"column",gap:1,flexShrink:0}}>
                <button onClick={()=>moveItem(gid,item.id,-1)} disabled={firstPending} style={{background:"none",border:"none",fontSize:11,cursor:firstPending?"default":"pointer",color:firstPending?T.border:T.textMuted,padding:"1px 4px",lineHeight:1}}>▲</button>
                <button onClick={()=>moveItem(gid,item.id,1)} disabled={lastPending} style={{background:"none",border:"none",fontSize:11,cursor:lastPending?"default":"pointer",color:lastPending?T.border:T.textMuted,padding:"1px 4px",lineHeight:1}}>▼</button>
              </div>}
              <button onClick={()=>startEdit(item)} style={{background:"none",border:"none",fontSize:14,cursor:"pointer",color:T.textMuted,flexShrink:0,padding:"2px 4px"}}>✏️</button>
              <button onClick={()=>removeItem(item.id)} style={{background:"none",border:"none",fontSize:17,cursor:"pointer",color:T.textMuted,flexShrink:0,padding:"2px 4px"}}>×</button>
            </div>;
          })}
        </div>

        <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${T.border}`}}>
          {editingMeta===gid
            ?<div style={{background:T.bgCardAlt,borderRadius:12,padding:"12px"}}>
                <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:8}}>📝 Notes, Receipt & Cost</div>
                <textarea style={{...S.input,padding:"8px 10px",fontSize:13,marginBottom:10,minHeight:56,resize:"vertical",fontFamily:SANS}} value={metaNotes} onChange={e=>setMetaNotes(e.target.value)} placeholder="Notes — store hours, who's picking up, special instructions…"/>
                {metaReceiptPreview&&<div style={{position:"relative",marginBottom:10,width:90}}>
                  <img src={metaReceiptPreview} alt="receipt" style={{width:90,height:90,objectFit:"cover",borderRadius:10,border:`1.5px solid ${T.border}`}}/>
                  <button onClick={()=>setMetaReceiptPreview(null)} style={{position:"absolute",top:-7,right:-7,width:22,height:22,borderRadius:"50%",background:T.red,color:"#fff",border:"2px solid #fff",cursor:"pointer",fontSize:12,lineHeight:1,display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
                </div>}
                <div style={{display:"flex",gap:8,marginBottom:10}}>
                  <div style={{flex:1,position:"relative"}}>
                    <span style={{position:"absolute",left:11,top:"50%",transform:"translateY(-50%)",fontSize:13,color:T.textMuted}}>$</span>
                    <input type="number" inputMode="decimal" style={{...S.input,padding:"8px 10px 8px 22px",fontSize:13}} placeholder="Total cost" value={metaCost} onChange={e=>setMetaCost(e.target.value)}/>
                  </div>
                  <input ref={receiptFileRef} type="file" accept="image/*" style={{display:"none"}} onChange={pickReceipt}/>
                  <button onClick={()=>receiptFileRef.current?.click()} disabled={metaUploading} style={{width:42,flexShrink:0,borderRadius:10,border:`1.5px solid ${T.border}`,background:T.bgCard,cursor:"pointer",fontSize:16}}>{metaUploading?"⏳":"🧾"}</button>
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={saveMeta} style={{flex:1,padding:"9px",borderRadius:8,border:"none",background:T.accent,color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700}}>Save</button>
                  <button onClick={cancelMetaEdit} style={{flex:1,padding:"9px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",cursor:"pointer",fontSize:12,color:T.textMuted}}>Cancel</button>
                </div>
              </div>
            :meta[gid]&&(meta[gid].cost!=null||meta[gid].receiptPhoto||meta[gid].notes)
              ?<div style={{background:T.greenBg,borderRadius:12,padding:"10px 12px"}}>
                  {meta[gid].notes&&<div style={{fontSize:12,color:T.text,marginBottom:meta[gid].cost!=null||meta[gid].receiptPhoto?8:0,lineHeight:1.4,whiteSpace:"pre-wrap"}}>📝 {meta[gid].notes}</div>}
                  {(meta[gid].cost!=null||meta[gid].receiptPhoto)&&<div style={{display:"flex",alignItems:"center",gap:10}}>
                    {meta[gid].receiptPhoto&&<img src={meta[gid].receiptPhoto} alt="receipt" onClick={()=>setViewPhoto({photo:meta[gid].receiptPhoto,text:`${t} receipt`})} style={{width:40,height:40,borderRadius:8,objectFit:"cover",cursor:"pointer",flexShrink:0,border:`1px solid ${T.border}`}}/>}
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:9,textTransform:"uppercase",letterSpacing:1,color:T.textMuted}}>Total spent</div>
                      <div style={{fontFamily:MONO,fontSize:16,fontWeight:700,color:T.green}}>{meta[gid].cost!=null?fmt(meta[gid].cost):"—"}</div>
                    </div>
                    <button onClick={()=>startMetaEdit(gid)} style={{background:"none",border:"none",fontSize:14,cursor:"pointer",color:T.textMuted,padding:"4px"}}>✏️</button>
                    <button onClick={()=>clearMeta(gid)} style={{background:"none",border:"none",fontSize:17,cursor:"pointer",color:T.textMuted,padding:"4px"}}>×</button>
                  </div>}
                  {!(meta[gid].cost!=null||meta[gid].receiptPhoto)&&<div style={{display:"flex",gap:8,marginTop:4}}>
                    <button onClick={()=>startMetaEdit(gid)} style={{background:"none",border:"none",fontSize:12,cursor:"pointer",color:T.green,fontWeight:600,padding:0}}>✏️ Edit</button>
                    <button onClick={()=>clearMeta(gid)} style={{background:"none",border:"none",fontSize:12,cursor:"pointer",color:T.textMuted,padding:0}}>Clear</button>
                  </div>}
                </div>
              :<button onClick={()=>startMetaEdit(gid)} style={{width:"100%",padding:"10px",borderRadius:10,border:`1.5px dashed ${T.border}`,background:"transparent",cursor:"pointer",fontSize:12,color:T.textMuted,fontWeight:600}}>📝 Add notes, receipt or cost</button>
          }
        </div>

        <div style={{display:"flex",gap:6,marginTop:12,paddingTop:12,borderTop:`1px solid ${T.border}`}}>
          <input
            style={{...S.input,flex:1,padding:"9px 12px",fontSize:13}}
            placeholder={`Add to ${t}…`}
            value={quickAddGroup===gid?quickAddText:""}
            onChange={e=>{setQuickAddGroup(gid);setQuickAddText(e.target.value);}}
            onKeyDown={e=>{if(e.key==="Enter"&&quickAddText.trim()){quickAdd(gid,t,quickAddText.trim());setQuickAddText("");}}}
          />
          <input type="file" accept="image/*" multiple style={{display:"none"}} id={`quickphoto-${gid}`} onChange={async(e)=>{
            const files=Array.from(e.target.files||[]);
            if(!files.length)return;
            const newItems=[];
            let base=nextOrder(gid);
            for(const file of files){
              const compressed=await compressImage(file);
              newItems.push({...newItemObj("Tap to name item",t,gid,compressed),order:base--});
            }
            onSave([...newItems,...items]);
            e.target.value="";
          }}/>
          <button onClick={()=>document.getElementById(`quickphoto-${gid}`)?.click()} style={{width:42,flexShrink:0,borderRadius:10,border:`1.5px solid ${T.border}`,background:T.bgCardAlt,cursor:"pointer",fontSize:16}}>📷</button>
          <button onClick={()=>{if(quickAddText.trim()){quickAdd(gid,t,quickAddText.trim());setQuickAddText("");}}} style={{width:42,flexShrink:0,borderRadius:10,border:"none",background:T.accent,color:"#fff",cursor:"pointer",fontSize:18,fontWeight:700}}>＋</button>
        </div>
        </>}
      </div>;
    })}
  </div>;
}

// ─── NOTES TAB (links, photos, comments for future reference) ─────────────────
function NotesTab({notes,onSave,userEmail}){
  const [showAddForm,setShowAddForm]=useState(false);
  const [text,setText]=useState("");
  const [link,setLink]=useState("");
  const [photos,setPhotos]=useState([]);
  const [uploading,setUploading]=useState(false);
  const [viewPhoto,setViewPhoto]=useState(null);
  const [search,setSearch]=useState("");
  const [editingId,setEditingId]=useState(null);
  const [editText,setEditText]=useState("");
  const [editLink,setEditLink]=useState("");
  const fileRef=useRef(null);

  const pickPhotos=async(e)=>{
    const files=Array.from(e.target.files||[]);
    if(!files.length)return;
    setUploading(true);
    try{
      const compressed=[];
      for(const file of files)compressed.push(await compressImage(file,700,0.6));
      setPhotos(p=>[...p,...compressed]);
    }catch{}
    setUploading(false);
    e.target.value="";
  };
  const removePreviewPhoto=(idx)=>setPhotos(p=>p.filter((_,i)=>i!==idx));

  const normalizeLink=(l)=>{
    const trimmed=l.trim();
    if(!trimmed)return"";
    return/^https?:\/\//i.test(trimmed)?trimmed:`https://${trimmed}`;
  };

  const addNote=()=>{
    if(!text.trim()&&!link.trim()&&photos.length===0)return;
    const newNote={
      id:Date.now().toString(36)+Math.random().toString(36).slice(2,6),
      text:text.trim(),
      link:normalizeLink(link),
      photos,
      addedBy:(userEmail||"").split("@")[0]||"you",
      createdAt:new Date().toISOString(),
    };
    onSave([newNote,...notes]);
    setText("");setLink("");setPhotos([]);setShowAddForm(false);
  };

  const removeNote=(id)=>onSave(notes.filter(n=>n.id!==id));
  const startEdit=(n)=>{setEditingId(n.id);setEditText(n.text||"");setEditLink(n.link||"");};
  const saveEdit=()=>{
    onSave(notes.map(n=>n.id===editingId?{...n,text:editText.trim(),link:normalizeLink(editLink)}:n));
    setEditingId(null);setEditText("");setEditLink("");
  };
  const cancelEdit=()=>{setEditingId(null);setEditText("");setEditLink("");};

  const filtered=search.trim()
    ?notes.filter(n=>(n.text||"").toLowerCase().includes(search.toLowerCase())||(n.link||"").toLowerCase().includes(search.toLowerCase()))
    :notes;

  return <div className="tab-content">
    {viewPhoto&&<PhotoViewerModal photo={viewPhoto} onClose={()=>setViewPhoto(null)}/>}

    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
      <div style={{fontFamily:FONT,fontSize:22}}>🗒️ Notes</div>
      <div style={{fontSize:12,color:T.textMuted,fontWeight:600}}>{notes.length} saved</div>
    </div>

    {!showAddForm&&<button onClick={()=>setShowAddForm(true)} style={{...S.btn,marginBottom:16,display:"flex",alignItems:"center",justifyContent:"center",gap:8,fontSize:15}}>＋ New Note</button>}

    {showAddForm&&<div style={{...S.card,marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{fontSize:14,fontWeight:700,color:T.text}}>New Note</div>
        <button onClick={()=>{setShowAddForm(false);setText("");setLink("");setPhotos([]);}} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:T.textMuted,lineHeight:1}}>×</button>
      </div>
      <textarea style={{...S.input,minHeight:70,resize:"vertical",fontFamily:SANS,marginBottom:10}} placeholder="Write a note, comment, or reminder…" value={text} onChange={e=>setText(e.target.value)}/>
      <input style={{...S.input,marginBottom:10}} placeholder="Link (optional) — paste a URL" value={link} onChange={e=>setLink(e.target.value)}/>
      {photos.length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:10}}>
        {photos.map((p,i)=><div key={i} style={{position:"relative",width:72,height:72}}>
          <img src={p} alt="" style={{width:72,height:72,objectFit:"cover",borderRadius:10,border:`1.5px solid ${T.border}`}}/>
          <button onClick={()=>removePreviewPhoto(i)} style={{position:"absolute",top:-7,right:-7,width:20,height:20,borderRadius:"50%",background:T.red,color:"#fff",border:"2px solid #fff",cursor:"pointer",fontSize:11,lineHeight:1,display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
        </div>)}
      </div>}
      <div style={{display:"flex",gap:8}}>
        <input ref={fileRef} type="file" accept="image/*" multiple style={{display:"none"}} onChange={pickPhotos}/>
        <button onClick={()=>fileRef.current?.click()} disabled={uploading} style={{flex:1,borderRadius:12,border:`1.5px solid ${T.border}`,background:T.bgCardAlt,cursor:"pointer",fontSize:14,fontWeight:600,color:T.text,padding:"11px",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
          {uploading?"⏳ Adding…":"📷 Add Photo(s)"}
        </button>
        <button onClick={addNote} style={{flex:1,borderRadius:12,border:"none",background:T.accent,color:"#fff",cursor:"pointer",fontSize:14,fontWeight:700,padding:"11px"}}>＋ Save Note</button>
      </div>
    </div>}

    {notes.length>0&&<div style={{position:"relative",marginBottom:16}}>
      <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",fontSize:14,color:T.textMuted,pointerEvents:"none"}}>🔍</span>
      <input style={{...S.input,paddingLeft:36}} placeholder="Search notes…" value={search} onChange={e=>setSearch(e.target.value)}/>
      {search&&<button onClick={()=>setSearch("")} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",fontSize:18,cursor:"pointer",color:T.textMuted,lineHeight:1}}>×</button>}
    </div>}

    {notes.length===0&&!showAddForm&&<div style={{...S.card,textAlign:"center",padding:"40px 20px",color:T.textMuted}}>
      <div style={{fontSize:40,marginBottom:10}}>🗒️</div>
      <div style={{fontSize:14,fontWeight:600,marginBottom:4}}>No notes yet</div>
      <div style={{fontSize:12}}>Save links, photos, or reminders here for later</div>
    </div>}

    {filtered.length===0&&notes.length>0&&<div style={{...S.card,textAlign:"center",padding:"30px 20px",color:T.textMuted,fontSize:13}}>No notes match "{search}"</div>}

    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      {filtered.map(n=>{
        if(editingId===n.id){
          return <div key={n.id} style={{...S.card,border:`1.5px solid ${T.accent}`}}>
            <textarea autoFocus style={{...S.input,minHeight:70,resize:"vertical",fontFamily:SANS,marginBottom:10}} value={editText} onChange={e=>setEditText(e.target.value)} placeholder="Note text"/>
            <input style={{...S.input,marginBottom:10}} value={editLink} onChange={e=>setEditLink(e.target.value)} placeholder="Link (optional)"/>
            <div style={{display:"flex",gap:8}}>
              <button onClick={saveEdit} style={{flex:1,padding:"9px",borderRadius:8,border:"none",background:T.accent,color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700}}>Save</button>
              <button onClick={cancelEdit} style={{flex:1,padding:"9px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",cursor:"pointer",fontSize:12,color:T.textMuted}}>Cancel</button>
            </div>
          </div>;
        }
        return <div key={n.id} style={S.card}>
          {n.photos?.length>0&&<div style={{display:"grid",gridTemplateColumns:n.photos.length===1?"1fr":"repeat(auto-fill,minmax(100px,1fr))",gap:8,marginBottom:n.text||n.link?12:8}}>
            {n.photos.map((p,i)=><img key={i} src={p} alt="" onClick={()=>setViewPhoto(p)} style={{width:"100%",aspectRatio:"1",objectFit:"cover",borderRadius:12,cursor:"pointer",border:`1px solid ${T.border}`}}/>)}
          </div>}
          {n.text&&<div style={{fontSize:14,color:T.text,lineHeight:1.5,whiteSpace:"pre-wrap",marginBottom:n.link?10:8}}>{n.text}</div>}
          {n.link&&<a href={n.link} target="_blank" rel="noopener noreferrer" style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:13,color:T.blue,background:T.blueBg,borderRadius:10,padding:"7px 12px",textDecoration:"none",fontWeight:600,marginBottom:8,wordBreak:"break-all"}}>
            🔗 {n.link.replace(/^https?:\/\//,"").slice(0,40)}{n.link.length>47?"…":""}
          </a>}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:6,paddingTop:8,borderTop:`1px solid ${T.border}`}}>
            <span style={{fontSize:11,color:T.textMuted}}>{n.addedBy} · {new Date(n.createdAt).toLocaleDateString("en-US",{month:"short",day:"numeric"})}</span>
            <div style={{display:"flex",gap:4}}>
              <button onClick={()=>startEdit(n)} style={{background:"none",border:"none",fontSize:14,cursor:"pointer",color:T.textMuted,padding:"4px"}}>✏️</button>
              <button onClick={()=>removeNote(n.id)} style={{background:"none",border:"none",fontSize:17,cursor:"pointer",color:T.textMuted,padding:"4px"}}>×</button>
            </div>
          </div>
        </div>;
      })}
    </div>
  </div>;
}


// ─── SPENDING TAB ─────────────────────────────────────────────────────────────
// ─── PRICE TRACKER TAB ──────────────────────────────────────────────────────────
function PriceTrackerTab({entries,onSave,userEmail}){
  const [showAddForm,setShowAddForm]=useState(false);
  const [itemName,setItemName]=useState("");
  const [price,setPrice]=useState("");
  const [store,setStore]=useState("");
  const [customStore,setCustomStore]=useState("");
  const [showCustomInput,setShowCustomInput]=useState(false);
  const [date,setDate]=useState(localISO());
  const [search,setSearch]=useState("");
  const [expandedItem,setExpandedItem]=useState(null);
  const [editingEntryId,setEditingEntryId]=useState(null);
  const [editName,setEditName]=useState("");
  const [renamingGroup,setRenamingGroup]=useState(null);
  const [renameValue,setRenameValue]=useState("");
  const [editPrice,setEditPrice]=useState("");
  const [editDate,setEditDate]=useState("");
  const [editStore,setEditStore]=useState("");

  const COMMON_STORES=["Costco","Walmart","Superstore","Save-On-Foods","Safeway","T&T"];
  const knownItemNames=[...new Set(entries.map(e=>e.itemName))].sort();
  const knownStores=[...new Set(entries.map(e=>e.store).filter(Boolean))];

  const addEntry=()=>{
    if(!itemName.trim()||!price)return;
    const newEntry={
      id:Date.now().toString(36)+Math.random().toString(36).slice(2,6),
      itemName:itemName.trim(),
      price:parseFloat(price),
      store:store||null,
      date,
      addedBy:(userEmail||"").split("@")[0]||"you",
      createdAt:new Date().toISOString(),
    };
    onSave([newEntry,...entries]);
    setPrice("");setStore("");setShowAddForm(false);
  };

  const confirmCustomStore=()=>{
    if(customStore.trim()){setStore(customStore.trim());setShowCustomInput(false);setCustomStore("");}
  };

  const removeEntry=(id)=>onSave(entries.filter(e=>e.id!==id));
  const startEditEntry=(e)=>{setEditingEntryId(e.id);setEditName(e.itemName);setEditPrice(String(e.price));setEditDate(e.date);setEditStore(e.store||"");};
  const saveEditEntry=()=>{
    if(!editName.trim())return;
    onSave(entries.map(e=>e.id===editingEntryId?{...e,itemName:editName.trim(),price:parseFloat(editPrice),date:editDate,store:editStore||null}:e));
    setEditingEntryId(null);setEditName("");setEditPrice("");setEditDate("");setEditStore("");
  };
  const cancelEditEntry=()=>{setEditingEntryId(null);setEditName("");setEditPrice("");setEditDate("");setEditStore("");};
  // Renaming at the group level updates every entry sharing this item name at once,
  // so the price history doesn't get split across an old and new group by accident.
  const startRenameGroup=(name)=>{setRenamingGroup(name);setRenameValue(name);};
  const saveRenameGroup=(oldName)=>{
    if(!renameValue.trim()){setRenamingGroup(null);return;}
    onSave(entries.map(e=>e.itemName.toLowerCase()===oldName.toLowerCase()?{...e,itemName:renameValue.trim()}:e));
    setRenamingGroup(null);setRenameValue("");
  };
  const cancelRenameGroup=()=>{setRenamingGroup(null);setRenameValue("");};

  // Group entries by item name (case-insensitive)
  const grouped={};
  entries.forEach(e=>{
    const key=e.itemName.toLowerCase();
    if(!grouped[key])grouped[key]={name:e.itemName,entries:[]};
    grouped[key].entries.push(e);
  });
  Object.values(grouped).forEach(g=>g.entries.sort((a,b)=>a.date.localeCompare(b.date)));

  const filteredGroups=Object.values(grouped)
    .filter(g=>!search.trim()||g.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a,b)=>{
      const aLatest=a.entries[a.entries.length-1]?.createdAt||"";
      const bLatest=b.entries[b.entries.length-1]?.createdAt||"";
      return bLatest.localeCompare(aLatest);
    });

  return <div className="tab-content">
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
      <div style={{fontFamily:FONT,fontSize:22}}>🏷️ Price Tracker</div>
      <div style={{fontSize:12,color:T.textMuted,fontWeight:600}}>{Object.keys(grouped).length} items</div>
    </div>

    {!showAddForm&&<button onClick={()=>setShowAddForm(true)} style={{...S.btn,marginBottom:16,display:"flex",alignItems:"center",justifyContent:"center",gap:8,fontSize:15}}>＋ Log a Price</button>}

    {showAddForm&&<div style={{...S.card,marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{fontSize:14,fontWeight:700,color:T.text}}>Log a Price</div>
        <button onClick={()=>{setShowAddForm(false);setItemName("");setPrice("");setStore("");}} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:T.textMuted,lineHeight:1}}>×</button>
      </div>

      <div style={{marginBottom:10}}>
        <input
          style={S.input}
          list="known-items"
          placeholder="Item name — e.g. 2% Milk 4L"
          value={itemName}
          onChange={e=>setItemName(e.target.value)}
        />
        <datalist id="known-items">{knownItemNames.map(n=><option key={n} value={n}/>)}</datalist>
      </div>

      <div style={{display:"flex",gap:8,marginBottom:10}}>
        <div style={{flex:1,position:"relative"}}>
          <span style={{position:"absolute",left:11,top:"50%",transform:"translateY(-50%)",fontSize:13,color:T.textMuted}}>$</span>
          <input type="number" inputMode="decimal" style={{...S.input,padding:"11px 12px 11px 22px"}} placeholder="Price paid" value={price} onChange={e=>setPrice(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")addEntry();}}/>
        </div>
        <input type="date" style={{...S.input,flex:1}} value={date} onChange={e=>setDate(e.target.value)}/>
      </div>

      <div style={{marginBottom:12}}>
        <div style={{fontSize:11,color:T.textMuted,fontWeight:600,marginBottom:6,textTransform:"uppercase",letterSpacing:1}}>Store (optional)</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
          {COMMON_STORES.map(s=><button key={s} onClick={()=>{setStore(s);setShowCustomInput(false);}} style={{padding:"6px 12px",borderRadius:16,border:`1.5px solid ${store===s?T.accent:T.border}`,background:store===s?T.accentLight:"transparent",cursor:"pointer",fontSize:12,fontWeight:store===s?700:500,color:store===s?T.accent:T.text}}>{s}</button>)}
          {knownStores.filter(s=>!COMMON_STORES.includes(s)).map(s=><button key={s} onClick={()=>{setStore(s);setShowCustomInput(false);}} style={{padding:"6px 12px",borderRadius:16,border:`1.5px solid ${store===s?T.accent:T.border}`,background:store===s?T.accentLight:"transparent",cursor:"pointer",fontSize:12,fontWeight:store===s?700:500,color:store===s?T.accent:T.text}}>{s}</button>)}
          <button onClick={()=>setShowCustomInput(v=>!v)} style={{padding:"6px 12px",borderRadius:16,border:`1.5px dashed ${T.border}`,background:"transparent",cursor:"pointer",fontSize:12,color:T.textMuted}}>＋ Other</button>
        </div>
        {showCustomInput&&<div style={{display:"flex",gap:6,marginTop:8}}>
          <input style={{...S.input,flex:1,padding:"8px 12px",fontSize:13}} placeholder="Store name…" value={customStore} onChange={e=>setCustomStore(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")confirmCustomStore();}}/>
          <button onClick={confirmCustomStore} style={{padding:"8px 14px",borderRadius:10,border:"none",background:T.accent,color:"#fff",cursor:"pointer",fontSize:13,fontWeight:700}}>Use</button>
        </div>}
      </div>

      <button onClick={addEntry} style={{...S.btn}}>＋ Save Price</button>
    </div>}

    {Object.keys(grouped).length>0&&<div style={{position:"relative",marginBottom:16}}>
      <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",fontSize:14,color:T.textMuted,pointerEvents:"none"}}>🔍</span>
      <input style={{...S.input,paddingLeft:36}} placeholder="Search items…" value={search} onChange={e=>setSearch(e.target.value)}/>
      {search&&<button onClick={()=>setSearch("")} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",fontSize:18,cursor:"pointer",color:T.textMuted,lineHeight:1}}>×</button>}
    </div>}

    {entries.length===0&&!showAddForm&&<div style={{...S.card,textAlign:"center",padding:"40px 20px",color:T.textMuted}}>
      <div style={{fontSize:40,marginBottom:10}}>🏷️</div>
      <div style={{fontSize:14,fontWeight:600,marginBottom:4}}>No prices logged yet</div>
      <div style={{fontSize:12}}>Log a price each time you buy something to start tracking trends</div>
    </div>}

    {filteredGroups.length===0&&entries.length>0&&<div style={{...S.card,textAlign:"center",padding:"30px 20px",color:T.textMuted,fontSize:13}}>No items match "{search}"</div>}

    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      {filteredGroups.map(g=>{
        const sorted=g.entries; // chronological ascending
        const latest=sorted[sorted.length-1];
        const prev=sorted.length>1?sorted[sorted.length-2]:null;
        const lowest=sorted.reduce((min,e)=>e.price<min.price?e:min,sorted[0]);
        const highest=sorted.reduce((max,e)=>e.price>max.price?e:max,sorted[0]);
        const diff=prev?latest.price-prev.price:0;
        const diffPct=prev&&prev.price?((diff/prev.price)*100):0;
        const isExpanded=expandedItem===g.name;
        const chartData=sorted.map(e=>({label:new Date(e.date+"T00:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"}),value:e.price}));

        return <div key={g.name} style={S.card}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
            <div style={{flex:1,minWidth:0,cursor:renamingGroup===g.name?"default":"pointer"}} onClick={()=>renamingGroup!==g.name&&setExpandedItem(isExpanded?null:g.name)}>
              {renamingGroup===g.name
                ?<div style={{display:"flex",gap:6,marginBottom:8}} onClick={e=>e.stopPropagation()}>
                    <input autoFocus style={{...S.input,flex:1,padding:"7px 9px",fontSize:13}} value={renameValue} onChange={e=>setRenameValue(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")saveRenameGroup(g.name);if(e.key==="Escape")cancelRenameGroup();}}/>
                    <button onClick={()=>saveRenameGroup(g.name)} style={{padding:"7px 12px",borderRadius:8,border:"none",background:T.accent,color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700}}>Save</button>
                    <button onClick={cancelRenameGroup} style={{padding:"7px 10px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",cursor:"pointer",fontSize:12,color:T.textMuted}}>Cancel</button>
                  </div>
                :<div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                    <div style={{fontSize:15,fontWeight:700,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{g.name}</div>
                    <button onClick={e=>{e.stopPropagation();startRenameGroup(g.name);}} style={{background:"none",border:"none",fontSize:12,cursor:"pointer",color:T.textMuted,flexShrink:0,padding:"2px"}}>✏️</button>
                  </div>}
              <div style={{display:"flex",alignItems:"baseline",gap:8,flexWrap:"wrap"}}>
                <span style={{fontFamily:MONO,fontSize:20,fontWeight:700,color:T.text}}>{fmt(latest.price)}</span>
                {prev&&diff!==0&&<span style={{fontSize:12,fontWeight:700,color:diff>0?T.red:T.green,display:"flex",alignItems:"center",gap:2}}>
                  {diff>0?"▲":"▼"} {fmt(Math.abs(diff))} ({Math.abs(diffPct).toFixed(0)}%)
                </span>}
                {prev&&diff===0&&<span style={{fontSize:12,color:T.textMuted}}>No change</span>}
              </div>
              <div style={{fontSize:11,color:T.textMuted,marginTop:3}}>
                Last bought {new Date(latest.date+"T00:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}{latest.store?` at ${latest.store}`:""} · {sorted.length} price{sorted.length!==1?"s":""} logged
              </div>
            </div>
            {renamingGroup!==g.name&&<span onClick={()=>setExpandedItem(isExpanded?null:g.name)} style={{fontSize:16,color:T.textMuted,flexShrink:0,marginLeft:8,cursor:"pointer"}}>{isExpanded?"▲":"▼"}</span>}
          </div>

          {isExpanded&&<div style={{marginTop:16,paddingTop:16,borderTop:`1px solid ${T.border}`}}>
            {/* Lowest / Highest badges */}
            <div style={{display:"flex",gap:8,marginBottom:16}}>
              <div style={{flex:1,background:T.greenBg,borderRadius:10,padding:"8px 10px",textAlign:"center"}}>
                <div style={{fontSize:9,color:T.textMuted,textTransform:"uppercase",letterSpacing:1,marginBottom:2}}>Lowest</div>
                <div style={{fontFamily:MONO,fontSize:14,fontWeight:700,color:T.green}}>{fmt(lowest.price)}</div>
                <div style={{fontSize:9,color:T.textMuted}}>{new Date(lowest.date+"T00:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}</div>
              </div>
              <div style={{flex:1,background:T.redBg,borderRadius:10,padding:"8px 10px",textAlign:"center"}}>
                <div style={{fontSize:9,color:T.textMuted,textTransform:"uppercase",letterSpacing:1,marginBottom:2}}>Highest</div>
                <div style={{fontFamily:MONO,fontSize:14,fontWeight:700,color:T.red}}>{fmt(highest.price)}</div>
                <div style={{fontSize:9,color:T.textMuted}}>{new Date(highest.date+"T00:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}</div>
              </div>
              <div style={{flex:1,background:T.bgCardAlt,borderRadius:10,padding:"8px 10px",textAlign:"center"}}>
                <div style={{fontSize:9,color:T.textMuted,textTransform:"uppercase",letterSpacing:1,marginBottom:2}}>Average</div>
                <div style={{fontFamily:MONO,fontSize:14,fontWeight:700,color:T.text}}>{fmt(sorted.reduce((s,e)=>s+e.price,0)/sorted.length)}</div>
              </div>
            </div>

            {/* Price history chart */}
            {sorted.length>1&&<div style={{marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:10}}>Price History</div>
              <BarChart data={chartData} color={T.accent}/>
            </div>}

            {/* Individual entries list */}
            <div style={{fontSize:12,fontWeight:700,color:T.text,marginBottom:8}}>All Entries</div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {[...sorted].reverse().map(e=>{
                if(editingEntryId===e.id){
                  return <div key={e.id} style={{padding:"10px",background:T.bgCardAlt,borderRadius:10,border:`1.5px solid ${T.accent}`}}>
                    <input style={{...S.input,padding:"7px 8px",fontSize:12,marginBottom:8}} placeholder="Item name" value={editName} onChange={e2=>setEditName(e2.target.value)}/>
                    <div style={{display:"flex",gap:8,marginBottom:8}}>
                      <div style={{flex:1,position:"relative"}}>
                        <span style={{position:"absolute",left:9,top:"50%",transform:"translateY(-50%)",fontSize:12,color:T.textMuted}}>$</span>
                        <input type="number" inputMode="decimal" style={{...S.input,padding:"7px 8px 7px 20px",fontSize:12}} value={editPrice} onChange={e2=>setEditPrice(e2.target.value)}/>
                      </div>
                      <input type="date" style={{...S.input,flex:1,padding:"7px 8px",fontSize:12}} value={editDate} onChange={e2=>setEditDate(e2.target.value)}/>
                    </div>
                    <input style={{...S.input,padding:"7px 8px",fontSize:12,marginBottom:8}} placeholder="Store (optional)" value={editStore} onChange={e2=>setEditStore(e2.target.value)}/>
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={saveEditEntry} style={{flex:1,padding:"7px",borderRadius:8,border:"none",background:T.accent,color:"#fff",cursor:"pointer",fontSize:11,fontWeight:700}}>Save</button>
                      <button onClick={cancelEditEntry} style={{flex:1,padding:"7px",borderRadius:8,border:`1px solid ${T.border}`,background:"transparent",cursor:"pointer",fontSize:11,color:T.textMuted}}>Cancel</button>
                    </div>
                  </div>;
                }
                return <div key={e.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 10px",background:T.bgCardAlt,borderRadius:8}}>
                  <div>
                    <span style={{fontFamily:MONO,fontSize:13,fontWeight:700,color:T.text}}>{fmt(e.price)}</span>
                    <span style={{fontSize:11,color:T.textMuted,marginLeft:8}}>{new Date(e.date+"T00:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}{e.store?` · ${e.store}`:""}</span>
                  </div>
                  <div style={{display:"flex",gap:4}}>
                    <button onClick={()=>startEditEntry(e)} style={{background:"none",border:"none",fontSize:13,cursor:"pointer",color:T.textMuted,padding:"3px"}}>✏️</button>
                    <button onClick={()=>removeEntry(e.id)} style={{background:"none",border:"none",fontSize:16,cursor:"pointer",color:T.textMuted,padding:"3px"}}>×</button>
                  </div>
                </div>;
              })}
            </div>
          </div>}
        </div>;
      })}
    </div>
  </div>;
}


// ─── SPENDING TAB ─────────────────────────────────────────────────────────────
function SpendingTab({expenses,categories,income,budgets,locks,privacyMode}){
  const [viewMode,setViewMode]=useState("month");
  const [selCats,setSelCats]=useState([]);
  const [selMonth,setSelMonth]=useState(nowMonthKey());
  const [selYear,setSelYear]=useState(String(new Date().getFullYear()));
  const allMonths=[...new Set(expenses.map(e=>expMonthKey(e)))].sort().reverse();
  const allYears=[...new Set(allMonths.map(mk=>mk.split("-")[0]))].sort().reverse();
  const viewExp=viewMode==="month"?expenses.filter(e=>expMonthKey(e)===selMonth):expenses.filter(e=>expMonthKey(e).startsWith(selYear));
  const toggleCat=(id)=>setSelCats(prev=>prev.includes(id)?prev.filter(x=>x!==id):[...prev,id]);
  const isAll=selCats.length===0;
  const filteredExp=isAll?viewExp:viewExp.filter(e=>selCats.includes(e.category));
  const catTotals={};viewExp.forEach(e=>{catTotals[e.category]=(catTotals[e.category]||0)+e.amount;});
  const sortedCats=Object.entries(catTotals).sort((a,b)=>b[1]-a[1]);
  const totalAll=viewExp.reduce((s,e)=>s+e.amount,0);
  const totalSel=filteredExp.reduce((s,e)=>s+e.amount,0);
  const maxCat=sortedCats[0]?.[1]||1;
  const yearMonths=selYear===String(new Date().getFullYear())?new Date().getMonth()+1:12;
  const mInc=viewMode==="month"?getMonthIncome(income,selMonth):Array.from({length:yearMonths},(_,k)=>getMonthIncome(income,`${selYear}-${String(k+1).padStart(2,"0")}`)).reduce((x,y)=>x+y,0);
  const isBL=locks?.breakdown;
  const chartMonths=viewMode==="year"?allMonths.filter(mk=>mk.startsWith(selYear)).sort():allMonths.slice(0,12).reverse();
  const chartData=chartMonths.map(mk=>({label:monthShort(mk),value:isAll?expenses.filter(e=>expMonthKey(e)===mk).reduce((s,e)=>s+e.amount,0):expenses.filter(e=>expMonthKey(e)===mk&&selCats.includes(e.category)).reduce((s,e)=>s+e.amount,0)}));

  return <div className="tab-content">
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,gap:8}}>
      <div style={{fontFamily:FONT,fontSize:22}}>📊 Spending</div>
      <div style={{display:"flex",background:T.navBg,borderRadius:10,padding:3,gap:2}}>
        {[["month","Monthly"],["year","Yearly"]].map(([m,label])=>
          <button key={m} onClick={()=>setViewMode(m)} style={{padding:"7px 16px",borderRadius:8,border:"none",cursor:"pointer",fontSize:13,fontWeight:600,background:viewMode===m?T.accent:"transparent",color:viewMode===m?"#fff":T.textMuted,transition:"all 0.15s"}}>{label}</button>
        )}
      </div>
    </div>
    <div style={{marginBottom:14}}>
      {viewMode==="month"
        ?<select style={{...S.select,width:"100%"}} value={selMonth} onChange={e=>setSelMonth(e.target.value)}>
            {(allMonths.length?allMonths:[nowMonthKey()]).map(mk=><option key={mk} value={mk}>{monthLabel(mk)}</option>)}
          </select>
        :<select style={{...S.select,width:"100%"}} value={selYear} onChange={e=>setSelYear(e.target.value)}>
            {(allYears.length?allYears:[String(new Date().getFullYear())]).map(y=><option key={y} value={y}>{y}</option>)}
          </select>}
    </div>
    <div style={{...S.card,background:"linear-gradient(135deg,#1C1915,#2E2318)",border:"none",marginBottom:14,padding:"20px 20px 16px"}}>
      <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:2,color:"rgba(255,255,255,0.4)",marginBottom:6,fontWeight:600}}>
        {isAll?"Total Spent":selCats.length===1?`${categories.find(c=>c.id===selCats[0])?.label||""} Spent`:`${selCats.length} Categories Combined`}
      </div>
      <div style={{display:"flex",alignItems:"flex-end",justifyContent:"space-between",marginBottom:8,gap:10}}>
        <div style={{fontFamily:MONO,fontSize:36,fontWeight:700,color:"#fff",lineHeight:1}}>{isBL?"••••••":fmt(totalSel)}</div>
        {!isBL&&!isAll&&totalAll>0&&<div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:2,paddingBottom:2}}>
          <div style={{fontFamily:MONO,fontSize:22,fontWeight:700,color:selCats.length===1?(categories.find(c=>c.id===selCats[0])?.color||T.accent):T.accent,lineHeight:1}}>{((totalSel/totalAll)*100).toFixed(1)}%</div>
          <div style={{fontSize:10,color:"rgba(255,255,255,0.35)"}}>of total</div>
        </div>}
        {!isBL&&isAll&&mInc>0&&<div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:2,paddingBottom:2}}>
          <div style={{fontFamily:MONO,fontSize:22,fontWeight:700,color:totalAll/mInc>0.9?T.red:T.accent,lineHeight:1}}>{((totalAll/mInc)*100).toFixed(1)}%</div>
          <div style={{fontSize:10,color:"rgba(255,255,255,0.35)"}}>of income</div>
        </div>}
      </div>
      {!isAll&&totalAll>0&&<div style={{height:4,background:"rgba(255,255,255,0.1)",borderRadius:2,overflow:"hidden",marginBottom:8}}>
        <div style={{height:"100%",width:`${Math.min((totalSel/totalAll)*100,100)}%`,background:T.accent,borderRadius:2}}/>
      </div>}
      <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
        {!isAll&&<div style={{fontSize:12,color:"rgba(255,255,255,0.4)"}}>of {fmt(totalAll)} total</div>}
        {filteredExp.length>0&&<div style={{fontSize:12,color:"rgba(255,255,255,0.35)",marginLeft:"auto"}}>{filteredExp.length} transactions</div>}
        {!isAll&&<button onClick={()=>setSelCats([])} style={{fontSize:11,color:"rgba(255,255,255,0.5)",background:"rgba(255,255,255,0.08)",border:"none",borderRadius:8,padding:"3px 8px",cursor:"pointer"}}>Clear ×</button>}
      </div>
      {!isAll&&selCats.length>1&&<div style={{display:"flex",gap:5,marginTop:10,flexWrap:"wrap"}}>
        {selCats.map(cid=>{const c=categories.find(x=>x.id===cid)||{icon:"📦",label:cid,color:"#888"};const amt=catTotals[cid]||0;const pct=totalAll>0?((amt/totalAll)*100).toFixed(1):0;return<div key={cid} style={{display:"flex",alignItems:"center",gap:5,padding:"4px 9px",borderRadius:10,background:"rgba(255,255,255,0.08)"}}>
          <span style={{fontSize:13}}><CatIcon icon={c.icon}/></span><span style={{fontSize:11,color:"rgba(255,255,255,0.7)",fontWeight:600}}>{isBL?"•••":fmt(amt)}</span><span style={{fontSize:10,color:c.color,fontWeight:700}}>{pct}%</span>
        </div>;})}
      </div>}
    </div>
    <div style={{...S.card,marginBottom:14}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <div style={{fontSize:13,fontWeight:700,color:T.text}}>{isAll?"All Categories":selCats.length===1?"1 selected":`${selCats.length} selected`}</div>
        {!isAll&&<button onClick={()=>setSelCats([])} style={{fontSize:12,color:T.accent,background:"none",border:"none",cursor:"pointer",fontWeight:700}}>Clear ×</button>}
      </div>
      <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
        {categories.map(c=>{const catId=c.id;const amt=catTotals[catId]||0;const sel=selCats.includes(catId);const pct=totalAll?((amt/totalAll)*100).toFixed(0):0;
          return<button key={catId} onClick={()=>toggleCat(catId)} style={{display:"flex",alignItems:"center",gap:6,padding:"8px 12px",borderRadius:20,border:`1.5px solid ${sel?c.color:T.border}`,background:sel?`${c.color}18`:"transparent",cursor:"pointer",transition:"all 0.15s",opacity:amt===0&&!sel?0.55:1}}>
            <span style={{fontSize:16,lineHeight:1}}><CatIcon icon={c.icon}/></span><span style={{fontSize:12,fontWeight:sel?700:500,color:sel?c.color:T.text,whiteSpace:"nowrap"}}>{c.label}</span>
            <span style={{fontSize:11,fontFamily:MONO,color:sel?c.color:T.textMuted}}>{isBL?"•••":fmt(amt)}</span>
            {amt>0&&<span style={{fontSize:10,color:sel?c.color:T.textMuted,background:sel?`${c.color}25`:T.bgCard,borderRadius:6,padding:"1px 5px",fontWeight:600}}>{pct}%</span>}
          </button>;
        })}
      </div>
    </div>
    {chartData.length>0&&<div style={{...S.card,marginBottom:14}}>
      <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>{viewMode==="year"?`Monthly — ${selYear}`:"Last 12 months"}</div>
      <BarChart data={chartData} color={isAll?T.accent:selCats.length===1?(categories.find(c=>c.id===selCats[0])?.color||T.accent):T.accent}/>
    </div>}
    <div style={{...S.card,marginBottom:14}}>
      <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>By Category</div>
      {sortedCats.length===0?<div style={{color:T.textMuted,fontSize:13,textAlign:"center",padding:"28px 0"}}>No expenses for this period</div>
        :sortedCats.map(([catId,amt])=>{const c=categories.find(x=>x.id===catId)||{icon:"📦",label:catId,color:"#888"};const sel=selCats.includes(catId)||isAll;const pct=totalAll?((amt/totalAll)*100).toFixed(1):0;const bud=budgets[catId];const over=bud&&amt>bud;
          return<div key={catId} onClick={()=>toggleCat(catId)} style={{marginBottom:14,cursor:"pointer",opacity:(!isAll&&!selCats.includes(catId))?0.4:1,transition:"opacity 0.15s"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={S.tag(sel?c.color:"#aaa")}><CatIcon icon={c.icon}/></span><span style={{fontSize:14,fontWeight:600,color:T.text}}>{c.label}</span>
                {viewMode==="month"&&bud&&<span style={{fontSize:11,color:over?T.red:T.green,fontWeight:600}}>{over?"⚠️ Over":"✓ Under"}</span>}
              </div>
              <div style={{textAlign:"right",display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:11,color:T.textMuted}}>{pct}%</span><span style={{fontFamily:MONO,fontSize:15,fontWeight:700,color:c.color}}>{isBL?"••••":fmt(amt)}</span>
              </div>
            </div>
            <div style={{height:7,background:T.bgCardAlt,borderRadius:4,overflow:"hidden"}}>
              <div style={{height:"100%",width:`${(amt/maxCat)*100}%`,background:c.color,borderRadius:4,transition:"width 0.4s"}}/>
            </div>
            {viewMode==="month"&&bud>0&&<div style={{display:"flex",alignItems:"center",gap:6,marginTop:3}}>
              <div style={{flex:1,height:3,background:T.bgCardAlt,borderRadius:2,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${Math.min((amt/bud)*100,100)}%`,background:over?T.red:T.green,borderRadius:2}}/>
              </div>
              <span style={{fontSize:10,color:T.textMuted}}>{fmt(amt)}/{fmt(bud)}</span>
            </div>}
          </div>;
        })}
      {!isAll&&<div style={{paddingTop:12,borderTop:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span style={{fontSize:13,fontWeight:700,color:T.text}}>Selected Total</span><span style={{fontFamily:MONO,fontSize:16,fontWeight:700,color:T.accent}}>{isBL?"••••":fmt(totalSel)}</span>
      </div>}
    </div>
    {filteredExp.length>0&&<div style={{...S.card,marginBottom:16}}>
      <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>Transactions</div>
      {[...filteredExp].sort((a,b)=>new Date(b.created_at)-new Date(a.created_at)).map((e,i,arr)=>{const c=catForExpense(e,categories);
        return<div key={e.id||i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 0",borderBottom:i<arr.length-1?`1px solid ${T.border}`:"none"}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={S.tag(c.color)}><CatIcon icon={c.icon}/></span>
            <div><div style={{fontSize:14,fontWeight:600,color:T.text}}>{e.description}</div><div style={{fontSize:11,color:T.textMuted}}>{e.date}</div></div>
          </div>
          <span style={{fontFamily:MONO,fontSize:15,fontWeight:700,color:T.accent}}>{isBL?"••••":fmt(e.amount)}</span>
        </div>;
      })}
      <div style={{display:"flex",justifyContent:"space-between",paddingTop:12,borderTop:`1px solid ${T.border}`,marginTop:4}}>
        <span style={{fontSize:12,color:T.textMuted,fontWeight:600}}>{filteredExp.length} transactions</span><span style={{fontFamily:MONO,fontSize:15,fontWeight:700,color:T.accent}}>{isBL?"••••":fmt(totalSel)}</span>
      </div>
    </div>}
    {viewMode==="year"&&allYears.length>1&&<div style={{...S.card,marginBottom:16}}>
      <div style={{fontSize:13,fontWeight:700,color:T.text,marginBottom:12}}>Year over Year</div>
      {allYears.map(y=>{const yTotal=expenses.filter(e=>expMonthKey(e).startsWith(y)&&(isAll||selCats.includes(e.category))).reduce((s,e)=>s+e.amount,0);const yMax=Math.max(...allYears.map(yr=>expenses.filter(e=>expMonthKey(e).startsWith(yr)&&(isAll||selCats.includes(e.category))).reduce((s,e)=>s+e.amount,0)),1);
        return<div key={y} style={{marginBottom:12,cursor:"pointer"}} onClick={()=>setSelYear(y)}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
            <span style={{fontSize:14,fontWeight:y===selYear?700:400,color:y===selYear?T.accent:T.text}}>{y}{y===selYear?" ←":""}</span><span style={{fontFamily:MONO,fontSize:14,fontWeight:700,color:T.text}}>{isBL?"••••":fmt(yTotal)}</span>
          </div>
          <div style={{height:7,background:T.bgCardAlt,borderRadius:4,overflow:"hidden"}}>
            <div style={{height:"100%",width:`${(yTotal/yMax)*100}%`,background:y===selYear?T.accent:T.textMuted,borderRadius:4,opacity:y===selYear?1:0.5}}/>
          </div>
        </div>;
      })}
    </div>}
  </div>;
}


// ─── AUTH SCREEN ──────────────────────────────────────────────────────────────
function AuthScreen({onAuth}) {
  const [mode,setMode]=useState("login"); // "login" | "signup" | "reset"
  const [email,setEmail]=useState("");
  const [password,setPassword]=useState("");
  const [confirm,setConfirm]=useState("");
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  const [showPass,setShowPass]=useState(false);

  const submit=async()=>{
    setError("");setMessage("");
    if(!email.trim()||(!password&&mode!=="reset")){setError("Please fill in all fields.");return;}
    if(mode==="signup"&&password!==confirm){setError("Passwords don't match.");return;}
    if(mode==="signup"&&password.length<8){setError("Password must be at least 8 characters.");return;}
    setLoading(true);
    try{
      if(mode==="login"){
        const{data,error:e}=await supabase.auth.signInWithPassword({email:email.trim(),password});
        if(e)throw e;
        if(data.user&&!data.user.email_confirmed_at){
          setError("Please verify your email before signing in. Check your inbox for a confirmation link.");
          await supabase.auth.signOut();
        } else {
          onAuth(data.user);
        }
      } else if(mode==="signup"){
        const{data,error:e}=await supabase.auth.signUp({email:email.trim(),password});
        if(e)throw e;
        if(data.user&&!data.user.email_confirmed_at){
          setMode("verify");setPassword("");setConfirm("");
        } else if(data.user){
          onAuth(data.user);
        }
      } else if(mode==="reset"){
        const{error:e}=await supabase.auth.resetPasswordForEmail(email.trim(),{redirectTo:window.location.origin});
        if(e)throw e;
        setMessage("Password reset email sent! Check your inbox.");
        setMode("login");
      }
    }catch(e){
      setError(e.message||"Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return <>
    <style>{GLOBAL_CSS}</style>
    <link href="https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,700&family=JetBrains+Mono:wght@400;700;800&display=swap" rel="stylesheet"/>
    <div style={{minHeight:"100vh",background:T.bg,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"24px 16px",fontFamily:SANS}}>
      {/* Logo */}
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:32}}>
        <BearLogo size={52}/>
        <div><div style={{fontFamily:FONT,fontSize:22,color:T.accent}}>Mocha & Milk's</div><div style={{fontFamily:FONT,fontSize:13,color:T.textSub}}>Expenses 🐾</div></div>
      </div>

      <div style={{width:"100%",maxWidth:420,background:T.bgCard,borderRadius:24,padding:28,border:`1px solid ${T.border}`,boxShadow:T.shadowMd}}>
        {/* Tab switcher */}
        {mode!=="reset"&&<div style={{display:"flex",background:T.navBg,borderRadius:12,padding:4,marginBottom:24}}>
          {["login","signup"].map(m=><button key={m} onClick={()=>{setMode(m);setError("");setMessage("");}} style={{flex:1,padding:"11px",borderRadius:9,border:"none",cursor:"pointer",fontFamily:SANS,fontSize:14,fontWeight:700,background:mode===m?T.accent:"transparent",color:mode===m?"#fff":T.textSub,transition:"all 0.18s",textTransform:"capitalize"}}>{m==="login"?"Sign In":"Create Account"}</button>)}
        </div>}

        {mode==="reset"&&<div style={{marginBottom:20}}>
          <button onClick={()=>{setMode("login");setError("");setMessage("");}} style={{background:"none",border:"none",cursor:"pointer",color:T.textMuted,fontFamily:SANS,fontSize:14,display:"flex",alignItems:"center",gap:6,padding:0}}>← Back to sign in</button>
          <div style={{fontFamily:FONT,fontSize:22,marginTop:10}}>Reset Password</div>
          <div style={{fontSize:13,color:T.textMuted,marginTop:4}}>We'll send a reset link to your email</div>
        </div>}

        {error&&<div style={{background:T.redBg,border:`1px solid ${T.redBorder}`,borderRadius:12,padding:"12px 15px",color:T.red,fontSize:13,marginBottom:16}}>{error}</div>}
        {message&&<div style={{background:T.greenBg,border:`1px solid ${T.greenBorder}`,borderRadius:12,padding:"12px 15px",color:T.green,fontSize:13,marginBottom:16}}>{message}</div>}

        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div>
            <label style={S.label}>Email</label>
            <input
              style={S.input} type="email" placeholder="you@example.com"
              value={email} onChange={e=>setEmail(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&submit()}
              autoComplete="email" autoCapitalize="none"
            />
          </div>
          {mode!=="reset"&&<div>
            <label style={S.label}>Password</label>
            <div style={{position:"relative"}}>
              <input
                style={{...S.input,paddingRight:46}} type={showPass?"text":"password"}
                placeholder={mode==="signup"?"At least 8 characters":"••••••••"}
                value={password} onChange={e=>setPassword(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&submit()}
                autoComplete={mode==="login"?"current-password":"new-password"}
              />
              <button onClick={()=>setShowPass(!showPass)} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",fontSize:16,color:T.textMuted,padding:4}}>{showPass?"🙈":"👁"}</button>
            </div>
          </div>}
          {mode==="signup"&&<div>
            <label style={S.label}>Confirm Password</label>
            <input
              style={S.input} type={showPass?"text":"password"} placeholder="Repeat password"
              value={confirm} onChange={e=>setConfirm(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&submit()}
              autoComplete="new-password"
            />
          </div>}

          <button
            onClick={submit} disabled={loading}
            style={{...S.btn,marginTop:4,opacity:loading?0.7:1,fontSize:15}}
          >
            {loading?"⏳ Please wait…":mode==="login"?"Sign In →":mode==="signup"?"Create Account →":"Send Reset Email"}
          </button>

          {mode==="login"&&<button onClick={()=>{setMode("reset");setError("");setMessage("");}} style={{background:"none",border:"none",cursor:"pointer",color:T.textMuted,fontFamily:SANS,fontSize:13,textAlign:"center",padding:"4px 0"}}>
            Forgot your password?
          </button>}
        </div>
      </div>

      {mode==="verify"&&<div style={{width:"100%",maxWidth:420,background:T.bgCard,borderRadius:24,padding:28,border:`1px solid ${T.border}`,boxShadow:T.shadowMd,marginTop:16,textAlign:"center"}}>
        <div style={{fontSize:48,marginBottom:14}}>📬</div>
        <div style={{fontFamily:FONT,fontSize:22,marginBottom:8}}>Check your email</div>
        <div style={{fontSize:14,color:T.textMuted,lineHeight:1.7,marginBottom:20}}>We sent a confirmation link to <strong>{email}</strong>. Click it to activate your account, then come back and sign in.</div>
        <button onClick={()=>setMode("login")} style={S.btn}>Back to Sign In</button>
        <div style={{fontSize:12,color:T.textMuted,marginTop:12}}>Did not get it? Check your spam folder or <button onClick={async()=>{await supabase.auth.resend({type:"signup",email});}} style={{background:"none",border:"none",color:T.accent,cursor:"pointer",fontFamily:SANS,fontSize:12,fontWeight:700}}>resend the email</button>.</div>
      </div>}

      <div style={{marginTop:20,fontSize:11,color:T.textMuted,textAlign:"center",maxWidth:320,lineHeight:1.7}}>
        🔒 Your data is encrypted and private. Only you can access your account.<br/>
        By signing up you agree to our Terms and Privacy Policy.
      </div>
      <div style={{textAlign:"center",padding:"20px 0 8px",borderTop:"1px solid "+T.border,marginTop:24,display:"flex",alignItems:"center",justifyContent:"center",gap:16,flexWrap:"wrap"}}>
        <span style={{fontSize:11,color:T.textMuted}}>🐾 Mocha and Milk Expenses</span>
                      </div>
    </div>
  </>;
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [user,setUser]=useState(null);
  const [authChecked,setAuthChecked]=useState(false);
  const [householdId,setHouseholdId]=useState(null);
  // No idle auto-logout: this is a family app on personal phones, and the
  // 30-minute timer used to nuke half-filled forms. The Supabase session
  // persists until manual logout; privacy mode + PIN cover casual snooping.
  const [tab,setTab]=useState("dashboard");
  const [expenses,setExpenses]=useState([]);
  const [learnedKW,setLearnedKW]=useState({});
  const learnedKWRef=useRef({}); // mirror: avoids stale-closure races when learning
  const [categories,setCategories]=useState(()=>withLightning(DEFAULT_CATEGORIES));
  const [income,setIncome]=useState({monthly:{},default:0});
  const [currency,setCurrencyState]=useState("CAD");
  const [budgets,setBudgets]=useState({});
  const [listItems,setListItems]=useState([]);
  const [listMeta,setListMeta]=useState({});
  const [notes,setNotes]=useState([]);
  const [priceEntries,setPriceEntries]=useState([]);
  const [syncStatus,setSyncStatus]=useState("synced");
  const [loaded,setLoaded]=useState(false);
  const [locks,setLocks]=useState({});
  const [privacyMode,setPrivacyMode]=useState(false);
  const [pinHash,setPinHash]=useState(null);
  const [pinModal,setPinModal]=useState(null);
  const [householdName,setHouseholdName]=useState("Mocha & Milk's");
  const [householdLogo,setHouseholdLogo]=useState(""); // "" = default BearLogo
  const [showProfileModal,setShowProfileModal]=useState(false);
  // ── Recurring templates (one row per recurring bill; monthly instances are
  // generated from these so a skipped month can never kill the chain)
  const [templates,setTemplates]=useState([]);
  const [tplSupported,setTplSupported]=useState(true);
  // ── Toasts / haptics feedback
  const [toasts,setToasts]=useState([]);
  const toast=(msg,type)=>{const id=Date.now()+Math.random();setToasts(p=>[...p,{id,msg,type:type||"ok"}]);buzz(10);setTimeout(()=>setToasts(p=>p.filter(t=>t.id!==id)),3600);};
  // ── Offline queue: writes that failed are kept and retried
  const [pendingOps,setPendingOps]=useState(()=>{try{return JSON.parse(localStorage.getItem("pendingOps_v1")||"[]")}catch{return[]}});
  useEffect(()=>{try{localStorage.setItem("pendingOps_v1",JSON.stringify(pendingOps))}catch{}},[pendingOps]);
  // ── Display currency + FX
  const [fxRates,setFxRates]=useState(null);
  const [showCurrencyPicker,setShowCurrencyPicker]=useState(false);
  // ── Modals
  const [showIncomeBudgets,setShowIncomeBudgets]=useState(false);
  const [showRecurringMgr,setShowRecurringMgr]=useState(false);
  const [confirmDlg,setConfirmDlg]=useState(null); // {title,message,confirmLabel,danger,onConfirm}

  // ── Auth session listener ──
  useEffect(()=>{
    supabase.auth.getSession().then(({data:{session}})=>{
      setUser(session?.user||null);
      setAuthChecked(true);
    });
    const{data:{subscription}}=supabase.auth.onAuthStateChange((_,session)=>{
      setUser(session?.user||null);
      if(!session?.user){
        // Clear all data on logout
        setExpenses([]);setCategories(withLightning(DEFAULT_CATEGORIES));
        setIncome({monthly:{},default:0});setBudgets({});setLocks({});setLoaded(false);
        setHouseholdName("Mocha & Milk's");setHouseholdLogo("");
      }
    });
    return()=>subscription.unsubscribe();
  },[]);

  useEffect(()=>{
    if(!user)return;
    async function load(){
      setSyncStatus("syncing");
      try{
        // household_id scoping: look up whether this account is linked to a
        // household (via household_members). If a link exists, use it — this is
        // how existing users with real data are connected to their household_id.
        // If NO link exists, fall back to the user's own auth id, matching the
        // ||user?.id pattern used in every write throughout the app — this is
        // safe for brand-new users with nothing yet. Deliberately NOT auto-
        // creating a new household anymore: that was what caused data to appear
        // to vanish previously (it silently pointed the app at a fresh, empty
        // household instead of the real one).
        let hid=null;
        try{
          const{data:members,error:memErr}=await supabase.from("household_members")
            .select("household_id,role")
            .eq("user_id",user.id);
          if(memErr)console.warn("household_members query error:",memErr);
          const owned=(members||[]).find(m=>m.role==="owner");
          const member=owned||(members||[])[0]||null;
          if(member)hid=member.household_id;
        }catch(householdErr){console.warn("Household lookup error (non-fatal, falls back to user id):",householdErr);}
        setHouseholdId(hid);
        const uid=hid||user.id;
        const [{data:expRaw,error:expErr},{data:cats,error:catErr},{data:settings,error:setErr}]=await Promise.all([
          supabase.from("expenses").select("*").eq("household_id",uid).order("created_at",{ascending:false}),
          supabase.from("categories").select("*").or(`household_id.eq.${uid},household_id.eq.${user.id}`).order("created_at",{ascending:true}),
          supabase.from("settings").select("*").eq("household_id",uid),
        ]);
        if(expErr)console.warn("expenses query error:",expErr);
        if(catErr)console.warn("categories query error:",catErr);
        if(setErr)console.warn("settings query error:",setErr);
        let exp=expRaw;
        // Self-heal: identical recurring entries in the same month (left over from load races between
        // devices) -> keep the oldest, remove the rest.
        if(exp&&exp.length){
          const keepKeys=new Set(),dupIds=[];
          [...exp].sort((x,y)=>new Date(x.created_at)-new Date(y.created_at)).forEach(e=>{
            if(!e.recurring)return;
            const k=`${expMonthKey(e)}::${(e.description||"").trim().toLowerCase()}::${e.category}::${Number(e.amount).toFixed(2)}`;
            if(keepKeys.has(k))dupIds.push(e.id);else keepKeys.add(k);
          });
          if(dupIds.length){
            console.warn(`Removing ${dupIds.length} duplicate recurring expense(s)`);
            for(let i=0;i<dupIds.length;i+=50)await supabase.from("expenses").delete().in("id",dupIds.slice(i,i+50)).eq("household_id",uid);
            exp=exp.filter(e=>!dupIds.includes(e.id));
          }
        }
        // ── Recurring bills: template-driven when the migration has run ──
        // (recurring-templates-migration.sql). Otherwise the legacy
        // instance-copy rollover below runs instead.
        const _tpl=await ensureTemplates(uid,exp);
        if(_tpl.ok){
          const removedIds=await cleanupFutureInstances(uid);
          if(removedIds.size)exp=exp.filter(e=>!removedIds.has(e.id));
          await runTemplateRollover(uid,exp,_tpl.templates);
        }else{
        if(exp){
          const MSHORT=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
          const curMonth=new Date().getMonth();
          const curYear=new Date().getFullYear();
          const curMk=`${curYear}-${String(curMonth+1).padStart(2,"0")}`;
          // Auto-create next month's recurring expenses on the last day of current month
          // e.g. on May 31, create all June recurring expenses
          const today=new Date();
          const lastDayOfMonth=new Date(curYear,curMonth+1,0).getDate();
          const isLastDay=today.getDate()===lastDayOfMonth;

          // Next month values
          const nextMonth=curMonth===11?0:curMonth+1;
          const nextYear=curMonth===11?curYear+1:curYear;
          const nextMk=`${nextYear}-${String(nextMonth+1).padStart(2,"0")}`;

          // Lock key — one per month we're scheduling FOR (e.g. recurringDone_2026-06)
          const recurringKey=`recurringDone_${nextMk}_${uid}`;
          const{data:lockCheck}=await supabase.from("settings")
            .select("value").eq("key",recurringKey).eq("household_id",uid).limit(1);
          const alreadyScheduled=lockCheck&&lockCheck.length>0;

          // Also run for current month if it hasn't been done yet (first time setup)
          const curRecurringKey=`recurringDone_${curMk}_${uid}`;
          const{data:curLockCheck}=await supabase.from("settings")
            .select("value").eq("key",curRecurringKey).eq("household_id",uid).limit(1);
          const curMonthDone=curLockCheck&&curLockCheck.length>0;

          // Determine which month to schedule for
          const scheduleForMonth=!curMonthDone?curMonth:(isLastDay&&!alreadyScheduled?nextMonth:null);
          const scheduleForYear=!curMonthDone?curYear:(isLastDay&&!alreadyScheduled?nextYear:null);
          const scheduleKey=!curMonthDone?curRecurringKey:recurringKey;

          if(scheduleForMonth!==null){
            await supabase.from("settings").upsert([{key:scheduleKey,value:"done",household_id:uid}]);
            const prevMonth=curMonth===0?11:curMonth-1;
            const prevYear=curMonth===0?curYear-1:curYear;
            const prevMk=`${prevYear}-${String(prevMonth+1).padStart(2,"0")}`;
            const sourceMk=!curMonthDone?prevMk:curMk;
            const norm=(x)=>(x||"").trim().toLowerCase();
            // Every recurring item from the source month is carried forward (not collapsed by
            // name, so two different "Life Insurance" policies both carry forward).
            // The recurring flag is the single source of truth: it drives the 🔄 icon AND
            // the monthly auto-add, so the two can never disagree.
            const templates=exp.filter(e=>e.recurring&&expMonthKey(e)===sourceMk);
            const{data:targetMonthDB}=await supabase.from("expenses")
              .select("description,category").eq("household_id",uid)
              .gte("created_at",new Date(scheduleForYear,scheduleForMonth,1).toISOString())
              .lte("created_at",new Date(scheduleForYear,scheduleForMonth+1,0,23,59,59).toISOString());
            // Count what already exists per name+category so we never double up (even if an amount was edited)
            const existing={};
            (targetMonthDB||[]).forEach(e=>{const k=norm(e.description)+"::"+e.category;existing[k]=(existing[k]||0)+1;});
            const missing=templates.filter(t=>{const k=norm(t.description)+"::"+t.category;if(existing[k]>0){existing[k]--;return false;}return true;});
            if(missing.length>0){
              const toInsert=missing.map(t=>{
                const origDay=parseInt((t.date||"").split(" ")[1])||1;
                const day=Math.min(origDay,new Date(scheduleForYear,scheduleForMonth+1,0).getDate());
                return {
                  description:t.description,amount:t.amount,category:t.category,
                  catObj:t.catObj,recurring:true,
                  date:`${MSHORT[scheduleForMonth]} ${day}`,
                  household_id:uid,
                  created_at:new Date(scheduleForYear,scheduleForMonth,day,12,0,0).toISOString()
                };
              });
              const{data:newRec}=await supabase.from("expenses").insert(toInsert).select();
              if(newRec&&!curMonthDone)setExpenses([...newRec,...exp]);
              else setExpenses(exp);
            } else {
              setExpenses(exp);
            }
          } else {
            setExpenses(exp);
          }
        }
        } // end legacy-rollover fallback
        if(cats&&cats.length>0){
          // Deduplicate by id
          const seen=new Set();
          const unique=cats.filter(c=>{if(seen.has(c.id))return false;seen.add(c.id);return true;});
          const finalCats=withLightning(unique);
          setCategories(finalCats);
          // Repair expenses orphaned by a deleted category id: the one-shot
          // utilities repair plus the rerun-able catObj repair below.
          const fixedOrphans=await repairOrphanUtilities(uid,exp,finalCats);
          const fixedCatObj=await repairOrphanCatObj(uid,exp,finalCats);
          const fixed=[...fixedOrphans,...fixedCatObj];
          if(fixed.length){
            const m={};fixed.forEach(f=>{m[f.id]=f;});
            setExpenses(p=>p.map(e=>m[e.id]?{...e,category:m[e.id].toId,catObj:m[e.id].catObj}:e));
            toast(`✓ ${fixed.length} expense${fixed.length===1?"":"s"} restored to their categories`);
          }
        } else if(cats&&cats.length===0&&uid){
          setCategories(withLightning(DEFAULT_CATEGORIES));
        }
        if(settings){
          const i=settings.find(s=>s.key==="income"),b=settings.find(s=>s.key==="budgets"),l=settings.find(s=>s.key==="locks"),p=settings.find(s=>s.key==="pinHash"),cur=settings.find(s=>s.key==="currency"),lkw=settings.find(s=>s.key==="learnedKW"),sl=settings.find(s=>s.key==="sharedList"),nt=settings.find(s=>s.key==="sharedNotes"),pt=settings.find(s=>s.key==="priceTracker"),hp=settings.find(s=>s.key==="householdProfile");
          if(pt){try{setPriceEntries(JSON.parse(pt.value));}catch{}}
          if(nt){try{setNotes(JSON.parse(nt.value));}catch{}}
          if(sl){try{
            const parsed=JSON.parse(sl.value);
            if(Array.isArray(parsed)){setListItems(parsed);setListMeta({});}
            else{setListItems(parsed.items||[]);setListMeta(parsed.meta||{});}
          }catch{}}
          if(i){try{const iv=JSON.parse(i.value);setIncome(iv.monthly?iv:{monthly:{},default:parseFloat(iv.total)||0});}catch{}}
          if(cur){setCurrencyState(cur.value);_currency=cur.value;}
          if(lkw){try{const parsed=JSON.parse(lkw.value);setLearnedKW(parsed);learnedKWRef.current=parsed;}catch{}}
          if(b)setBudgets(JSON.parse(b.value));
          if(l)setLocks(JSON.parse(l.value));
          if(p)setPinHash(p.value);
          if(hp){try{const hv=JSON.parse(hp.value);if(hv.name)setHouseholdName(hv.name);if(hv.logo!==undefined)setHouseholdLogo(hv.logo);}catch{}}
        }
        setSyncStatus("synced");
      }catch{setSyncStatus("error");}
      setLoaded(true);
      loadFxRates(); // display-currency rates (async, cached)
      // Retry any writes that were queued while offline
      setTimeout(()=>flushPendingOps(),1500);
    }
    load();
  },[user]);

  useEffect(()=>{
    if(!loaded)return;
    const subs=[
      supabase.channel("exp-ch").on("postgres_changes",{event:"*",schema:"public",table:"expenses"},p=>{
        const _r=p.new||p.old,_h=householdId||user?.id;
        if(_r&&_r.household_id&&_r.household_id!==_h)return; // ignore other households' rows
        if(p.eventType==="INSERT")setExpenses(prev=>[p.new,...prev.filter(e=>e.id!==p.new.id)]);
        if(p.eventType==="UPDATE")setExpenses(prev=>prev.map(e=>e.id===p.new.id?p.new:e));
        if(p.eventType==="DELETE")setExpenses(prev=>prev.filter(e=>e.id!==p.old.id));
      }).subscribe(),
      supabase.channel("cat-ch").on("postgres_changes",{event:"*",schema:"public",table:"categories"},p=>{
        const _r=p.new||p.old,_h=householdId||user?.id;
        if(_r&&_r.household_id&&_r.household_id!==_h&&_r.household_id!==user?.id)return; // ignore other households' rows
        if(p.eventType==="INSERT")setCategories(prev=>withLightning(prev.some(c=>c.id===p.new.id)?prev:[...prev,p.new]));
        if(p.eventType==="UPDATE")setCategories(prev=>withLightning(prev.map(c=>c.id===p.new.id?p.new:c)));
        if(p.eventType==="DELETE")setCategories(prev=>prev.filter(c=>c.id!==p.old.id));
      }).subscribe(),
      supabase.channel("tpl-ch").on("postgres_changes",{event:"*",schema:"public",table:"recurring_templates"},p=>{
        const _r=p.new||p.old,_h=householdId||user?.id;
        if(_r&&_r.household_id&&_r.household_id!==_h)return; // ignore other households' rows
        if(p.eventType==="INSERT")setTemplates(prev=>prev.some(t=>t.id===p.new.id)?prev:[...prev,p.new]);
        if(p.eventType==="UPDATE")setTemplates(prev=>prev.map(t=>t.id===p.new.id?p.new:t));
        if(p.eventType==="DELETE")setTemplates(prev=>prev.filter(t=>t.id!==p.old.id));
      }).subscribe(),
      supabase.channel("set-ch").on("postgres_changes",{event:"*",schema:"public",table:"settings"},p=>{
        const _r=p.new||p.old,_h=householdId||user?.id;
        if(_r&&_r.household_id&&_r.household_id!==_h)return; // ignore other households' rows
        if(p.new?.key==="income"){try{const iv=JSON.parse(p.new.value);setIncome(iv.monthly?iv:{monthly:{},default:parseFloat(iv.total)||0});}catch{}}
        if(p.new?.key==="budgets")setBudgets(JSON.parse(p.new.value));
        if(p.new?.key==="locks")setLocks(JSON.parse(p.new.value));
        if(p.new?.key==="pinHash")setPinHash(p.new.value);
        if(p.new?.key==="currency"){setCurrencyState(p.new.value);_currency=p.new.value;}
        if(p.new?.key==="learnedKW"){try{const parsed=JSON.parse(p.new.value);setLearnedKW(parsed);learnedKWRef.current=parsed;}catch{}}
        if(p.new?.key==="householdProfile"){try{const hv=JSON.parse(p.new.value);if(hv.name)setHouseholdName(hv.name);if(hv.logo!==undefined)setHouseholdLogo(hv.logo);}catch{}}
        if(p.new?.key==="sharedList"){try{
          const parsed=JSON.parse(p.new.value);
          if(Array.isArray(parsed)){setListItems(parsed);setListMeta({});}
          else{setListItems(parsed.items||[]);setListMeta(parsed.meta||{});}
        }catch{}}
        if(p.new?.key==="sharedNotes"){try{setNotes(JSON.parse(p.new.value));}catch{}}
        if(p.new?.key==="priceTracker"){try{setPriceEntries(JSON.parse(p.new.value));}catch{}}
      }).subscribe(),
    ];
    return()=>subs.forEach(s=>s.unsubscribe());
  },[loaded,householdId,user?.id]);

  const saveLocks=async(l)=>{setLocks(l);await supabase.from("settings").upsert([{key:"locks",value:JSON.stringify(l),household_id:householdId||user?.id}]);};
  const saveHouseholdProfile=async(name,logo)=>{setHouseholdName(name);setHouseholdLogo(logo);await supabase.from("settings").upsert([{key:"householdProfile",value:JSON.stringify({name,logo}),household_id:householdId||user?.id}]);};
  const saveListState=async(items,meta)=>{
    setListItems(items);setListMeta(meta);
    await supabase.from("settings").upsert([{key:"sharedList",value:JSON.stringify({items,meta}),household_id:householdId||user?.id}]);
  };
  const saveListItems=(items)=>saveListState(items,listMeta);
  const saveListMeta=(meta)=>saveListState(listItems,meta);
  const saveNotes=async(newNotes)=>{
    setNotes(newNotes);
    await supabase.from("settings").upsert([{key:"sharedNotes",value:JSON.stringify(newNotes),household_id:householdId||user?.id}]);
  };
  const savePriceEntries=async(newEntries)=>{
    setPriceEntries(newEntries);
    await supabase.from("settings").upsert([{key:"priceTracker",value:JSON.stringify(newEntries),household_id:householdId||user?.id}]);
  };
  const saveCurrency=async(c)=>{setCurrencyState(c);_currency=c;applyFxRate(c);await supabase.from("settings").upsert([{key:"currency",value:c,household_id:householdId||user?.id}]);};
  // ─── FX RATES (display only — amounts are stored in CAD) ──────────────────
  const applyFxRate=(code,rates)=>{
    const r=rates||fxRates;
    _fxRate=code==="CAD"?1:((r&&r[code])||FX_FALLBACK[code]||1);
  };
  const loadFxRates=async()=>{
    try{
      const cached=JSON.parse(localStorage.getItem("fxRatesCAD")||"null");
      if(cached&&Date.now()-cached.ts<24*3600*1000&&cached.rates){setFxRates(cached.rates);applyFxRate(_currency,cached.rates);return;}
    }catch{}
    try{
      const r=await fetch("https://open.er-api.com/v6/latest/CAD");
      const j=await r.json();
      if(j&&j.rates&&j.rates.USD){
        const rates={USD:j.rates.USD,JPY:j.rates.JPY,CNY:j.rates.CNY,TWD:j.rates.TWD};
        try{localStorage.setItem("fxRatesCAD",JSON.stringify({ts:Date.now(),rates}));}catch{}
        setFxRates(rates);applyFxRate(_currency,rates);return;
      }
    }catch(e){console.warn("fx fetch failed, using fallback rates");}
    setFxRates(FX_FALLBACK);applyFxRate(_currency,FX_FALLBACK);
  };
  // ─── OFFLINE QUEUE ────────────────────────────────────────────────────────
  // Failed writes stay visible (marked) and are retried — an offline add
  // never silently vanishes anymore.
  const enqueueOp=(op)=>{
    setPendingOps(p=>[...p,{...op,qid:"q"+Date.now()+Math.random().toString(36).slice(2)}]);
    setSyncStatus("error");
    toast("📡 Offline — saved here, will sync when reconnected","err");
  };
  const flushPendingOps=async()=>{
    if(!pendingOps.length)return;
    if(typeof navigator!=="undefined"&&!navigator.onLine){toast("Still offline — will retry automatically","err");return;}
    const hid=householdId||user?.id;
    setSyncStatus("syncing");
    const remaining=[];
    for(const op of pendingOps){
      try{
        if(op.kind==="add"){
          const{data,error}=await supabase.from("expenses").insert(op.payload).select().single();
          if(error)throw error;
          setExpenses(p=>p.map(e=>e.id===op.tempId?{...data,catObj:op.catObj,pendingSync:false}:e));
          if(op.tplData)linkTemplateForNewExpense(op.tplData,data.id,hid);
        }else if(op.kind==="edit"){
          const{error}=await supabase.from("expenses").update(op.payload).eq("id",op.id).eq("household_id",hid);
          if(error)throw error;
          setExpenses(p=>p.map(e=>e.id===op.id?{...e,pendingSync:false}:e));
        }else if(op.kind==="delete"){
          const{error}=await supabase.from("expenses").delete().eq("id",op.id).eq("household_id",hid);
          if(error)throw error;
        }else{throw new Error("unknown op");}
      }catch(e){remaining.push(op);}
    }
    setPendingOps(remaining);
    if(remaining.length){setSyncStatus("error");}
    else{setSyncStatus("synced");toast("✓ All caught up");}
  };
  useEffect(()=>{
    const h=()=>flushPendingOps();
    window.addEventListener("online",h);
    return()=>window.removeEventListener("online",h);
  });
  // ─── INCOME & BUDGETS ─────────────────────────────────────────────────────
  const saveIncomeBudgets=async(inc,bud)=>{
    const hid=householdId||user?.id;
    const iv={monthly:{},default:inc};
    setIncome(iv);setBudgets(bud);
    setSyncStatus("syncing");
    try{
      await supabase.from("settings").upsert([
        {key:"income",value:JSON.stringify(iv),household_id:hid},
        {key:"budgets",value:JSON.stringify(bud),household_id:hid},
      ]);
      setSyncStatus("synced");
      toast("✓ Income & budgets saved");
    }catch(e){setSyncStatus("error");toast("Couldn't save — try again","err");}
  };
  const handleToggleLock=(k)=>{if(!locks[k])saveLocks({...locks,[k]:true});else setPinModal({mode:"unlock",sectionKey:k});};
  const handleRequestUnlock=(k)=>{if(!pinHash)saveLocks({...locks,[k]:false});else setPinModal({mode:"unlock",sectionKey:k});};
  const handlePrivacyToggle=()=>{if(privacyMode){if(pinHash)setPinModal({mode:"unlock",sectionKey:null});else setPrivacyMode(false);}else setPrivacyMode(true);};
  const handleSetupPin=()=>setPinModal({mode:"setup",sectionKey:null});
  const handlePinSuccess=()=>{const{sectionKey}=pinModal;if(pinModal.mode==="setup")setPrivacyMode(true);else if(sectionKey===null)setPrivacyMode(false);else saveLocks({...locks,[sectionKey]:false});setPinModal(null);};
  const handleSetPin=async(h)=>{setPinHash(h);await supabase.from("settings").upsert([{key:"pinHash",value:h,household_id:householdId||user?.id}]);};

  const parsedExp=expenses.map(e=>{const co=typeof e.catObj==="string"?(()=>{try{return JSON.parse(e.catObj);}catch{return {};}})():e.catObj||{};return{...e,catObj:co,autoRecur:co.autoRecur||false};});

  // Batched category learning: one state update + one Supabase write per
  // correction (full phrase + each meaningful word). The ref mirror avoids the
  // stale-closure race where rapid per-word learns clobbered each other and the
  // learned correction silently failed to stick.
  const learnCorrection=async(desc,catId)=>{
    const full=(desc||"").toLowerCase().trim();
    if(!full||full.length<3||!catId)return;
    const words=[...new Set([full,...full.split(/\s+/).filter(w=>w.length>=3)])];
    const updated={...learnedKWRef.current};
    words.forEach(w=>{updated[w]=catId;});
    learnedKWRef.current=updated;
    setLearnedKW(updated);
    try{await supabase.from("settings").upsert([{key:"learnedKW",value:JSON.stringify(updated),household_id:householdId||user?.id}]);}
    catch(e){console.warn("learnCorrection save failed:",e);}
  };

  const addExpense=async(data)=>{
    setSyncStatus("syncing");
    // Backdating: the chosen date (default today) drives BOTH the label and
    // created_at, since month grouping follows created_at. Noon local avoids
    // UTC-day edge cases. Without an explicit created_at the DB would stamp
    // insert time and the expense would pop back to the current month.
    const dISO=(data.dateISO && !isNaN(new Date(data.dateISO+"T12:00:00").getTime())) ? data.dateISO : localISO();
    const dObj=new Date(dISO+"T12:00:00");
    const dateStr=dObj.toLocaleDateString("en-US",{month:"short",day:"numeric"});
    const createdISO=dObj.toISOString();
    const tempId="temp_"+Date.now();
    const tempRecord={id:tempId,description:data.description,amount:data.amount,category:data.category,catObj:data.catObj,recurring:data.recurring,date:dateStr,created_at:createdISO};
    setExpenses(p=>[tempRecord,...p]);
    const hid=householdId||user?.id;
    const record={description:data.description,amount:data.amount,category:data.category,catObj:JSON.stringify(data.catObj),recurring:data.recurring,date:dateStr,created_at:createdISO,household_id:hid};
    const fail=()=>{
      // Keep the entry visible but flagged — it syncs when reconnected
      setExpenses(p=>p.map(e=>e.id===tempId?{...e,pendingSync:true}:e));
      enqueueOp({kind:"add",payload:record,tempId,catObj:data.catObj,tplData:data.recurring?{...data,created_at:createdISO}:null});
    };
    try{
      if(typeof navigator!=="undefined"&&!navigator.onLine){fail();return;}
      const{data:ins,error}=await supabase.from("expenses").insert([record]).select().single();
      if(!error&&ins){
        // Replace temp with real DB record
        setExpenses(p=>p.map(e=>e.id===tempId?{...ins,catObj:data.catObj}:e));
        setSyncStatus("synced");
        toast("✓ Expense added");
        if(data.recurring)linkTemplateForNewExpense({...data,created_at:createdISO},ins.id,hid);
      }else{console.error("addExpense DB error:",error);fail();}
    }catch(err){console.error("addExpense exception:",err);fail();}
  };
  const addExpenses=async(items)=>{
    setSyncStatus("syncing");
    const now=new Date().toISOString();
    const tempRecords=items.map((d,i)=>({id:"temp_"+Date.now()+i,description:d.description,amount:d.amount,category:d.category,catObj:d.catObj,recurring:false,date:d.date,created_at:d.created_at||now}));
    const tempIds=tempRecords.map(r=>r.id);
    setExpenses(p=>[...tempRecords,...p]);
    const hid=householdId||user?.id;
    const records=items.map(d=>({description:d.description,amount:d.amount,category:d.category,catObj:JSON.stringify(d.catObj),recurring:false,date:d.date,...(d.created_at?{created_at:d.created_at}:{}),household_id:hid}));
    const fail=()=>{
      // Keep entries flagged — they sync when reconnected
      setExpenses(p=>p.map(e=>tempIds.includes(e.id)?{...e,pendingSync:true}:e));
      records.forEach((rec,i)=>enqueueOp({kind:"add",payload:rec,tempId:tempIds[i],catObj:items[i].catObj,tplData:null}));
    };
    try{
      if(typeof navigator!=="undefined"&&!navigator.onLine){fail();return;}
      const{data:ins,error}=await supabase.from("expenses").insert(records).select();
      if(!error&&ins){
        setExpenses(p=>{const without=p.filter(e=>!tempIds.includes(e.id));return[...ins.map((r,i)=>({...r,catObj:items[i].catObj})),...without];});
        setSyncStatus("synced");
        toast(`✓ ${ins.length} expense${ins.length===1?"":"s"} added`);
      }else{console.error("addExpenses DB error:",error);fail();}
    }catch(err){console.error("addExpenses exception:",err);fail();}
  };
  const editExpense=async(id,data)=>{
    const prev=expenses.find(e=>e.id===id);
    // Optimistic update first
    setExpenses(p=>p.map(e=>e.id===id?{...e,...data,catObj:data.catObj}:e));
    const u={description:data.description,amount:data.amount,category:data.category,catObj:JSON.stringify(data.catObj),recurring:data.recurring,date:data.date};
    // If date was changed, also update created_at so it sorts into the right month
    if(data.created_at)u.created_at=data.created_at;
    const offline=typeof navigator!=="undefined"&&!navigator.onLine;
    try{
      if(offline)throw new Error("offline");
      const{data:upd,error}=await supabase.from("expenses").update(u).eq("id",id).select().single();
      if(error)throw error;
      if(upd)setExpenses(p=>p.map(e=>e.id===id?{...upd,catObj:data.catObj}:e));
      setSyncStatus("synced");
      toast("✓ Expense updated");
    }catch(err){
      console.error("editExpense error:",err);
      if(offline){
        // Keep the optimistic values and queue the write for reconnect
        setExpenses(p=>p.map(e=>e.id===id?{...e,pendingSync:true}:e));
        enqueueOp({kind:"edit",id,payload:u});
      }else{
        // Real failure — roll back so the UI never shows unsaved values
        if(prev)setExpenses(p=>p.map(e=>e.id===id?prev:e));
        setSyncStatus("error");
        toast("Couldn't save — try again","err");
      }
    }
  };
  // Edit a whole recurring chain: applies the new description/amount/category
  // to this month and all FUTURE copies (matched by the ORIGINAL values, so a
  // rename still finds its chain). Each copy keeps its own date. Past months
  // are left alone.
  const editRecurringSeries=async(orig,data)=>{
    const hid=householdId||user?.id;
    const norm=(x)=>(x||"").trim().toLowerCase();
    const amt=(x)=>Number(x.amount).toFixed(2);
    const origMk=expMonthKey(orig);
    const chain=expenses.filter(e=>e.recurring&&expMonthKey(e)>=origMk&&norm(e.description)===norm(orig.description)&&e.category===orig.category&&amt(e)===amt(orig));
    const ids=chain.map(e=>e.id);
    if(!ids.length){editExpense(orig.id,data);return;}
    const u={description:data.description,amount:data.amount,category:data.category,catObj:JSON.stringify(data.catObj),recurring:data.recurring};
    setExpenses(p=>p.map(e=>ids.includes(e.id)?{...e,...u,catObj:data.catObj}:e));
    setSyncStatus("syncing");
    try{
      const{error}=await supabase.from("expenses").update(u).in("id",ids).eq("household_id",hid);
      if(error)console.error("editRecurringSeries error:",error);else setSyncStatus("synced");
    }catch(err){console.error("editRecurringSeries exception:",err);}
  };
  // ─── RECURRING TEMPLATES ──────────────────────────────────────────────
  // One row per recurring bill in `recurring_templates`. Monthly instances
  // are generated FROM these, so a month with zero app-opens can never
  // silently kill the chain, and editing any instance with "apply to future"
  // updates the template — future months follow until changed again.
  const tplNorm=(x)=>(x||"").trim().toLowerCase();
  const parseCatObj=(v)=>{try{return typeof v==="string"?JSON.parse(v):v;}catch{return null;}};

  // Load templates; one-time seed from legacy recurring expenses. Returns
  // {ok:false} when the migration SQL hasn't been run yet (app falls back
  // to the legacy instance-copy rollover).
  const ensureTemplates=async(uid,exp)=>{
    try{
      const{data,error}=await supabase.from("recurring_templates").select("*").eq("household_id",uid).order("description");
      if(error)throw error;
      let tpls=data||[];
      const{data:seedFlag}=await supabase.from("settings").select("value").eq("key","recTplSeeded").eq("household_id",uid).limit(1);
      if(!seedFlag?.length&&tpls.length===0){
        const rec=(exp||[]).filter(e=>e.recurring);
        const seen=new Map();
        [...rec].sort((a,b)=>new Date(b.created_at)-new Date(a.created_at)).forEach(e=>{
          const k=tplNorm(e.description)+"::"+e.category;
          if(!seen.has(k)){
            let day=parseInt((e.date||"").split(" ")[1])||new Date(e.created_at).getDate()||1;
            seen.set(k,{description:(e.description||"").trim(),amount:Number(e.amount)||0,category:e.category,cat_obj:parseCatObj(e.catObj),day_of_month:Math.min(Math.max(day,1),28),household_id:uid});
          }
        });
        if(seen.size){
          const{data:ins,error:insErr}=await supabase.from("recurring_templates").insert([...seen.values()]).select();
          if(!insErr&&ins){
            tpls=ins;
            // Backfill template_id on recent instances (JS-side matching, so
            // "Netflix" finds its template even with case/space differences)
            const since=new Date();since.setDate(since.getDate()-200);
            const{data:recent}=await supabase.from("expenses").select("id,description,category")
              .eq("household_id",uid).eq("recurring",true).gte("created_at",since.toISOString());
            const byKey={};ins.forEach(t=>{byKey[tplNorm(t.description)+"::"+t.category]=t.id;});
            const updates={};
            (recent||[]).forEach(e=>{const tid=byKey[tplNorm(e.description)+"::"+e.category];if(tid){(updates[tid]=updates[tid]||[]).push(e.id);}});
            for(const tid of Object.keys(updates)){
              await supabase.from("expenses").update({template_id:tid}).in("id",updates[tid]);
            }
          }
        }
        await supabase.from("settings").upsert([{key:"recTplSeeded",value:"1",household_id:uid}]);
      }
      setTemplates(tpls);
      return{ok:true,templates:tpls};
    }catch(e){
      console.warn("recurring_templates unavailable — run recurring-templates-migration.sql:",e.message);
      setTplSupported(false);
      return{ok:false,templates:[]};
    }
  };

  // Generate missing monthly instances from templates: `backfillN` months back
  // (catch-up — a skipped month can never kill the chain) through the CURRENT
  // month. Bills appear in their own month, never early. Idempotent via
  // per-template-month done-locks + skip-locks.
  const runTemplateRollover=async(uid,exp,tpls,backfillN=6)=>{
    const MSHORT=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const now=new Date();
    const mkOf=(y,m)=>`${y}-${String(m+1).padStart(2,"0")}`;
    const curMk=mkOf(now.getFullYear(),now.getMonth());
    const months=[];
    for(let back=backfillN;back>=0;back--){const d=new Date(now.getFullYear(),now.getMonth()-back,1);months.push({y:d.getFullYear(),m:d.getMonth(),mk:mkOf(d.getFullYear(),d.getMonth())});}
    const{data:existing}=await supabase.from("expenses").select("template_id,description,category,created_at").eq("household_id",uid).not("template_id","is",null)
      .gte("created_at",new Date(now.getFullYear(),now.getMonth()-7,1).toISOString());
    const have=new Set((existing||[]).map(e=>e.template_id+"::"+isoMonthKey(e.created_at)));
    // Name-based guard: pre-migration instances have no template_id, so also
    // skip months where a RECURRING same-named expense already exists (no
    // duplicates). One-time same-name expenses don't block the bill.
    const{data:existingByName}=await supabase.from("expenses").select("description,category,recurring,created_at").eq("household_id",uid)
      .gte("created_at",new Date(now.getFullYear(),now.getMonth()-7,1).toISOString());
    const haveName=new Set((existingByName||[]).filter(e=>e.recurring).map(e=>(e.description||"").trim().toLowerCase()+"::"+e.category+"::"+isoMonthKey(e.created_at)));
    const lockKeys=[];
    tpls.forEach(t=>months.forEach(mm=>{lockKeys.push(`recTplDone_${t.id}_${mm.mk}`);lockKeys.push(`recTplSkip_${t.id}_${mm.mk}`);}));
    const locked=new Set();
    for(let i=0;i<lockKeys.length;i+=100){
      const{data}=await supabase.from("settings").select("key").eq("household_id",uid).in("key",lockKeys.slice(i,i+100));
      (data||[]).forEach(r=>locked.add(r.key));
    }
    const toInsert=[],doneKeys=[],catByTpl={};
    let restored=0;
    tpls.forEach(t=>{catByTpl[t.id]=t.cat_obj||null;});
    for(const t of tpls){
      for(const mm of months){
        const dk=`recTplDone_${t.id}_${mm.mk}`,sk=`recTplSkip_${t.id}_${mm.mk}`;
        if(locked.has(dk)||locked.has(sk)||have.has(t.id+"::"+mm.mk))continue;
        const nameKey=tplNorm(t.description)+"::"+t.category+"::"+mm.mk;
        if(haveName.has(nameKey)){doneKeys.push({key:dk,value:"1",household_id:uid});continue;} // already there (pre-migration)
        const dim=new Date(mm.y,mm.m+1,0).getDate();
        const day=Math.min(t.day_of_month||1,dim);
        toInsert.push({
          description:t.description,amount:t.amount,category:t.category,
          catObj:t.cat_obj?JSON.stringify(t.cat_obj):"{}",
          recurring:true,template_id:t.id,
          date:`${MSHORT[mm.m]} ${day}`,
          household_id:uid,
          created_at:new Date(mm.y,mm.m,day,12,0,0).toISOString()
        });
        doneKeys.push({key:dk,value:"1",household_id:uid});
        if(mm.mk<curMk)restored++;
      }
    }
    if(toInsert.length){
      const{data:ins,error}=await supabase.from("expenses").insert(toInsert).select();
      if(!error){
        if(doneKeys.length)await supabase.from("settings").upsert(doneKeys);
        const fresh=(ins||[]).map(r=>({...r,catObj:catByTpl[r.template_id]||null}));
        // Keep the newest-first invariant the load query establishes —
        // otherwise backfilled months jump to the top of Recent Expenses.
        setExpenses([...fresh,...exp].sort((a,b)=>new Date(b.created_at)-new Date(a.created_at)));
        if(restored>0)toast(`✓ Restored ${restored} recurring bill${restored===1?"":"s"} from skipped months`);
        else if(fresh.length>0)toast(`✓ ${fresh.length} recurring bill${fresh.length===1?"":"s"} generated`);
        return;
      }
      console.error("template rollover insert error:",error);
    }
    setExpenses(exp);
  };

  // One-time cleanup (2026-10-15): the first template backfill eagerly created
  // NEXT month's instances, but bills should only appear in their own month.
  // Remove premature future instances + their done/skip locks so the month
  // regenerates correctly when it actually arrives. Returns removed ids.
  const cleanupFutureInstances=async(uid)=>{
    const removed=new Set();
    try{
      const{data:flag}=await supabase.from("settings").select("value").eq("key","recTplFutureFix").eq("household_id",uid).limit(1);
      if(flag?.length)return removed;
      const now=new Date();
      const nextStart=new Date(now.getFullYear(),now.getMonth()+1,1).toISOString();
      const{data:fut}=await supabase.from("expenses").select("id,template_id,created_at").eq("household_id",uid).not("template_id","is",null).gte("created_at",nextStart);
      const lockKeys=[];
      (fut||[]).forEach(e=>{
        const mk=isoMonthKey(e.created_at||"");
        removed.add(e.id);
        lockKeys.push(`recTplDone_${e.template_id}_${mk}`,`recTplSkip_${e.template_id}_${mk}`);
      });
      if((fut||[]).length)await supabase.from("expenses").delete().in("id",fut.map(e=>e.id)).eq("household_id",uid);
      if(lockKeys.length)await supabase.from("settings").delete().eq("household_id",uid).in("key",lockKeys);
      await supabase.from("settings").upsert([{key:"recTplFutureFix",value:"1",household_id:uid}]);
    }catch(e){console.warn("future-instance cleanup skipped:",e.message);}
    return removed;
  };

  // Repair (2026-10-17, flag removed 2026-11-03 so it reruns every load):
  // expenses left pointing at a deleted utilities category id (e.g. the old
  // default "utilities", or a custom "utility_<timestamp>" id) showed the 📦
  // fallback instead of the lightning icon. Reassign those orphans to the
  // live utilities category. Idempotent: a no-op once none remain, so new
  // orphans (e.g. instances generated after the first run) get fixed too.
  // Returns [{id,toId,catObj}] for state updates.
  const repairOrphanUtilities=async(uid,expList,liveCats)=>{
    const fixed=[];
    try{
      const utilCat=(liveCats||[]).find(c=>c.id==="utilities")||(liveCats||[]).find(c=>/utilit/i.test(c.label||""));
      const liveIds=new Set((liveCats||[]).map(c=>c.id));
      if(utilCat){
        const ids=(expList||[]).filter(e=>!liveIds.has(e.category)&&/utilit/i.test(e.category||"")).map(e=>e.id);
        if(ids.length){
          const u={category:utilCat.id,catObj:JSON.stringify(utilCat)};
          const{error}=await supabase.from("expenses").update(u).in("id",ids).eq("household_id",uid);
          if(!error)ids.forEach(id=>fixed.push({id,toId:utilCat.id,catObj:utilCat}));
        }
      }
    }catch(e){console.warn("orphan utility repair skipped:",e.message);}
    return fixed;
  };

  // Repair (runs on every load; idempotent): expenses pointing at a deleted
  // category id whose saved catObj still names a LIVE category get reassigned
  // to it. Unlike the one-shot utilities repair above, this covers any
  // category and reruns until nothing is left to fix. Returns
  // [{id,toId,catObj}] for state updates.
  const repairOrphanCatObj=async(uid,expList,liveCats)=>{
    const fixed=[];
    try{
      const liveIds=new Set((liveCats||[]).map(c=>c.id));
      const byId={};(liveCats||[]).forEach(c=>{byId[c.id]=c;});
      const toFix=(expList||[]).filter(e=>!liveIds.has(e.category)).map(e=>{const co=parseCatObj(e.catObj);return co&&co.id&&liveIds.has(co.id)?{id:e.id,toId:co.id,catObj:byId[co.id]}:null;}).filter(Boolean);
      if(toFix.length){
        const groups={};toFix.forEach(f=>{(groups[f.toId]=groups[f.toId]||[]).push(f.id);});
        for(const toId of Object.keys(groups)){
          const{error}=await supabase.from("expenses").update({category:toId}).in("id",groups[toId]).eq("household_id",uid);
          if(!error)toFix.filter(f=>f.toId===toId).forEach(f=>fixed.push(f));
        }
      }
    }catch(e){console.warn("orphan catObj repair skipped:",e.message);}
    return fixed;
  };

  // Resolve the template behind an instance (direct link, else name match).
  const findTemplateForInstance=(inst)=>{
    if(!inst)return null;
    if(inst.template_id)return templates.find(t=>t.id===inst.template_id)||null;
    return templates.find(t=>tplNorm(t.description)===tplNorm(inst.description)&&t.category===inst.category)||null;
  };

  // Repair a broken instance→template link (e.g. the description drifted
  // after a rename, so the exact name-match fails). Only links to a
  // uniquely-matching existing template — never creates one, so a
  // duplicate series can't spawn.
  const healTemplateLink=async(inst)=>{
    const hid=householdId||user?.id;
    if(!inst||!hid||!tplSupported)return null;
    let tpl=findTemplateForInstance(inst);
    if(tpl)return tpl;
    const nDesc=tplNorm(inst.description);
    const cands=(templates||[]).filter(t=>{
      const tn=tplNorm(t.description);
      return t.category===inst.category&&nDesc&&tn&&(nDesc.includes(tn)||tn.includes(nDesc));
    });
    if(cands.length!==1)return null;
    tpl=cands[0];
    try{
      await supabase.from("expenses").update({template_id:tpl.id}).eq("id",inst.id).eq("household_id",hid);
    }catch(e){console.warn("healTemplateLink:",e.message);}
    setExpenses(p=>p.map(e=>e.id===inst.id?{...e,template_id:tpl.id}:e));
    return tpl;
  };

  // "Apply to future months": update the template + this and future instances.
  const updateTemplateFromInstance=async(tpl,orig,data)=>{
    const hid=householdId||user?.id;
    const newDay=data.created_at?new Date(data.created_at).getDate():tpl.day_of_month;
    const u={description:data.description,amount:data.amount,category:data.category,cat_obj:data.catObj,day_of_month:Math.min(Math.max(newDay||1,1),28),updated_at:new Date().toISOString()};
    setTemplates(p=>p.map(t=>t.id===tpl.id?{...t,...u}:t));
    const origMk=expMonthKey(orig);
    setExpenses(p=>p.map(e=>(e.template_id===tpl.id&&expMonthKey(e)>=origMk)?{...e,description:data.description,amount:data.amount,category:data.category,catObj:data.catObj}:e));
    setSyncStatus("syncing");
    try{
      const{error}=await supabase.from("recurring_templates").update(u).eq("id",tpl.id);
      if(error)throw error;
      const{error:e2}=await supabase.from("expenses")
        .update({description:data.description,amount:data.amount,category:data.category,catObj:JSON.stringify(data.catObj)})
        .eq("template_id",tpl.id).eq("household_id",hid).gte("created_at",orig.created_at);
      if(e2)throw e2;
      setSyncStatus("synced");
      toast(`✓ ${data.description} updated — future months will use ${fmt(data.amount)}`);
    }catch(err){
      console.error("updateTemplate error:",err);
      setSyncStatus("error");
      toast("Couldn't save the series — will retry","err");
    }
  };

  // Link a newly added recurring expense to its template (create if needed).
  // Match on description + category + amount: two distinct bills can share
  // a description (e.g. two Life Insurance policies), and matching on the
  // first two alone silently absorbed the second bill into the first bill's
  // template, so it never generated its own months.
  const linkTemplateForNewExpense=async(data,insId,hid)=>{
    if(!tplSupported)return;
    try{
      let tpl=templates.find(t=>tplNorm(t.description)===tplNorm(data.description)&&t.category===data.category&&Math.round(Number(t.amount)*100)===Math.round(Number(data.amount)*100));
      if(!tpl){
        const{data:nt,error}=await supabase.from("recurring_templates").insert([{
          description:data.description,amount:data.amount,category:data.category,
          cat_obj:data.catObj,day_of_month:data.created_at?new Date(data.created_at).getDate():new Date().getDate(),household_id:hid
        }]).select().single();
        if(error)throw error;
        tpl=nt;setTemplates(p=>[...p,nt]);
      }
      if(tpl){
        await supabase.from("expenses").update({template_id:tpl.id}).eq("id",insId);
        setExpenses(p=>p.map(e=>e.id===insId?{...e,template_id:tpl.id}:e));
      }
    }catch(e){console.warn("template link skipped:",e.message);}
  };

  // Delete ONE instance: remove it + set a skip-lock so the rollover won't
  // recreate it that month. Future months still auto-create (honest scoping).
  const deleteInstanceOnly=async(target)=>{
    const hid=householdId||user?.id;
    const tpl=findTemplateForInstance(target);
    const mk=expMonthKey(target);
    setExpenses(p=>p.filter(e=>e.id!==target.id));
    setSyncStatus("syncing");
    try{
      const{error}=await supabase.from("expenses").delete().eq("id",target.id).eq("household_id",hid);
      if(error)throw error;
      if(tpl&&tplSupported)await supabase.from("settings").upsert([{key:`recTplSkip_${tpl.id}_${mk}`,value:"1",household_id:hid}]);
      setSyncStatus("synced");
      toast("✓ Deleted for this month only — future months still auto-create");
    }catch(err){
      console.error("deleteInstanceOnly:",err);
      setSyncStatus("error");
      setExpenses(p=>[target,...p]);
      toast("Couldn't delete — try again","err");
    }
  };

  // Delete the whole series: template gone, this + future instances removed,
  // past months kept as history (marked non-recurring so the 🔄 doesn't lie).
  const deleteTemplateSeries=async(target)=>{
    const hid=householdId||user?.id;
    const tpl=findTemplateForInstance(target);
    if(!tpl||!tplSupported){await deleteRecurringSeries(target);return;} // legacy fallback
    const cutoff=target.created_at;
    const doomed=expenses.filter(e=>e.template_id===tpl.id&&e.created_at>=cutoff);
    const pastIds=expenses.filter(e=>e.template_id===tpl.id&&e.created_at<cutoff).map(e=>e.id);
    setTemplates(p=>p.filter(t=>t.id!==tpl.id));
    setExpenses(p=>p.filter(e=>!(e.template_id===tpl.id&&e.created_at>=cutoff)).map(e=>pastIds.includes(e.id)?{...e,recurring:false}:e));
    setSyncStatus("syncing");
    try{
      await supabase.from("recurring_templates").delete().eq("id",tpl.id).eq("household_id",hid);
      if(doomed.length)await supabase.from("expenses").delete().in("id",doomed.map(e=>e.id)).eq("household_id",hid);
      if(pastIds.length)await supabase.from("expenses").update({recurring:false}).in("id",pastIds).eq("household_id",hid);
      setSyncStatus("synced");
      toast(`✓ ${tpl.description} series stopped`);
      buzz(20);
    }catch(err){
      console.error("deleteTemplateSeries:",err);
      setSyncStatus("error");
      toast("Couldn't stop the series — try again","err");
    }
  };
  // Save from the Manage-recurring-bills modal (new or edited template).
  // Editing pushes the new values to this + future instances.
  const saveTemplate=async(id,data)=>{
    const hid=householdId||user?.id;
    setSyncStatus("syncing");
    try{
      if(id==="new"){
        const{data:nt,error}=await supabase.from("recurring_templates").insert([{
          description:data.description,amount:data.amount,category:data.category,
          cat_obj:data.catObj,day_of_month:data.day_of_month,household_id:hid
        }]).select().single();
        if(error)throw error;
        const freshTpls=[...templates,nt].sort((a,b)=>a.description.localeCompare(b.description));
        setTemplates(freshTpls);
        toast("✓ Recurring bill added");
        // Generate the current month's instance for the new bill (no backfill)
        await runTemplateRollover(hid,expenses,freshTpls,0);
      }else{
        const u={description:data.description,amount:data.amount,category:data.category,cat_obj:data.catObj,day_of_month:data.day_of_month,updated_at:new Date().toISOString()};
        const{error}=await supabase.from("recurring_templates").update(u).eq("id",id).eq("household_id",hid);
        if(error)throw error;
        setTemplates(p=>p.map(t=>t.id===id?{...t,...u}:t));
        const nowMk=nowMonthKey();
        const mStart=new Date(new Date().getFullYear(),new Date().getMonth(),1).toISOString();
        setExpenses(p=>p.map(e=>(e.template_id===id&&expMonthKey(e)>=nowMk)?{...e,description:data.description,amount:data.amount,category:data.category,catObj:data.catObj}:e));
        const{error:e2}=await supabase.from("expenses")
          .update({description:data.description,amount:data.amount,category:data.category,catObj:JSON.stringify(data.catObj)})
          .eq("template_id",id).eq("household_id",hid).gte("created_at",mStart);
        if(e2)throw e2;
        toast("✓ Recurring bill updated — future months follow");
      }
      setSyncStatus("synced");
      buzz(10);
    }catch(e){console.error("saveTemplate:",e);setSyncStatus("error");toast("Couldn't save — try again","err");}
  };
  // Delete from the Manage-recurring-bills modal (with confirm handled by caller).
  const deleteTemplate=async(tpl)=>{
    const hid=householdId||user?.id;
    const mStart=new Date(new Date().getFullYear(),new Date().getMonth(),1).toISOString();
    setTemplates(p=>p.filter(t=>t.id!==tpl.id));
    setExpenses(p=>p.filter(e=>!(e.template_id===tpl.id&&e.created_at>=mStart)).map(e=>e.template_id===tpl.id?{...e,recurring:false}:e));
    setSyncStatus("syncing");
    try{
      await supabase.from("recurring_templates").delete().eq("id",tpl.id).eq("household_id",hid);
      const pastIds=expenses.filter(e=>e.template_id===tpl.id&&e.created_at<mStart).map(e=>e.id);
      await supabase.from("expenses").delete().eq("template_id",tpl.id).eq("household_id",hid).gte("created_at",mStart);
      if(pastIds.length)await supabase.from("expenses").update({recurring:false}).in("id",pastIds).eq("household_id",hid);
      setSyncStatus("synced");
      toast(`✓ ${tpl.description} series stopped`);
      buzz(20);
    }catch(e){console.error("deleteTemplate:",e);setSyncStatus("error");toast("Couldn't stop the series — try again","err");}
  };
  const deleteExpense=async(id)=>{
    const prev=expenses.find(e=>e.id===id);
    setExpenses(p=>p.filter(e=>e.id!==id));
    const hid=householdId||user?.id;
    const offline=typeof navigator!=="undefined"&&!navigator.onLine;
    try{
      if(offline)throw new Error("offline");
      const{error}=await supabase.from("expenses").delete().eq("id",id).eq("household_id",hid);
      if(error)throw error;
      setSyncStatus("synced");
      toast("✓ Expense deleted");
      buzz(20);
    }catch(err){
      console.error("deleteExpense error:",err);
      if(offline){enqueueOp({kind:"delete",id});}
      else{
        if(prev)setExpenses(p=>[prev,...p]);
        setSyncStatus("error");
        toast("Couldn't delete — try again","err");
      }
    }
  };
  // Stops an entire recurring chain — marks EVERY past/future copy (same description+category)
  // as non-recurring, then deletes the clicked instance. Fixes the bug where deleting one
  // month's copy didn't stop already-created later copies from continuing to propagate.
  const deleteRecurringSeries=async(item)=>{
    const hid=householdId||user?.id;
    const norm=(x)=>(x||"").trim().toLowerCase();
    const amt=(e)=>Number(e.amount).toFixed(2);
    const itemMk=expMonthKey(item);
    // Same item = same name + category + amount (so a second, different policy with the same name is untouched)
    const same=expenses.filter(e=>e.id!==item.id&&e.recurring&&norm(e.description)===norm(item.description)&&e.category===item.category&&amt(e)===amt(item));
    const laterIds=same.filter(e=>expMonthKey(e)>itemMk).map(e=>e.id); // already auto-created future copies -> remove
    const stopIds=same.filter(e=>expMonthKey(e)<=itemMk).map(e=>e.id); // history -> keep, but stop it recurring
    const removeIds=[item.id,...laterIds];
    setExpenses(p=>p.filter(e=>!removeIds.includes(e.id)).map(e=>stopIds.includes(e.id)?{...e,recurring:false}:e));
    try{
      if(stopIds.length)await supabase.from("expenses").update({recurring:false}).in("id",stopIds).eq("household_id",hid);
      await supabase.from("expenses").delete().in("id",removeIds).eq("household_id",hid);
    }catch(err){console.error("deleteRecurringSeries exception:",err);}
  };
  const addCategory=async(cat)=>{
    try{
      const hid=householdId||user?.id;
      const{data:ins,error}=await supabase.from("categories").insert([{...cat,keywords:cat.keywords||[],household_id:hid}]).select().single();
      if(ins)setCategories(p=>withLightning([...p,ins]));
      if(error)console.error("addCategory error:",error);
    }catch(err){console.error("addCategory exception:",err);}
  };
  const editCategory=async(id,data)=>{
    const existing=categories.find(c=>c.id===id);
    if(existing?.custom){
      const{data:upd}=await supabase.from("categories").update(data).eq("id",id).select().single();
      if(upd)setCategories(p=>withLightning(p.map(c=>c.id===id?upd:c)));
      else setCategories(p=>withLightning(p.map(c=>c.id===id?{...c,...data}:c)));
    } else {
      setCategories(p=>p.map(c=>c.id===id?{...c,...data}:c));
    }
  };
  const deleteCategory=async(id)=>{
    const hid=householdId||user?.id;
    try{
      await supabase.from("categories").delete().eq("id",id).eq("household_id",hid);
      setCategories(p=>p.filter(c=>c.id!==id));
      toast("✓ Category deleted");
    }catch(e){toast("Couldn't delete — try again","err");}
  };
  // Delete a category AND move its expenses somewhere (no orphans).
  const deleteCategoryAndMove=async(fromId,moves)=>{
    // moves: [{id, toId}] — each expense goes to its own chosen category.
    const hid=householdId||user?.id;
    setSyncStatus("syncing");
    try{
      // Group by target so each destination is one batched write
      const byTarget={};
      moves.forEach(m=>{(byTarget[m.toId]=byTarget[m.toId]||[]).push(m.id);});
      const moveOf={};moves.forEach(m=>{moveOf[m.id]=m.toId;});
      setExpenses(p=>p.map(e=>{
        const toId=moveOf[e.id];if(!toId)return e;
        const toCat=categories.find(c=>c.id===toId);
        return {...e,category:toId,catObj:toCat||e.catObj};
      }));
      for(const toId of Object.keys(byTarget)){
        const toCat=categories.find(c=>c.id===toId);
        const u={category:toId};
        if(toCat)u.catObj=JSON.stringify(toCat);
        const{error}=await supabase.from("expenses").update(u).in("id",byTarget[toId]).eq("household_id",hid);
        if(error)throw error;
      }
      await supabase.from("categories").delete().eq("id",fromId).eq("household_id",hid);
      setCategories(p=>withLightning(p.filter(c=>c.id!==fromId)));
      setSyncStatus("synced");
      toast(`✓ ${moves.length} expense${moves.length===1?"":"s"} moved & category deleted`);
    }catch(e){console.error("deleteCategoryAndMove:",e);setSyncStatus("error");toast("Couldn't finish — try again","err");}
  };

  _privacy=privacyMode; // show decoy amounts for every fmt() while privacy mode is on
  const lockProps={locks,privacyMode,onRequestUnlock:handleRequestUnlock,onToggleLock:handleToggleLock};

  // These effects must be before any early returns (Rules of Hooks)
  useEffect(()=>{_currency=currency;},[currency]);

  const handleLogout=async()=>{await supabase.auth.signOut();};

  // Show nothing while checking session, show auth screen if not logged in
  if(!authChecked) return <div style={{minHeight:"100vh",background:T.bg,display:"flex",alignItems:"center",justifyContent:"center"}}><div style={{fontSize:36}}>⏳</div></div>;
  if(!user) return <AuthScreen onAuth={setUser}/>;

  const NAV=[{id:"dashboard",label:"🏠 Home"},{id:"expenses",label:"💳 Expenses"},{id:"spending",label:"📊 Spending"},{id:"list",label:"🛒 List"},{id:"notes",label:"🗒️ Notes"},{id:"prices",label:"🏷️ Prices"}];

  return <>
    <style>{GLOBAL_CSS}</style>
    <link rel="preconnect" href="https://fonts.googleapis.com"/>
    <link href="https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,700&family=JetBrains+Mono:wght@400;700;800&display=swap" rel="stylesheet"/>
    {pinModal&&<PinModal mode={pinModal.mode} storedHash={pinHash} onSuccess={handlePinSuccess} onClose={()=>setPinModal(null)} onSetPin={handleSetPin}/>}
    {confirmDlg&&<ConfirmDialog title={confirmDlg.title} message={confirmDlg.message} confirmLabel={confirmDlg.confirmLabel} danger={confirmDlg.danger} onConfirm={confirmDlg.onConfirm} onClose={()=>setConfirmDlg(null)}/>}
    {showCurrencyPicker&&<CurrencyPickerModal current={currency} rates={fxRates} onPick={(c)=>saveCurrency(c)} onClose={()=>setShowCurrencyPicker(false)}/>}
    {showIncomeBudgets&&<IncomeBudgetsModal income={income} budgets={budgets} categories={categories} onSave={saveIncomeBudgets} onClose={()=>setShowIncomeBudgets(false)}/>}
    {showRecurringMgr&&<ManageRecurringModal templates={templates} categories={categories} onSaveTpl={saveTemplate} onDeleteTpl={(t)=>setConfirmDlg({title:"Stop this recurring bill?",message:`${t.description} · ${fmt(t.amount)}/month — this and future months are removed. Past months stay as history.`,confirmLabel:"Stop series",danger:true,onConfirm:()=>deleteTemplate(t)})} onClose={()=>setShowRecurringMgr(false)}/>}
    {toasts.length>0&&<div style={{position:"fixed",bottom:24,left:"50%",transform:"translateX(-50%)",zIndex:3000,display:"flex",flexDirection:"column",gap:8,alignItems:"center",pointerEvents:"none",width:"max-content",maxWidth:"92vw"}}>
      {toasts.map(t=><div key={t.id} style={{background:t.type==="err"?"#7f1d1d":"#1C1915",color:"#fff",padding:"12px 20px",borderRadius:12,fontSize:14,fontWeight:600,boxShadow:"0 8px 24px rgba(0,0,0,0.25)",maxWidth:"92vw",textAlign:"center"}}>{t.msg}</div>)}
    </div>}
    {showProfileModal&&<HouseholdProfileModal name={householdName} logo={householdLogo} onSave={saveHouseholdProfile} onClose={()=>setShowProfileModal(false)}/>}
    <div style={S.app}>
      <div style={S.inner}>
        {pendingOps.length>0&&<div style={{background:"#7C2D12",borderRadius:12,padding:"12px 16px",marginBottom:12,display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}><span>📡</span><span style={{fontSize:13,color:"#fff",fontWeight:600}}>{pendingOps.length} change{pendingOps.length===1?"":"s"} waiting to sync</span></div>
          <button onClick={()=>flushPendingOps()} style={{padding:"7px 16px",background:"#fff",border:"none",borderRadius:8,cursor:"pointer",fontFamily:SANS,fontSize:12,fontWeight:700,color:"#7C2D12",whiteSpace:"nowrap"}}>Retry now</button>
        </div>}
        {/* Header */}
        <div style={S.header}>
          <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
            <button onClick={()=>setShowProfileModal(true)} title="Customize household name & logo" style={{display:"flex",alignItems:"center",gap:10,background:"none",border:"none",cursor:"pointer",padding:0,textAlign:"left",fontFamily:"inherit"}}>
              <HouseholdLogo logo={householdLogo} size={46}/>
              <div style={{lineHeight:1.2}}><div style={{fontFamily:FONT,fontSize:17,color:T.accent}}>{householdName}</div><div style={{fontFamily:FONT,fontSize:12,color:T.textSub}}>Expenses{householdLogo?"":" 🐾"}</div></div>
            </button>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <SyncBadge status={syncStatus}/>
            <PrivacyButton privacyMode={privacyMode} pinHash={pinHash} onToggle={handlePrivacyToggle} onSetupPin={handleSetupPin}/>
            <button onClick={()=>setShowCurrencyPicker(true)} title="Display currency (amounts stored in CAD)" style={{display:"flex",alignItems:"center",gap:5,padding:"10px 13px",borderRadius:10,border:"1px solid "+T.border,background:T.bgCardAlt,cursor:"pointer",fontFamily:MONO,fontSize:12,fontWeight:700,color:T.accent,whiteSpace:"nowrap",flexShrink:0}}>{(CURRS.find(c=>c.code===currency)||CURRS[0]).flag} {currency}</button>
            <button onClick={handleLogout} title={"Signed in as "+user?.email} style={{display:"flex",alignItems:"center",gap:5,padding:"10px 13px",borderRadius:10,border:"1px solid "+T.border,background:T.bgCardAlt,cursor:"pointer",fontFamily:SANS,fontSize:12,fontWeight:600,color:T.textSub,whiteSpace:"nowrap",flexShrink:0}}>🚪 Sign Out</button>
            <nav className="app-nav">
              {NAV.map(t=>{
                const p=t.label.split(" ");
                const count=NAV.length;
                const iconSize=count<=4?28:count===5?22:19;
                const textSize=count<=4?14:count===5?11:10;
                return<button key={t.id} style={S.navBtn(tab===t.id,count)} onClick={()=>setTab(t.id)}>
                  <span style={{fontSize:iconSize,lineHeight:1}}>{p[0]}</span>
                  <span style={{fontSize:textSize,lineHeight:1,whiteSpace:"nowrap"}}>{p.slice(1).join(" ")}</span>
                </button>;
              })}
            </nav>
          </div>
        </div>

        {/* Content */}
        {!loaded
          ?<div style={{textAlign:"center",padding:"80px 0",color:T.textMuted}}><div style={{fontSize:36,marginBottom:14}}>⏳</div><div style={{fontFamily:FONT,fontSize:22}}>Loading your data…</div></div>
          :<>
            {tab==="dashboard"&&<DashboardTab expenses={parsedExp} income={income} budgets={budgets} categories={categories} setTab={setTab} onEditIncome={()=>setShowIncomeBudgets(true)}/>}
            {tab==="expenses"&&<ExpensesTab expenses={parsedExp} categories={categories} fxRates={fxRates} displayCurrency={currency} onAddExpense={addExpense} onAddExpenses={addExpenses} onEditExpense={editExpense} onDeleteExpense={deleteExpense} onDeleteRecurringSeries={deleteRecurringSeries} onEditRecurringSeries={editRecurringSeries} onUpdateTemplate={updateTemplateFromInstance} onDeleteInstanceOnly={deleteInstanceOnly} onDeleteTemplateSeries={deleteTemplateSeries} findTemplateForInstance={findTemplateForInstance} onHealTemplateLink={healTemplateLink} onToast={toast} onAskConfirm={(d)=>setConfirmDlg(d)} templates={templates} onManageRecurring={()=>setShowRecurringMgr(true)} onAddCategory={addCategory} onEditCategory={editCategory} onDeleteCategory={deleteCategory} onDeleteCategoryAndMove={deleteCategoryAndMove} householdId={householdId} learnedKW={learnedKW} onLearnKW={learnCorrection} {...lockProps}/>}
            {tab==="spending"&&<SpendingTab expenses={parsedExp} categories={categories} income={income} budgets={budgets} {...lockProps}/>}
            {tab==="list"&&<ListTab items={listItems} onSave={saveListItems} meta={listMeta} onSaveMeta={saveListMeta} onSaveAll={saveListState} userEmail={user?.email}/>}
            {tab==="notes"&&<NotesTab notes={notes} onSave={saveNotes} userEmail={user?.email}/>}
            {tab==="prices"&&<PriceTrackerTab entries={priceEntries} onSave={savePriceEntries} userEmail={user?.email}/>}
          </>
        }
        {/* Floating quick-add button */}
        {loaded&&tab!=="expenses"&&<button
          onClick={()=>setTab("expenses")}
          style={{position:"fixed",bottom:24,right:20,width:56,height:56,borderRadius:"50%",background:T.accent,border:"none",cursor:"pointer",boxShadow:"0 4px 20px rgba(217,111,62,0.5)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,zIndex:200,transition:"transform 0.15s"}}
          onTouchStart={e=>e.currentTarget.style.transform="scale(0.93)"}
          onTouchEnd={e=>e.currentTarget.style.transform="scale(1)"}
          title="Add expense">＋</button>}
        {/* Build stamp — proves which version is live */}
        <div style={{textAlign:"center",padding:"18px 0 26px",fontSize:11,color:T.textMuted}}>VestTrack · build {BUILD_STAMP}</div>
      </div>
    </div>
  </>;
}
